import Phaser from 'phaser';
import { getCurrentTheme, buildItemsById } from '../Utils.js';
import { handleChatMessage } from '../chat/ChatCommands.js';
import BattleStateManager from '../game/BattleStateManager.js';
import TeamBuilder from '../game/TeamBuilder.js';
import { autoJoinRemaining, joinQueue } from '../game/GameConfig.js';
import { showMarqueeMessage, clearMarqueeQueues } from '../Utils.js';
import VoteManager from '../services/VoteManager.js';
import { limitInventoryPerContainer } from '../game/PlayerSpawner.js';
import JoinShopManager from './shop/JoinShopManager.js';
import sessionStateManager from '../game/SessionStateManager.js';
import { socketService } from '../services/SocketService.js';

export class JoinScene extends Phaser.Scene {
  constructor() {
    super({ key: 'JoinScene' });
    this.backgroundImage = null;
    this._onChatEvent = (ev) => this.onExternalChat(ev);
    // Dynamic grid state
    this.isResizingGrid = false;
    this.pendingVisualJoins = [];
    this.rosterPreparing = null;
    this.battleRosterReady = false;
    this.battleRosterPlayers = [];
    this.teamBuildResult = null;
    this.joinOverlayTween = null;
    this.gridRebuildTimer = null;
    this.maxJoinRetries = 3;
    this.sideWidthExtra = 125;
    
    // Auto-join system state
    this.lobbyJoiners = new Map();
    this.walletDisplayNames = new Map();
    this.joinGridArea = null;
    this.joinGridObjects = [];
    this.joinGridMap = new Map();
    this.shopManager = new JoinShopManager(this);
    this.itemsData = this.shopManager.getItemsData();
    
    // Server-selected heroes (keyed by userId) - populated when server selects hero for join
    this.serverSelectedHeroes = new Map();

    // Prize pool state
    this.prizePoolConfig = { tokens: [] };
    this.prizePoolSavedConfig = null;
    this.prizePoolThisMatchOnly = false;
    this.prizePoolBalances = null;
    this.prizePoolDataLoaded = false;
    this.prizePoolLoading = false;
    this.prizePoolWarnings = [];
    this.prizePoolUI = {
      container: null,
      tokensList: null,
      matchOnlyToggle: null,
      warningsText: null,
      ledgerText: null
    };
    this.prizePoolUI.sectionWidth = 360;
    this.prizePoolUI.sectionHeight = 320;
    this.modalLeftContainer = null;
    this.modalRightContainer = null;
    this.prizePoolTokenEditor = null;
    this.activePrizePoolInput = null;
    this.prizePoolKeyListener = null;
    this.tokensRegistry = null;

    // Match settings state
    this.matchSettings = null;
    this.matchSettingsLoaded = false;
    this.matchSettingsLoading = false;
    this.maxBattleStack = 10; // Default value
    this.casualMode = true; // Default to casual mode (stat normalization enabled)
    this.statCap = 50; // Default stat cap value (20-200 range)
    
    // Ranked mode vote state
    this.rankedVoteStatus = {
      totalPlayers: 0,
      votesRanked: 0,
      votesCasual: 0,
      allVotedRanked: false,
      rankedModeEnabled: false,
      voters: []
    };
    this.rankedVoteUI = null; // UI container for vote status
    
    // Battlefield display row state
    this.battlefieldRow = null;
    this.battlefieldData = null;
    this.battlefieldLootPool = [];
  }

  init(data) {
    // Store any data passed from previous scene
    this.gameMode = data?.gameMode || 'free-for-all';
    this.teamCount = data?.teamCount || 2;
    this.maxHeroes = data?.maxHeroes || 20; // Default to 8 heroes max
    
    // Countdown state
    this.countdownSeconds = 60;
    this.isPaused = false;
    this.players = []; // Array of joined players
    this.battleRosterPlayers = [];
    this.battleRosterReady = false;
    this.teamBuildResult = null;
    this.rosterPreparing = null;
    
    // Voting state flags
    this.votingInterestExpressed = false;
    this.votingCountdownActive = false;
    this.playersLockedForVoting = false;
    
    // Clear any previous battle state
    BattleStateManager.reset();
    
    // Initialize battle state manager with current configuration
    BattleStateManager.setBattleConfig({
      maxPlayers: this.maxHeroes,
      gameMode: this.gameMode,
      teamCount: this.teamCount
    });
  }

  preload() {
    // Load theme background image if available
    const theme = getCurrentTheme();
    if (theme && theme.backgroundImage) {
      const backgroundImageUrl = `https://soulwars.s3.us-east-1.amazonaws.com/themes/${theme.id}/${theme.backgroundImage}`;
      this.load.image('joinBackground', backgroundImageUrl);

      // Handle load errors gracefully
      this.load.on('filecomplete', (key) => {
        if (key === 'joinBackground') {
          console.log('✅ Join scene background loaded successfully');
        }
      });

      this.load.on('loaderror', (file) => {
        if (file.key === 'joinBackground') {
          console.warn('⚠️ Failed to load join background, using fallback');
        }
      });
    }

    this.load.image('sponsorImage', 'assets/ads/sponsored1.jpg');

    //Preload common textures from the gubbins folder
    this.load.image('skullbones1', 'assets/gubbins/skullbones1.png');
    this.load.image('blood1', 'assets/gubbins/blood1.png');
    this.load.image('cloud', 'assets/gubbins/cloud1.png');
    this.load.image('fog', 'assets/gubbins/fog1.png');
    this.load.image('cloud1', 'assets/gubbins/cloud1.png');
    this.load.image('fog1', 'assets/gubbins/fog1.png');
    this.load.image('trophy', 'assets/gubbins/trophy1.png');
    this.load.image('crown', 'assets/gubbins/crown1.png');
    this.load.image('confetti', 'assets/gubbins/green-square.png');
    this.load.image('medal', 'assets/gubbins/medal2.png');
    this.load.image('xp', 'assets/gubbins/xp.png');
    this.load.image('coin', 'assets/chests/gold_1.png');
    this.load.image('loot-chest', 'assets/chests/chest1.png');

    //preload the platform icons
    this.load.image('platform-pumpfun', 'assets/platform/platform-pumpfun.png');
    this.load.image('platform-x', 'assets/platform/platform-x.png');
    //this.load.image('platform-discord', 'assets/platform/platform-discord.png');
    //this.load.image('platform-telegram', 'assets/platform/platform-telegram.png');
    this.load.image('platform-youtube', 'assets/platform/platform-youtube.png');
    //this.load.image('platform-instagram', 'assets/platform/platform-instagram.png');
    //this.load.image('platform-tiktok', 'assets/platform/tiktok.png');
    this.load.image('platform-twitch', 'assets/platform/platform-twitch.png');
    this.load.image('platform-facebook', 'assets/platform/platform-meta.png');
    //this.load.image('platform-kick', 'assets/platform/platform-kick.png');
    //this.load.image('platform-restream', 'assets/platform/platform-restream.png');

    this.load.image('star_particle', 'assets/particles/star_particle.png');


    this.load.image('default-battlefield', 'assets/battlefields/default-arena.jpg');

    // Preload SFX from theme (used frequently during gameplay)
    this.preloadThemeSFX(theme);
    
    // Note: Music is loaded on-demand by GameScene's music system for better performance
    // Only preload spell sounds from theme
    this.preloadSpellSounds(theme);
    
    // Preload victory and rewards music for battle end flow
    this.preloadVictoryRewardsMusic(theme);

    //preload food items
    this.preloadFoodItems(theme);

    //preload items
    this.preloadItems(theme);

    this.preloadAnimatedSprites(theme);
        
    // Initialize lobby music system
    this.initializeLobbyMusic(theme);
  }

  preloadAnimatedSprites(theme) {

    //load up the animated sprites from the theme
    if (!theme || !theme.animatedSprites) {
      console.log('🎵 No theme animated sprites data available');
      //return;
    } else {
      if(theme.animatedSprites.length > 0) {
        theme.animatedSprites.forEach(sprite => {
          this.load.spritesheet(sprite.key, sprite.url, {
            frameWidth: sprite.frameWidth,
            frameHeight: sprite.frameHeight,
            frameRate: sprite.frameRate,
            loop: sprite.loop,
          });
        });
      } else {
        console.log('🎵 No animated sprites found in theme');
      }
    }

    this.load.spritesheet('spin-coin', 
      'assets/items/spin-coin-64px-16-256.png',
      {
        frameWidth: 64,
        frameHeight: 64,
        loop: true,
    });


  }

  preloadFoodItems(theme) {
    if (!theme || !theme.food) {
      console.log('🎵 No theme food items data available');
      return;
    }
    //console.log('[JoinScene] loading food items:', theme.food);
    const awsRegion = process.env.REACT_APP_AWS_REGION || 'us-east-1';
    const s3BasePath = theme?.s3BasePath || (theme?.id ? `https://soulwars.s3.${awsRegion}.amazonaws.com/themes/${theme.id}` : '');
    const useS3 = !!s3BasePath;

      // Load food assets from theme
    theme.food.forEach(food => {
      if (food.spriteKey) {
        const assetPath = useS3
        ? `${s3BasePath}/items/food/${food.spriteKey}.png`
        : `assets/items/food/${food.spriteKey}.png`;
        //console.log('[JoinScene] loading food image:', food.spriteKey, assetPath);
        this.load.image(food.spriteKey, assetPath);
      }
    });
  }

  preloadItems(theme) {
    if (!theme || !Array.isArray(theme.items)) {
      console.log('[JoinScene] No theme items to preload');
      return;
    }

    const awsRegion = process.env.REACT_APP_AWS_REGION || 'us-east-1';
    const s3BasePath = theme?.s3BasePath || (theme?.id ? `https://soulwars.s3.${awsRegion}.amazonaws.com/themes/${theme.id}` : '');
    const useS3 = !!s3BasePath;

    // Load item icons from theme.items using iconKey (files are PNGs)
    theme.items.forEach(item => {
      const iconKey = item?.iconKey;
      if (!iconKey) return;
      const fileName = iconKey.endsWith('.png') ? iconKey : `${iconKey}.png`;
      const assetPath = useS3
        ? `${s3BasePath}/items/${fileName}`
        : `assets/items/${fileName}`;
      //console.log('[JoinScene] loading item icon:', iconKey, assetPath);
      try { this.load.image(iconKey, assetPath); } catch (_) {}
    });
  }

  preloadThemeSFX(theme) {
    if (!theme || !theme.sounds) {
      console.log('🎵 No theme SFX data available');
      return;
    }

    //console.log('🎵 Preloading theme SFX...');
    
    const awsRegion = process.env.REACT_APP_AWS_REGION || 'us-east-1';
    const s3BasePath = theme?.s3BasePath || (theme?.id ? `https://soulwars.s3.${awsRegion}.amazonaws.com/themes/${theme.id}` : '');
    const useS3 = !!s3BasePath;
    
    let totalSFX = 0;
    let loadedSFX = 0;

    // Count total SFX for progress tracking
    Object.values(theme.sounds).forEach(soundArray => {
      if (Array.isArray(soundArray)) {
        totalSFX += soundArray.length;
      }
    });

    //console.log(`🎵 Found ${totalSFX} SFX files to preload`);

    // Load all SFX from theme
    Object.entries(theme.sounds).forEach(([category, soundArray]) => {
      if (Array.isArray(soundArray)) {
        soundArray.forEach(sound => {
          if (sound && sound.file) {
            const soundKey = `sfx_${category}_${sound.id || sound.file.replace(/[^a-zA-Z0-9]/g, '_')}`;
            
            // Check if already loaded
            if (!this.cache.audio.exists(soundKey)) {
              const soundPath = useS3
                ? `${s3BasePath}/${sound.file}`
                : `assets/${sound.file}`;
              
              //console.log(`🎵 Loading SFX: ${soundKey} from ${soundPath}`);
              this.load.audio(soundKey, soundPath);
            } else {
              //console.log(`🎵 SFX already loaded: ${soundKey}`);
              loadedSFX++;
            }
          }
        });
      }
    });

    // Set up progress tracking
    this.load.on('filecomplete', (key, type) => {
      if (type === 'audio' && key.startsWith('sfx_')) {
        loadedSFX++;
        //console.log(`🎵 SFX loaded: ${key} (${loadedSFX}/${totalSFX})`);
      }
    });

    this.load.on('loaderror', (file) => {
      if (file.type === 'audio' && file.key.startsWith('sfx_')) {
        console.warn(`⚠️ Failed to load SFX: ${file.key} from ${file.url}`);
      }
    });
  }


  preloadSpellSounds(theme) {
    if (!theme || !theme.spellSounds) {
      console.log('🎵 No theme spell sounds data available');
      return;
    }

    //console.log('🎵 Preloading theme spell sounds...');
    
    const awsRegion = process.env.REACT_APP_AWS_REGION || 'us-east-1';
    const s3BasePath = theme?.s3BasePath || (theme?.id ? `https://soulwars.s3.${awsRegion}.amazonaws.com/themes/${theme.id}` : '');
    const useS3 = !!s3BasePath;
    
    let totalSpellSounds = 0;
    let loadedSpellSounds = 0;

    // Count total spell sound files
    if (Array.isArray(theme.spellSounds)) {
      totalSpellSounds = theme.spellSounds.length;
    }

    //console.log(`🎵 Found ${totalSpellSounds} spell sound files to preload`);

    // Load all spell sounds from theme
    if (Array.isArray(theme.spellSounds)) {
      theme.spellSounds.forEach(spellSound => {
        if (spellSound && spellSound.key && spellSound.file) {
          const spellSoundKey = `spell_${spellSound.key.replace(/[^a-zA-Z0-9]/g, '_')}`;
          
          // Check if already loaded
          if (!this.cache.audio.exists(spellSoundKey)) {
            const spellSoundPath = useS3
              ? `${s3BasePath}/${spellSound.file}`
              : `assets/${spellSound.file}`;
            
            //console.log(`🎵 Loading spell sound: ${spellSoundKey} from ${spellSoundPath}`);
            this.load.audio(spellSoundKey, spellSoundPath);
          } else {
            //console.log(`🎵 Spell sound already loaded: ${spellSoundKey}`);
            loadedSpellSounds++;
          }
        }
      });
    }

    // Set up progress tracking
    this.load.on('filecomplete', (key, type) => {
      if (type === 'audio' && key.startsWith('spell_')) {
        loadedSpellSounds++;
        //console.log(`🎵 Spell sound loaded: ${key} (${loadedSpellSounds}/${totalSpellSounds})`);
      }
    });

    this.load.on('loaderror', (file) => {
      if (file.type === 'audio' && file.key.startsWith('spell_')) {
        console.warn(`⚠️ Failed to load spell sound: ${file.key} from ${file.url}`);
      }
    });
  }

  preloadVictoryRewardsMusic(theme) {
    if (!theme || !theme.music) {
      console.log('🎵 No music data available for victory/rewards preloading');
      return;
    }

    //console.log('🎵 Preloading victory and rewards music...');
    
    const awsRegion = process.env.REACT_APP_AWS_REGION || 'us-east-1';
    const s3BasePath = theme?.s3BasePath || (theme?.id ? `https://soulwars.s3.${awsRegion}.amazonaws.com/themes/${theme.id}` : '');
    const useS3 = !!s3BasePath;

    // Preload victory music
    if (theme.music.victory && Array.isArray(theme.music.victory) && theme.music.victory.length > 0) {
      const randomVictoryTrack = theme.music.victory[Math.floor(Math.random() * theme.music.victory.length)];
      const victoryKey = 'music-victory'; // Fixed key for victory music
      const victoryPath = useS3 ? `${s3BasePath}/music/${randomVictoryTrack.key}` : `assets/music/${randomVictoryTrack.key}`;
      
      //console.log('🎵 Preloading victory music:', victoryKey, 'from', victoryPath);
      this.load.audio(victoryKey, victoryPath);
    } else {
      console.log('🎵 No victory music available for preloading');
    }

    // Preload rewards music
    if (theme.music.rewards && Array.isArray(theme.music.rewards) && theme.music.rewards.length > 0) {
      const randomRewardsTrack = theme.music.rewards[Math.floor(Math.random() * theme.music.rewards.length)];
      const rewardsKey = 'music-rewards'; // Fixed key for rewards music
      const rewardsPath = useS3 ? `${s3BasePath}/music/${randomRewardsTrack.key}` : `assets/music/${randomRewardsTrack.key}`;
      
      //console.log('🎵 Preloading rewards music:', rewardsKey, 'from', rewardsPath);
      this.load.audio(rewardsKey, rewardsPath);
    } else {
      //console.log('🎵 No rewards music available for preloading');
    }
  }

  async initializeLobbyMusic(theme) {
    if (!theme || !theme.music) {
      console.log('🎵 No music data available');
      return;
    }

    //console.log('🎵 Initializing lobby music system...');
    
    // Try to get lobby music (pregame or intermissions)
    let lobbyMusic = theme.music.pregame || theme.music.intermissions || [];
    
    // If no lobby music, fall back to battle music but we'll use it differently
    if (!Array.isArray(lobbyMusic) || lobbyMusic.length === 0) {
      lobbyMusic = theme.music.battle || [];
      //console.log('🎵 No lobby music found, using battle music for lobby');
    }
    
    if (!Array.isArray(lobbyMusic) || lobbyMusic.length === 0) {
      //console.log('🎵 No music tracks available');
      return;
    }

    // Create music queue (shuffled)
    this.lobbyMusicQueue = [...lobbyMusic];
    this.shuffleArray(this.lobbyMusicQueue);
    
    //console.log('🎵 Lobby music queue initialized:', this.lobbyMusicQueue.length, 'tracks');
    
    // Start playing the first song after a short delay
    this.time.delayedCall(1000, () => {
      this.startLobbyMusic();
    });
  }

  shuffleArray(array) {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
  }

  startLobbyMusic() {
    if (!this.lobbyMusicQueue || this.lobbyMusicQueue.length === 0) {
      console.log('🎵 No lobby music in queue to play');
      return;
    }

    const musicEntry = this.lobbyMusicQueue.shift();
    const key = musicEntry.key;
    
    //console.log('🎵 Starting lobby music:', key);
    
    // Load and play the music
    this.loadAndPlayLobbyMusic(key, musicEntry);
  }

  loadAndPlayLobbyMusic(key, musicEntry) {
    const play = () => {
      if (!this.sound) return;
      
      const s = this.sound.get(key) || this.sound.add(key);
      if (s) {
        s.setLoop(true);
        s.setVolume(0.25); // Lower volume for lobby music
        s.play();
        this.currentLobbyMusic = s;
        this.currentLobbyMusicKey = key;
        
        console.log('🎵 Lobby music started:', musicEntry.title || key);
        
        // Set up music change when current song ends (for non-looping behavior)
        s.on('complete', () => {
          this.playNextLobbySong();
        });
      }
    };

    if (this.cache.audio && this.cache.audio.exists(key)) {
      play();
    } else {
      this.load.once('complete', play);
      
      // Determine S3 base path
      const theme = getCurrentTheme();
      const awsRegion = process.env.REACT_APP_AWS_REGION || 'us-east-1';
      const s3BasePath = `https://soulwars.s3.${awsRegion}.amazonaws.com/themes/${theme.id}`;
      
      const musicPath = `${s3BasePath}/music/${key}`;
      console.log('🎵 Loading lobby music:', musicPath);
      this.load.audio(key, musicPath);
      this.load.start();
    }
  }

  playNextLobbySong() {
    if (!this.lobbyMusicQueue || this.lobbyMusicQueue.length === 0) {
      // Rebuild queue if empty
      const theme = getCurrentTheme();
      let lobbyMusic = theme.music.pregame || theme.music.intermissions || theme.music.battle || [];
      this.lobbyMusicQueue = [...lobbyMusic];
      this.shuffleArray(this.lobbyMusicQueue);
    }
    
    const musicEntry = this.lobbyMusicQueue.shift();
    const key = musicEntry.key;
    
    console.log('🎵 Playing next lobby song:', key);
    this.loadAndPlayLobbyMusic(key, musicEntry);
  }

  // Method to get current music state for passing to next scene
  getMusicState() {
    return {
      currentMusic: this.currentLobbyMusic,
      currentMusicKey: this.currentLobbyMusicKey,
      musicQueue: this.lobbyMusicQueue,
      musicManifest: this.lobbyMusicManifest
    };
  }

  setupFocusHandling() {
    // Prevent music from pausing when window loses focus
    this.focusHandler = () => {
      if (this.currentLobbyMusic && this.currentLobbyMusic.isPaused) {
        //console.log('🎵 Resuming lobby music after window focus');
        //this.currentLobbyMusic.resume();
      }
    };

    this.blurHandler = () => {
      // Don't pause music on blur - let it continue playing
      //console.log('🎵 Window lost focus, but keeping music playing');
    };

    this.visibilityChangeHandler = () => {
      if (document.hidden) {
        //console.log('🎵 Tab hidden, but keeping music playing');
      } else {
        //console.log('🎵 Tab visible again, ensuring music is playing');
        if (this.currentLobbyMusic && this.currentLobbyMusic.isPaused) {
          //this.currentLobbyMusic.resume();
        }
      }
    };

    // Add event listeners
    //window.addEventListener('focus', this.focusHandler);
    //window.addEventListener('blur', this.blurHandler);
    //document.addEventListener('visibilitychange', this.visibilityChangeHandler);
  }

  create() {
    const { width, height } = this.cameras.main;

    // Set session ID and update state to 'join'
    const sessionId = window.__SW_SESSION_ID__ || BattleStateManager.getSessionId();
    if (sessionId) {
      sessionStateManager.setSessionId(sessionId);
      sessionStateManager.updateState('join');
    }

    // Build itemsById map from theme items for inventory limiting
    const theme = getCurrentTheme();
    if (theme && Array.isArray(theme.items)) {
      this.itemsById = buildItemsById(theme.items);
    } else {
      // Fallback: build from shop items if theme items not available
      const shopItems = this.shopManager?.getItemsData() || [];
      if (shopItems.length > 0) {
        this.itemsById = buildItemsById(shopItems);
      } else {
        this.itemsById = new Map();
      }
    }

    // Create coin spin animation using column-major frame order for smoother rotation
    // Frame layout is 4x4; using column-major avoids visual jump between rows
    this.anims.create({
      key: 'spin-coin-anim',
      frames: this.anims.generateFrameNumbers('spin-coin', {
        frames: [
          0, 4, 8, 12,
          1, 5, 9, 13,
          2, 6, 10, 14,
          3, 7, 11, 15
        ]
      }),
      frameRate: 6,
      repeat: -1
    });

    // Clear marquee queues when JoinScene loads
    clearMarqueeQueues();

    // Add focus/blur event listeners to prevent music pausing
    this.setupFocusHandling();
    
    // Store original dimensions for scaling calculations
    this.originalWidth = width;
    this.originalHeight = height;
    
    // Create dark background
    this.background = this.add.rectangle(width / 2, height / 2, width, height, 0x1F2937);
    
    // Add theme background image if loaded
    if (this.textures.exists('joinBackground')) {
      this.backgroundImage = this.add.image(width / 2, height / 2, 'joinBackground');
      
      // Scale to cover the screen while maintaining aspect ratio
      const scaleX = width / this.backgroundImage.width;
      const scaleY = height / this.backgroundImage.height;
      const scale = Math.max(scaleX, scaleY);
      this.backgroundImage.setScale(scale);
      this.backgroundImage.setDepth(0);
      
      // Add dark overlay for better readability
      this.overlay = this.add.rectangle(width / 2, height / 2, width, height, 0x000000);
      this.overlay.setAlpha(0.5);
      this.overlay.setDepth(1);
    }
    
    // Calculate layout proportions (side column & main grid)
    const sideWidth = width * 0.18 + (this.sideWidthExtra || 0);
    const mainWidth = width - sideWidth;
    const padding = 20;
    const sideOffset = 5;
    const centerOffset = 5;
    const mainContentWidth = mainWidth - padding * 2;
    
    // Store dimensions for resize
    this.sideWidth = sideWidth;
    this.centerWidth = mainWidth;
    this.padding = padding;
    this.sideOffset = sideOffset;
    this.centerOffset = centerOffset;
    
    // Create main containers
    this.sideContainer = this.add.container(padding + sideWidth / 2 - sideOffset, height / 2).setDepth(10);
    this.centerContainer = this.add.container(sideWidth + padding + mainContentWidth / 2 - centerOffset, height / 2).setDepth(10);
    
    // === CENTER AREA: Players Grid ===
    this.createCenterColumn(mainContentWidth, height, padding);
    
    // === SIDE COLUMN: Rotating content + controls ===
    this.createSideColumn(sideWidth, height, padding);

    // Shop items will be received via socket when session state changes
    // Only call loadJoinShopManifest as a fallback if socket updates fail
    // this.loadJoinShopManifest();
    
    // Load match settings (affects inventory display)
    this.loadMatchSettings();
    
    // Select battlefield early (during join phase) so we know the instance ID for revenue sharing
    this.selectBattlefield().then(() => {
      // Create battlefield row after battlefield selection to ensure image path is available
      this.createBattlefieldRow();
    });
    
    // Fade in animation
    this.tweens.add({
      targets: [this.sideContainer, this.centerContainer],
      alpha: { from: 0, to: 1 },
      duration: 600,
      ease: 'Power2'
    });

    // Listen for resize events
    this.scale.on('resize', this.handleResize, this);
    
    // Listen for chat messages
    try {
      window.addEventListener('soulwars-chat', this._onChatEvent);
      this.events.once('shutdown', () => {
        try { 
          window.removeEventListener('soulwars-chat', this._onChatEvent); 
        } catch (e) {
          console.warn('[JoinScene] Failed to remove chat listener:', e);
        }
      });
      console.log('[JoinScene] Chat listener registered');
    } catch (e) {
      console.warn('[JoinScene] Failed to register chat listener:', e);
    }

    // Listen for vote standings updates via SSE (fallback polling remains below)
    try {
      this._voteStandingsUpdateHandler = (event) => {
        try { this.updateVotePercentages(event?.detail); } catch (_) {}
      };
      window.addEventListener('vote-standings-update', this._voteStandingsUpdateHandler);
      this.events.once('shutdown', () => {
        try { window.removeEventListener('vote-standings-update', this._voteStandingsUpdateHandler); } catch (_) {}
      });
    } catch (_) {}

    // Listen for voting interest events
    this._onVotingInterest = (event) => { 
      console.log('🎯 [JoinScene] Voting interest event received!', event);
      this.votingInterestExpressed = true; 
      showMarqueeMessage('🎯 Voting interest expressed!', { color: '#10B981' }, 'high');
      console.log('🎯 [JoinScene] votingInterestExpressed set to:', this.votingInterestExpressed);

    };
    window.addEventListener('voting-interest-expressed', this._onVotingInterest);
    console.log('🎯 [JoinScene] Event listener registered for voting-interest-expressed');

    // Initialize auto-join system
    this.initializeAutoJoin();
    
    // Reset and initialize vote system
    VoteManager.reset();
    
    // Vote standings update timer (after players are loaded)
    this.voteStandingsTimer = this.time.addEvent({
      delay: 3000, // Poll every 3 seconds
      callback: () => this.updateVotePercentages(),
      callbackScope: this,
      loop: true
    });

    // Listen for hero inventory updates from server (when player buys items)
    // Set up listener when socket is authenticated or after a delay
    const setupInventoryListener = () => {
      if (socketService && socketService.socket && socketService.socket.connected) {
        // Remove existing listener if any
        if (this.inventoryUpdateHandler) {
          socketService.socket.off('hero-inventory-updated', this.inventoryUpdateHandler);
        }
        
        console.log('[JoinScene] Setting up hero-inventory-updated listener, socket connected:', socketService.socket.connected, 'socket id:', socketService.socket.id);
        this.inventoryUpdateHandler = (data) => {
          console.log('[JoinScene] ✅ Received hero-inventory-updated event:', data);
          this.handleHeroInventoryUpdate(data);
        };
        socketService.socket.on('hero-inventory-updated', this.inventoryUpdateHandler);
        
        // Set up balance-updated listener
        this.balanceUpdateHandler = (data) => {
          console.log('[JoinScene] ✅ Received balance-updated event:', data);
          this.handleBalanceUpdate(data);
        };
        socketService.socket.on('balance-updated', this.balanceUpdateHandler);
        console.log('[JoinScene] ✅ Hero inventory update listener registered');
      } else {
        console.warn('[JoinScene] Cannot set up inventory update listener: socketService or socket not available/connected');
      }
    };

    // Listen for server-selected hero for join command
    const setupHeroSelectionListener = () => {
      if (socketService && socketService.socket && socketService.socket.connected) {
        // Remove existing listener if any
        if (this.heroSelectionHandler) {
          socketService.socket.off('hero-selected-for-join', this.heroSelectionHandler);
        }
        
        console.log('[JoinScene] Setting up hero-selected-for-join listener');
        this.heroSelectionHandler = (data) => {
          console.log('[JoinScene] ✅ Received hero-selected-for-join event:', data);
          // Store server-selected hero data keyed by userId
          if (data.userId && data.hero) {
            this.serverSelectedHeroes.set(data.userId, data);
            console.log(`[JoinScene] Stored server-selected hero for user ${data.userId}: ${data.heroName} (${data.heroId})`);
            
            // Request fresh inventory refresh when hero is selected for join
            if (data.heroId && socketService && socketService.refreshHeroInventory) {
              const sessionId = sessionStateManager.getSessionId();
              socketService.refreshHeroInventory(data.heroId, sessionId);
              console.log(`[JoinScene] Requested inventory refresh for hero-selected-for-join ${data.heroId}`);
            }
          }
        };
        socketService.socket.on('hero-selected-for-join', this.heroSelectionHandler);
        console.log('[JoinScene] ✅ Hero selection listener registered');
      } else {
        console.warn('[JoinScene] Cannot set up hero selection listener: socketService or socket not available/connected');
      }
    };

    // Listen for shop items updates from server
    const setupShopItemsListener = () => {
      if (socketService && socketService.socket && socketService.socket.connected) {
        // Remove existing listener if any
        if (this.shopItemsUpdateHandler) {
          socketService.socket.off('shop-items-updated', this.shopItemsUpdateHandler);
        }
        
        console.log('[JoinScene] Setting up shop-items-updated listener');
        this.shopItemsUpdateHandler = (data) => {
          console.log('[JoinScene] ✅ Received shop-items-updated event:', data);
          this.handleShopItemsUpdate(data);
        };
        socketService.socket.on('shop-items-updated', this.shopItemsUpdateHandler);
        console.log('[JoinScene] ✅ Shop items update listener registered');
      } else {
        console.warn('[JoinScene] Cannot set up shop items update listener: socketService or socket not available/connected');
      }
    };

    // Listen for ranked vote updates from server
    const setupRankedVoteListener = () => {
      if (socketService && socketService.socket && socketService.socket.connected) {
        // Remove existing listener if any
        if (this.rankedVoteUpdateHandler) {
          socketService.socket.off('ranked-vote-updated', this.rankedVoteUpdateHandler);
        }
        
        console.log('[JoinScene] Setting up ranked-vote-updated listener');
        this.rankedVoteUpdateHandler = (data) => {
          console.log('[JoinScene] ✅ Received ranked-vote-updated event:', data);
          if (data.voteStatus) {
            this.updateRankedVoteStatus(data.voteStatus);
          }
        };
        socketService.socket.on('ranked-vote-updated', this.rankedVoteUpdateHandler);
        console.log('[JoinScene] ✅ Ranked vote update listener registered');
      } else {
        console.warn('[JoinScene] Cannot set up ranked vote update listener: socketService or socket not available/connected');
      }
    };

    // Try to set up immediately if socket is already connected
    if (socketService && socketService.socket && socketService.socket.connected) {
      setupInventoryListener();
      setupHeroSelectionListener();
      setupShopItemsListener();
      setupRankedVoteListener();
    } else {
      // Otherwise, wait for socket to connect or set up after delay
      if (socketService && socketService.socket) {
        socketService.socket.once('authenticated', () => {
          console.log('[JoinScene] Socket authenticated, setting up listeners');
          setupInventoryListener();
          setupHeroSelectionListener();
          setupShopItemsListener();
          setupRankedVoteListener();
        });
      }
      
      // Fallback: set up after delay
      this.time.delayedCall(1000, () => {
        if (!this.inventoryUpdateHandler) {
          console.log('[JoinScene] Setting up inventory listener after delay (fallback)');
          setupInventoryListener();
        }
        if (!this.heroSelectionHandler) {
          console.log('[JoinScene] Setting up hero selection listener after delay (fallback)');
          setupHeroSelectionListener();
        }
      });
    }
  }

  /**
   * Handle hero inventory update from server (after purchase)
   */
  handleHeroInventoryUpdate(data) {
    console.log('[JoinScene] Handling hero inventory update:', data);
    try {
      const { heroId, userId, inventory, changeDetails, heroName } = data;
      
      if (!heroId || !inventory) {
        console.warn('[JoinScene] Invalid inventory update data:', data);
        return;
      }

      // Find the player by heroId
      const player = this.findPlayerByHeroId(heroId);
      if (!player) {
        console.log(`[JoinScene] Player with heroId ${heroId} not found in current roster, skipping inventory update`);
        return;
      }

      // Update player's hero inventory
      if (!player.hero) {
        player.hero = {};
      }
      player.hero.inventory = Array.isArray(inventory) ? [...inventory] : [];
      
      // Also update player.inventory for compatibility
      player.inventory = player.hero.inventory;

      console.log(`[JoinScene] Updated inventory for player ${player.name} (heroId: ${heroId})`);

      // Refresh the inventory display (this will apply max stack limits via limitInventoryPerContainer)
      this.refreshPlayerInventoryDisplay(player);

      // Handle purchase notification if changeDetails exists
      if (changeDetails) {
        const displayHeroName = heroName || player.hero?.name || player.name;
        const itemName = changeDetails.itemName || changeDetails.name || 'item';
        const quantity = changeDetails.quantity || 1;
        
        // Send marquee message
        showMarqueeMessage(
          `${displayHeroName} purchased ${quantity} ${itemName}`,
          { color: '#10B981' },
          'medium'
        );

        // Show popup effect over player's grid box
        this.showPurchasePopup(player, changeDetails);
      }
    } catch (error) {
      console.error('[JoinScene] Error handling hero inventory update:', error);
    }
  }

  /**
   * Handle balance update from server (via socket)
   */
  handleBalanceUpdate(data) {
    console.log('[JoinScene] Handling balance update:', data);
    try {
      const { userId, balances, tokenSymbol } = data;
      
      if (!userId || !balances) {
        console.warn('[JoinScene] Invalid balance update data:', data);
        return;
      }

      // Use token symbol from server (shop's token for current session state)
      // Fallback to shopManager or default if not provided
      const shopTokenSymbol = tokenSymbol || this.shopManager?.tokenMeta?.symbol || 'SOULWARS';
      const tokenSymbolLower = shopTokenSymbol.toLowerCase();
      const chain = 'solana'; // Default chain for shop

      // Extract balance for the shop token
      const chainBalances = balances[chain] || {};
      /*
      [JoinScene] Chain balances: {SOULWARS: '1226516.333300000000000000', ICE: '41100.000000000000000000'}
      */
      console.log('[JoinScene] Chain balances:', chainBalances);

      const tokenSymbolUpper = tokenSymbolLower.toUpperCase();
      const tokenBalance = chainBalances[tokenSymbolUpper] || '0';
      console.log('[JoinScene] Token balance:', tokenBalance);
      const balance = Math.floor(parseFloat(tokenBalance) || 0);

      // Find the player by userId
      const player = this.findPlayerByUserId(userId);
      if (!player) {
        console.log(`[JoinScene] Player with userId ${userId} not found in current roster, flashing balance at bottom of join grid`);
        // Flash balance at bottom of join grid panel
        this.flashBalanceAtBottomOfGrid(balance, shopTokenSymbol);
        return;
      }

      // Find the player's display object
      const displayRecord = this.playerDisplayObjects.find(
        (entry) => entry && entry.player === player
      );
      
      if (!displayRecord || !displayRecord.container) {
        console.warn('[JoinScene] Could not find display record for balance update:', player.name);
        // Fallback: flash at bottom of grid
        this.flashBalanceAtBottomOfGrid(balance, shopTokenSymbol);
        return;
      }

      console.log(`[JoinScene] Balance for ${player.name}: ${balance} ${shopTokenSymbol}`);

      // Flash the balance in the displayObject
      this.flashBalanceInDisplay(displayRecord, balance, shopTokenSymbol);
    } catch (error) {
      console.error('[JoinScene] Error handling balance update:', error);
    }
  }

  /**
   * Handle shop items update from server (via socket)
   */
  handleShopItemsUpdate(data) {
    console.log('[JoinScene] ✅ handleShopItemsUpdate:', data);
    try {
      if (this.shopManager) {
        this.shopManager.updateShopItemsFromSocket(data);
      }
    } catch (error) {
      console.error('[JoinScene] Error handling shop items update:', error);
    }
  }

  /**
   * Flash balance text in a player's displayObject
   */
  flashBalanceInDisplay(displayRecord, balance, tokenSymbol) {
    try {
      const container = displayRecord.container;
      const { cellWidth, cellHeight } = this.gridConfig || { cellWidth: 150, cellHeight: 200 };
      
      // Position balance text near the level text (top of container)
      const balanceY = -cellHeight / 2 + 45; // Just below level text
      
      // Create balance text
      const balanceText = this.add.text(0, balanceY, `${balance.toLocaleString()} ${tokenSymbol}`, {
        fontSize: '24px',
        color: '#F3F300', // Gold/yellow color
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 3,
        backgroundColor: '#000000',
        padding: { x: 8, y: 4 }
      }).setOrigin(0.5);
      balanceText.setDepth(10001); // High depth to appear on top
      container.add(balanceText);

      // Flash animation: scale up, then fade out
      this.tweens.add({
        targets: balanceText,
        scaleX: { from: 0.5, to: 1.2 },
        scaleY: { from: 0.5, to: 1.2 },
        duration: 300,
        ease: 'Back.easeOut'
      });

      this.tweens.add({
        targets: balanceText,
        scaleX: 1.0,
        scaleY: 1.0,
        duration: 200,
        delay: 300,
        ease: 'Power2'
      });

      // Hold for 2 seconds, then fade out
      this.tweens.add({
        targets: balanceText,
        alpha: { from: 1, to: 0 },
        delay: 2000,
        duration: 500,
        ease: 'Power2',
        onComplete: () => {
          try {
            balanceText.destroy();
          } catch (e) {
            console.warn('[JoinScene] Error destroying balance text:', e);
          }
        }
      });
    } catch (error) {
      console.error('[JoinScene] Error flashing balance:', error);
    }
  }

  /**
   * Flash balance text at the bottom of the join grid panel
   * Used when no matching player is found in the grid
   */
  flashBalanceAtBottomOfGrid(balance, tokenSymbol) {
    try {
      if (!this.centerContainer) {
        console.warn('[JoinScene] Center container not available for balance flash');
        return;
      }

      // Position at bottom of the players area (centerContainer)
      const playersAreaHeight = this.playersAreaHeight || 600;
      const balanceY = playersAreaHeight / 2 - 60; // 30px from bottom
      
      // Create balance text
      const balanceText = this.add.text(0, balanceY, `${balance.toLocaleString()} ${tokenSymbol}`, {
        fontSize: '56px', // Larger for visibility at bottom
        color: '#F3F300', // Gold/yellow color
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 4,
        backgroundColor: '#000000',
        padding: { x: 12, y: 6 }
      }).setOrigin(0.5);
      balanceText.setDepth(10001); // High depth to appear on top
      this.centerContainer.add(balanceText);

      // Flash animation: scale up, then fade out
      this.tweens.add({
        targets: balanceText,
        scaleX: { from: 0.5, to: 1.2 },
        scaleY: { from: 0.5, to: 1.2 },
        duration: 300,
        ease: 'Back.easeOut'
      });

      this.tweens.add({
        targets: balanceText,
        scaleX: 1.0,
        scaleY: 1.0,
        duration: 200,
        delay: 300,
        ease: 'Power2'
      });

      // Hold for 2 seconds, then fade out
      this.tweens.add({
        targets: balanceText,
        alpha: { from: 1, to: 0 },
        delay: 2000,
        duration: 500,
        ease: 'Power2',
        onComplete: () => {
          try {
            balanceText.destroy();
          } catch (e) {
            console.warn('[JoinScene] Error destroying balance text:', e);
          }
        }
      });
    } catch (error) {
      console.error('[JoinScene] Error flashing balance at bottom of grid:', error);
    }
  }

  /**
   * Show a popup effect with icon and quantity over a player's grid box
   */
  showPurchasePopup(player, changeDetails) {
    try {
      // Find the player's display object to get container position
      const displayRecord = this.playerDisplayObjects.find(
        (entry) => entry && entry.player === player
      );
      
      if (!displayRecord || !displayRecord.container) {
        console.warn('[JoinScene] Could not find display record for player:', player.name);
        return;
      }

      const container = displayRecord.container;
      
      // Get world position of the container (containers can have local coordinates)
      const containerBounds = container.getBounds();
      const containerX = containerBounds.centerX;
      const containerY = containerBounds.centerY;
      
      // Alternative: try to get world transform
      let worldX = containerX;
      let worldY = containerY;
      try {
        const worldTransform = container.getWorldTransform();
        if (worldTransform) {
          worldX = worldTransform.tx || containerX;
          worldY = worldTransform.ty || containerY;
        }
      } catch (e) {
        console.warn('[JoinScene] Could not get world transform, using bounds:', e);
      }

      // Position popup centered over the hero sprite area (sprite is typically at y: 0 relative to container)
      const popupStartY = worldY - 30; // Position above the center of the container

      const iconKey = changeDetails.iconKey;
      const quantity = changeDetails.quantity || 1;
      const tokenCost = changeDetails.tokenCost || 0;

      // Stage 1: Show token cost subtraction
      const costContainer = this.add.container(worldX, popupStartY);
      costContainer.setDepth(10000);
      
      const costText = this.add.text(0, 0, `-${tokenCost.toLocaleString()}`, {
        fontSize: '28px',
        color: '#EF4444', // Red for subtraction
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 4
      }).setOrigin(0.5);
      costContainer.add(costText);

      // Animate cost: float up and fade out
      this.tweens.add({
        targets: costContainer,
        y: popupStartY - 40,
        duration: 1000,
        ease: 'Quad.easeOut'
      });

      this.tweens.add({
        targets: costContainer,
        alpha: { from: 1, to: 0 },
        delay: 300,
        duration: 700,
        ease: 'Quad.easeOut',
        onComplete: () => {
          try {
            costContainer.destroy();
          } catch (e) {
            console.warn('[JoinScene] Error destroying cost container:', e);
          }
        }
      });

      // Stage 2: Show item gain (after a short delay)
      this.time.delayedCall(400, () => {
        const itemContainer = this.add.container(worldX, popupStartY);
        itemContainer.setDepth(10000);

        // Create icon image if texture exists
        let iconImage = null;
        if (iconKey && this.textures.exists(iconKey)) {
          iconImage = this.add.image(0, 0, iconKey);
          iconImage.setScale(0.8);
          iconImage.setOrigin(0.5);
          itemContainer.add(iconImage);
        } else {
          console.warn('[JoinScene] Icon key not found:', iconKey);
        }

        // Create quantity text
        const quantityText = this.add.text(0, iconImage ? 25 : 0, `+${quantity}`, {
          fontSize: '24px',
          color: '#10B981', // Green for addition
          fontFamily: 'Arial, sans-serif',
          fontStyle: 'bold',
          stroke: '#000000',
          strokeThickness: 3
        }).setOrigin(0.5);
        itemContainer.add(quantityText);

        // Animate item: float up and fade out
        this.tweens.add({
          targets: itemContainer,
          y: popupStartY - 60,
          duration: 1500,
          ease: 'Quad.easeOut'
        });

        this.tweens.add({
          targets: itemContainer,
          alpha: { from: 1, to: 0 },
          delay: 500,
          duration: 1000,
          ease: 'Quad.easeOut',
          onComplete: () => {
            try {
              itemContainer.destroy();
            } catch (e) {
              console.warn('[JoinScene] Error destroying item container:', e);
            }
          }
        });
      });
    } catch (error) {
      console.error('[JoinScene] Error showing purchase popup:', error);
    }
  }

  initializeAutoJoin() {
    console.log('[JoinScene] Initializing auto-join system...');
    
    // Auto-join users with remaining credits
    try {
      autoJoinRemaining.forEach(async (entry, userId) => {
        const record = this.getAutoJoinRecord(userId);
        const creditsRemaining = Number(record.credits) || 0;
        if (creditsRemaining <= 0) {
          return;
        }

        console.log(`🎫 Auto-joining user ${userId} with ${creditsRemaining} credits`);

        try {
          // Fetch fresh user data
          const userResponse = await fetch(`/api/users/${userId}`, {
            headers: {
              'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key'
            }
          });

          // Fetch fresh hero data from the dedicated heroes endpoint
          const heroesResponse = await fetch(`/api/heroes/user/${userId}`, {
            headers: {
              'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key'
            }
          });

          let playerData = {
            username: record.lastUsername || 'Auto-Join User',
            walletAddress: record.lastWallet || null,
            platform: record.lastPlatform || null,
            userId,
            heroId: null,
            heroName: record.lastUsername || 'Auto-Join User',
            heroLevel: 1,
            sprite: null,
            credits: creditsRemaining,
            autoJoin: true,
            isAutoJoin: true
          };

          // Get user data for username/wallet/platform info
          if (userResponse.ok) {
            const userData = await userResponse.json();
            const resolvedName = userData.chat_name || userData.username || playerData.username;
            const resolvedWallet = userData.solana_wallet_address || playerData.walletAddress;
            const resolvedPlatform = userData.last_platform || userData.platform || userData.signup_platform || playerData.platform;

            playerData.username = resolvedName;
            playerData.heroName = playerData.heroName || resolvedName;
            playerData.walletAddress = resolvedWallet;
            if (!playerData.platform && resolvedPlatform) {
              playerData.platform = resolvedPlatform.toString().toLowerCase();
            }

            this.updateAutoJoinRecord(userId, {
              lastUsername: resolvedName || record.lastUsername || null,
              lastWallet: resolvedWallet || record.lastWallet || null,
              lastPlatform: playerData.platform || record.lastPlatform || null
            });
          }

          // Check if server has already selected a hero for this user (for auto-join)
          const serverSelectedHero = this.serverSelectedHeroes.get(userId);
          if (serverSelectedHero && serverSelectedHero.hero) {
            console.log('[JoinScene] Using server-selected hero for auto-join user', userId);
            const hero = serverSelectedHero.hero;
            playerData.heroId = hero.id;
            playerData.heroName = hero.name;
            playerData.heroLevel = hero.level || 1;
            playerData.sprite = hero.image_url;
            playerData.health = hero.health || 100;
            playerData.attack = hero.attack || 50;
            playerData.defense = hero.defense || 30;
            playerData.speed = hero.speed || 40;
            playerData.quickness = hero.quickness || 10;
            playerData.coordination = hero.coordination || 10;
            playerData.strength = hero.strength || 10;
            playerData.endurance = hero.endurance || 10;
            playerData.focus = hero.focus || 10;
            playerData.self = hero.self || 10;
            playerData.mana = hero.mana || 100;
            playerData.stamina = hero.stamina || 100;
            playerData.inventory = Array.isArray(hero.inventory) ? hero.inventory : [];
            console.log('[JoinScene] Using server-provided inventory for auto-join (no API call needed)');
          } else {
            // Fallback: Get fresh hero data and select locally (if server hasn't selected yet)
            if (heroesResponse.ok) {
              const heroesData = await heroesResponse.json();
              if (heroesData && heroesData.data && heroesData.data.length > 0) {
                // Select hero based on battle frequency settings (fallback if server hasn't selected)
                console.log('[JoinScene] Server hasn\'t selected hero yet for auto-join, selecting locally by frequency');
                const hero = this.selectHeroByFrequency(heroesData.data);
                playerData.heroId = hero.id;
                playerData.heroName = hero.name;
                playerData.heroLevel = hero.level || 1;
                playerData.sprite = hero.image_url;
                playerData.health = hero.health || 100;
                playerData.attack = hero.attack || 50;
                playerData.defense = hero.defense || 30;
                playerData.speed = hero.speed || 40;
                playerData.quickness = hero.quickness || 10;
                playerData.coordination = hero.coordination || 10;
                playerData.strength = hero.strength || 10;
                playerData.endurance = hero.endurance || 10;
                playerData.focus = hero.focus || 10;
                playerData.self = hero.self || 10;
                playerData.mana = hero.mana || 100;
                playerData.stamina = hero.stamina || 100;
                try {
                  const fetchedInventory = await this.fetchHeroInventorySnapshot(hero.id);
                  if (Array.isArray(fetchedInventory) && fetchedInventory.length > 0) {
                    playerData.inventory = fetchedInventory;
                  } else {
                    playerData.inventory = hero.inventory || [];
                  }
                } catch (inventoryError) {
                  console.warn('[JoinScene] Failed to fetch inventory for auto-joined hero', hero.id, inventoryError);
                  playerData.inventory = hero.inventory || [];
                }
              }
            }
          }

          if (!playerData.walletAddress) {
            playerData.walletAddress = record.lastWallet || `auto_user_${userId}`;
          }

          if (!playerData.platform) {
            playerData.platform = (record.lastPlatform || 'unknown').toString().toLowerCase();
          } else {
            playerData.platform = playerData.platform.toString().toLowerCase();
          }

          const added = this.addPlayer(playerData);
          if (added) {
            // Track hero in session on server side via socket
            if (playerData.heroId && playerData.userId && playerData.heroName) {
              const sessionId = sessionStateManager.getSessionId();
              if (sessionId && socketService) {
                socketService.addSessionHero(
                  sessionId,
                  playerData.heroId,
                  playerData.userId,
                  playerData.heroName,
                  true // isAutoJoin
                );
                console.log(`[JoinScene] Tracked auto-join hero ${playerData.heroName} (${playerData.heroId}) in session ${sessionId} via socket`);
                
                // Request fresh inventory refresh via socket
                if (socketService.refreshHeroInventory) {
                  socketService.refreshHeroInventory(playerData.heroId, sessionId);
                  console.log(`[JoinScene] Requested inventory refresh for auto-join hero ${playerData.heroId}`);
                }
              }
            }

            this.updateAutoJoinRecord(userId, {
              lastPlatform: playerData.platform || record.lastPlatform || null,
              lastWallet: playerData.walletAddress || record.lastWallet || null,
              lastUsername: playerData.username || playerData.heroName || record.lastUsername || null,
              lastJoinSource: 'auto',
              lastJoinAt: Date.now()
            });
            console.log(`🎫 Auto-joined user ${userId} with fresh hero data`);
          } else {
            console.log(`🎫 Auto-join skipped for user ${userId} (already present or lobby locked)`);
          }

        } catch (error) {
          console.error(`🎫 Error fetching data for auto-join user ${userId}:`, error);
        }
      });
    } catch (error) {
      console.error('Error initializing auto-join:', error);
    }

    // Process join queue with delay
    this.processJoinQueue();
  }

  processJoinQueue() {
    console.log('[JoinScene] Processing join queue...', joinQueue.length, 'requests');

    if (this.playersLockedForVoting) {
      console.log('[JoinScene] Players locked for voting - deferring join queue processing');
      return;
    }

    if (joinQueue.length === 0) {
      return;
    }

    const processNext = () => {
      if (this.playersLockedForVoting) {
        console.log('[JoinScene] Queue processing halted - players locked for voting');
        return;
      }

      if (joinQueue.length === 0) {
        console.log('[JoinScene] Finished processing join queue');
        return;
      }

      const joinRequest = joinQueue.shift();
      if (!joinRequest) {
        return;
      }

      console.log('[JoinScene] Processing join queue entry:', joinRequest);

      this.enrichAndAddQueuedPlayer(joinRequest).then(result => {
        const status = result?.status || 'retry';
        const reason = result?.reason || 'unknown';
        let delay = 250;

        if (status === 'added' || status === 'already-present') {
          this.time.delayedCall(delay, processNext);
          return;
        }

        const retryDelay = this.scheduleJoinRetry(joinRequest, reason);
        delay = Math.max(delay, retryDelay ?? delay);
        this.time.delayedCall(delay, processNext);
      }).catch(error => {
        console.error('[JoinScene] Error processing join request:', error);
        const retryDelay = this.scheduleJoinRetry(joinRequest, 'exception');
        const delay = retryDelay != null ? retryDelay : 1000;
        this.time.delayedCall(delay, processNext);
      });
    };

    this.time.delayedCall(500, processNext);
  }

  async enrichAndAddQueuedPlayer(joinRequest) {
    const safeUsername = joinRequest?.username || 'Player';

    try {
      // Fetch fresh user data
      const userResponse = await fetch(`/api/users/${joinRequest.userId}`, {
        headers: {
          'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key'
        }
      });

      // Build query params for hero creation if needed
      const heroParams = new URLSearchParams();
      heroParams.append('createIfNone', 'true');
      if (joinRequest.authorAvatar && joinRequest.platform === 'x') {
        heroParams.append('avatarUrl', joinRequest.authorAvatar);
      }
      
      // Fetch fresh hero data from the dedicated heroes endpoint
      const heroesResponse = await fetch(`/api/heroes/user/${joinRequest.userId}?${heroParams.toString()}`, {
        headers: {
          'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key'
        }
      });

      let enrichedData = {
        username: joinRequest.username,
        walletAddress: joinRequest.wallet,
        platform: joinRequest.platform,
        userId: joinRequest.userId,
        heroId: null,
        heroName: joinRequest.username,
        heroLevel: 1,
        sprite: null,
        credits: 0
      };

      // Get user data for username/wallet info
      let userData = null;
      if (userResponse.ok) {
        userData = await userResponse.json();
        enrichedData.username = userData.chat_name || userData.username || joinRequest.username;
        enrichedData.walletAddress = userData.solana_wallet_address || joinRequest.wallet;
      }

      let selectedHero = null;

      // Check if server has already selected a hero for this user
      const serverSelectedHero = this.serverSelectedHeroes.get(joinRequest.userId);
      if (serverSelectedHero && serverSelectedHero.hero) {
        console.log('[JoinScene] Using server-selected hero for user', joinRequest.userId);
        const hero = serverSelectedHero.hero;
        selectedHero = hero;
        enrichedData.heroId = hero.id;
        enrichedData.heroName = hero.name;
        enrichedData.heroLevel = hero.level || 1;
        enrichedData.sprite = hero.image_url;
        enrichedData.health = hero.health || 100;
        enrichedData.attack = hero.attack || 50;
        enrichedData.defense = hero.defense || 30;
        enrichedData.speed = hero.speed || 40;
        enrichedData.quickness = hero.quickness || 10;
        enrichedData.coordination = hero.coordination || 10;
        enrichedData.strength = hero.strength || 10;
        enrichedData.endurance = hero.endurance || 10;
        enrichedData.focus = hero.focus || 10;
        enrichedData.self = hero.self || 10;
        enrichedData.mana = hero.mana || 100;
        enrichedData.stamina = hero.stamina || 100;
        enrichedData.inventory = Array.isArray(hero.inventory) ? hero.inventory : [];
        console.log('[JoinScene] Using server-provided inventory (no API call needed)');
      } else {
        // Fallback: Get fresh hero data and select locally (if server hasn't selected yet)
        if (heroesResponse.ok) {
          const heroesData = await heroesResponse.json();
          console.log('[JoinScene] Heroes data', { heroesData: heroesData.data });
          if (heroesData && heroesData.data && heroesData.data.length > 0) {
            // Select hero based on battle frequency settings (fallback if server hasn't selected)
            console.log('[JoinScene] Server hasn\'t selected hero yet, selecting locally by frequency', { heroesData: heroesData.data });
            selectedHero = this.selectHeroByFrequency(heroesData.data);
            enrichedData.heroId = selectedHero.id;
            enrichedData.heroName = selectedHero.name;
            enrichedData.heroLevel = selectedHero.level || 1;
            enrichedData.sprite = selectedHero.image_url;
            enrichedData.health = selectedHero.health || 100;
            enrichedData.attack = selectedHero.attack || 50;
            enrichedData.defense = selectedHero.defense || 30;
            enrichedData.speed = selectedHero.speed || 40;
            enrichedData.quickness = selectedHero.quickness || 10;
            enrichedData.coordination = selectedHero.coordination || 10;
            enrichedData.strength = selectedHero.strength || 10;
            enrichedData.endurance = selectedHero.endurance || 10;
            enrichedData.focus = selectedHero.focus || 10;
            enrichedData.self = selectedHero.self || 10;
            enrichedData.mana = selectedHero.mana || 100;
            enrichedData.stamina = selectedHero.stamina || 100;
            try {
              const fetchedInventory = await this.fetchHeroInventorySnapshot(selectedHero.id);
              if (Array.isArray(fetchedInventory) && fetchedInventory.length > 0) {
                enrichedData.inventory = fetchedInventory;
              } else {
                enrichedData.inventory = selectedHero.inventory || [];
              }
            } catch (inventoryError) {
              console.warn('[JoinScene] Failed to fetch inventory for queued hero', selectedHero.id, inventoryError);
              enrichedData.inventory = selectedHero.inventory || [];
            }
          }
        }
      }

      if (!enrichedData.heroId) {
        console.warn('[JoinScene] No hero data available for join request, deferring join', joinRequest);
        return { status: 'retry', reason: 'missing-hero' };
      }

      // Ensure heroName is set from the selected hero, not from username/wallet
      if (selectedHero && selectedHero.name && !enrichedData.heroName) {
        enrichedData.heroName = selectedHero.name;
        console.log('[JoinScene] Set heroName from selectedHero:', selectedHero.name);
      }

      //console.log('[JoinScene] Adding queued player with fresh hero data:', enrichedData);

      // Add player to the scene
      const added = this.addPlayer(enrichedData);
      if (!added) {
        const userId = joinRequest.userId || enrichedData.userId || null;
        const walletKey = enrichedData.walletAddress || joinRequest.wallet || null;
        const existing = (userId ? this.findPlayerByUserId(userId) : null) ||
          (walletKey ? this.findPlayerByWallet(walletKey) : null);

        if (existing) {
          console.log('[JoinScene] Player already present in roster, skipping duplicate add');
          return { status: 'already-present' };
        }

        if (this.players.length >= (this.maxHeroes || 25)) {
          return { status: 'retry', reason: 'grid-full' };
        }

        console.warn('[JoinScene] addPlayer rejected join request, will retry later');
        return { status: 'retry', reason: 'add-rejected' };
      }

      // Track hero in session on server side via socket (for queued joins)
      if (enrichedData.heroId && enrichedData.userId && enrichedData.heroName) {
        const sessionId = sessionStateManager.getSessionId();
        if (sessionId && socketService) {
          socketService.addSessionHero(
            sessionId,
            enrichedData.heroId,
            enrichedData.userId,
            enrichedData.heroName,
            false // isAutoJoin = false for queued joins
          );
          console.log(`[JoinScene] Tracked queued hero ${enrichedData.heroName} (${enrichedData.heroId}) in session ${sessionId} via socket`);
          
          // Request fresh inventory refresh via socket
          if (socketService.refreshHeroInventory) {
            socketService.refreshHeroInventory(enrichedData.heroId, sessionId);
            console.log(`[JoinScene] Requested inventory refresh for queued hero ${enrichedData.heroId}`);
          }
        }
      }

      // Show confirmation emote
      if (this.showEmoteForWallet) {
        try {
          this.showEmoteForWallet(enrichedData.walletAddress || joinRequest.wallet, '✅');
        } catch (emoteError) {
          console.warn('[JoinScene] Failed to show confirmation emote:', emoteError);
        }
      }

      return { status: 'added' };

    } catch (error) {
      console.error('[JoinScene] Error enriching queued player data:', error);
      return { status: 'retry', reason: 'fetch-error', error, username: safeUsername };
    }
  }

  scheduleJoinRetry(joinRequest, reason = 'unknown') {
    const currentRetry = joinRequest?.retryCount || 0;
    const maxSlots = this.maxHeroes || 25;
    const defaultDelay = this.players.length >= maxSlots ? 2000 : 750;

    if (reason === 'grid-full') {
      const updatedRequest = {
        ...joinRequest,
        retryCount: currentRetry,
        timestamp: Date.now()
      };
      joinQueue.push(updatedRequest);
      return 2000;
    }

    const nextRetry = currentRetry + 1;
    if (nextRetry > this.maxJoinRetries) {
      this.notifyJoinFailure(joinRequest, reason);
      return 750;
    }

    const updatedRequest = {
      ...joinRequest,
      retryCount: nextRetry,
      timestamp: Date.now()
    };

    joinQueue.push(updatedRequest);
    console.log(`[JoinScene] Scheduling join retry for ${joinRequest?.username || 'Player'} (attempt ${nextRetry}) reason: ${reason}`);
    return defaultDelay;
  }

  notifyJoinFailure(joinRequest, reason = 'unknown') {
    const username = joinRequest?.username || 'Player';
    console.warn(`[JoinScene] Join failed for ${username} after retries (reason: ${reason})`);
    let message;
    switch (reason) {
      case 'missing-hero':
        message = `❌ ${username} can't join yet - no hero data available.`;
        break;
      case 'fetch-error':
      case 'exception':
        message = `❌ ${username} couldn't join due to a sync error. Please try again.`;
        break;
      default:
        message = `❌ ${username} could not join. Please try again.`;
        break;
    }
    try {
      showMarqueeMessage(message, {
        color: '#EF4444'
      }, 'medium');
    } catch (error) {
      console.warn('[JoinScene] Failed to show join failure marquee message:', error);
    }
  }
  
  onExternalChat(ev) {
    
    console.log('[JoinScene] onExternalChat received:', ev);
    if (!ev || !ev.detail) {
      console.warn('[JoinScene] Invalid chat event:', ev);
      return;
    }

    // Scene might be shutting down or not ready
    if (!this.scene || !this.add || !this.game || !this.sys || this.sys.isDestroyed) {
      console.warn('[JoinScene] Scene not ready for chat message');
      return;
    }

    const m = ev.detail;
    const time = new Date(m.timestamp || Date.now()).toLocaleTimeString();
    const user = m.username || 'unknown';
    const userId = m.userId || null;
    const msg = (m.message == null ? '' : String(m.message));
    const wallet = (m && (m.userAddress || m.wallet || m.address)) || '';

    console.log('[JoinScene] Processing chat message:', {
      userId,
      user,
      wallet,
      message: msg,
      fullMessage: m
    });

    // Check if this message should trigger voting interest
    if (m.triggerVotingInterest) {
      console.log('🎯 [JoinScene] Server requested voting interest trigger');
      try {
        window.dispatchEvent(new CustomEvent('voting-interest-expressed'));
        console.log('✅ [JoinScene] Voting interest event dispatched');
      } catch (e) {
        console.warn('[JoinScene] Failed to dispatch voting interest event:', e);
      }
    }

    handleChatMessage(this, msg, { time, user, userId, raw: msg, fullMessage: m });

    // After processing chat message, check if there are new join queue entries to process
    this.processNewJoinQueueEntries();
  }

  /**
   * Process any new join queue entries that came in while JoinScene was active
   */
  processNewJoinQueueEntries() {
    if (joinQueue.length === 0) {
      return; // No entries to process
    }

    if (this.playersLockedForVoting) {
      console.log('[JoinScene] Players locked for voting - keeping join queue intact');
      return;
    }

    console.log('[JoinScene] Processing new join queue entries:', joinQueue.length);

    const processNext = () => {
      if (this.playersLockedForVoting) {
        console.log('[JoinScene] Stopping new join queue processing - players locked');
        return;
      }

      if (joinQueue.length === 0) {
        console.log('[JoinScene] Finished processing new join queue entries');
        return;
      }

      const joinRequest = joinQueue.shift();
      if (!joinRequest) {
        return;
      }

      console.log('[JoinScene] Processing new join queue request:', joinRequest);

      this.enrichAndAddQueuedPlayer(joinRequest).then(result => {
        const status = result?.status || 'retry';
        const reason = result?.reason || 'unknown';
        let delay = 250;

        if (status === 'added' || status === 'already-present') {
          this.time.delayedCall(delay, processNext);
          return;
        }

        const retryDelay = this.scheduleJoinRetry(joinRequest, reason);
        delay = Math.max(delay, retryDelay ?? delay);
        this.time.delayedCall(delay, processNext);
      }).catch(error => {
        console.error('[JoinScene] Error processing new join request:', error);
        const retryDelay = this.scheduleJoinRetry(joinRequest, 'exception');
        const delay = retryDelay != null ? retryDelay : 1000;
        this.time.delayedCall(delay, processNext);
      });
    };

    processNext();
  }
  
  createCenterColumn(columnWidth, height, padding) {
    const contentWidth = columnWidth;
    const contentHeight = height - padding * 2;
    this.centerContentWidth = contentWidth;

    const playersAreaHeight = contentHeight;
    this.playersAreaHeight = playersAreaHeight;

    this.playersAreaBg = this.add.rectangle(0, 0, contentWidth, playersAreaHeight, 0x1F2937);
    this.playersAreaBg.setStrokeStyle(2, 0x374151);
    this.playersAreaBg.setAlpha(0.8);
    this.centerContainer.add(this.playersAreaBg);

    const headerY = -playersAreaHeight / 2 + 30;
    const titleStyle = {
      fontSize: '26px',
      color: '#F3F4F6',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold'
    };
    this.playersHeaderText = this.add.text(-contentWidth / 2 + 20, headerY, 'Players Joined', titleStyle).setOrigin(0, 0.5);
    this.centerContainer.add(this.playersHeaderText);

    this.countersText = this.add.text(contentWidth / 2 - 20, headerY, '', {
      fontSize: '14px',
      color: '#9CA3AF',
      fontFamily: 'Arial, sans-serif'
    }).setOrigin(1, 0.5);
    this.centerContainer.add(this.countersText);
    this.updateCounters();

    this.playersPlaceholder = this.add.text(0 - 50, headerY, 'Waiting for players...', {
      fontSize: '18px',
      color: '#9CA3AF',
      fontFamily: 'Arial, sans-serif',
      align: 'center'
    }).setOrigin(0, 0.5);
    this.centerContainer.add(this.playersPlaceholder);

    const gridTopOffset = headerY + 40;
    this.playersGridContainer = this.add.container(0, gridTopOffset);
    this.centerContainer.add(this.playersGridContainer);
    this.playerDisplayObjects = [];

    const gridPadding = 20;
    this.gridPadding = gridPadding;
    this.applyGridConfig(this.calculateGridSize(0));

    this.joinOverlay = this.add.rectangle(0, 0, contentWidth, playersAreaHeight, 0x000000);
    this.joinOverlay.setAlpha(0.5);
    this.centerContainer.add(this.joinOverlay);

    this.joinOverlayText = this.add.text(0, 0, 'Type "Join" in Chat to Play', {
      fontSize: '128px',
      color: '#F3F300',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold',
      align: 'center',
      wordWrap: { width: contentWidth - 40 }
    }).setOrigin(0.5);
    this.centerContainer.add(this.joinOverlayText);

  }
  
  createSideColumn(columnWidth, height, padding) {
    const columnInnerWidth = columnWidth - padding * 2;
    const columnInnerHeight = height - padding * 2;

    const desiredControlsHeight = 180;
    let controlsHeight = Math.min(Math.max(desiredControlsHeight, 160), Math.max(160, columnInnerHeight - 200));

    const shopGap = 12;
    // Shop takes up all space except controls section
    const shopHeight = columnInnerHeight - controlsHeight - shopGap - 24;

    const columnBg = this.add.rectangle(0, 0, columnInnerWidth, columnInnerHeight, 0x111827);
    columnBg.setStrokeStyle(2, 0x374151);
    columnBg.setAlpha(0.92);
    this.sideContainer.add(columnBg);

    // Hide rotating content container (not used currently)
    // this.leftContentContainer = this.add.container(0, -columnInnerHeight / 2 + topHeight / 2);
    // this.sideContainer.add(this.leftContentContainer);
    // this.createLeaderboardContent(columnInnerWidth, topHeight);

    // Shop section (takes up all space above controls)
    const shopY = -columnInnerHeight / 2 + shopHeight / 2 + 12;
    this.shopContainer = this.add.container(0, shopY);
    this.sideContainer.add(this.shopContainer);
    this.shopHeight = shopHeight;
    this.shopManager.attachShopContainer(this.shopContainer);
    this.shopManager.buildShopSection(columnInnerWidth, shopHeight);

    // Controls section
    const controlsGap = 4;
    this.controlsGap = controlsGap;
    const controlsY = columnInnerHeight / 2 - controlsHeight / 2 + controlsGap - 5;
    this.controlsContainer = this.add.container(0, controlsY);
    this.sideContainer.add(this.controlsContainer);
    this.controlsHeight = controlsHeight;
    this.buildControlsSection(columnInnerWidth, controlsHeight);

    // Don't start rotating content (hidden for now)
    // this.startContentSwitching();
  }

  async loadJoinShopManifest() {
    // This is now a fallback method - shop items should come via socket
    // Only call this if socket updates fail or as a manual refresh
    console.log('[JoinScene] Loading shop manifest as fallback (shop items should come via socket)');
    try {
      const theme = getCurrentTheme();
      const themeId = theme?.id || 'soulwars_default';
      this.currentThemeId = themeId;
      await this.shopManager.refreshManifest({
        themeId,
        shopType: 'join',
        tokenSymbol: (process.env.REACT_APP_DEFAULT_SHOP_TOKEN || 'soulwars'),
        limit: 6,
      });
    } catch (error) {
      console.error('[JoinScene] Failed to load shop manifest:', error);
    }
  }

  buildControlsSection(sectionWidth, sectionHeight) {
    const innerPadding = 12;
    const controlsBg = this.add.rectangle(0, 0, sectionWidth, sectionHeight, 0x1F2937);
    controlsBg.setStrokeStyle(2, 0x374151);
    controlsBg.setAlpha(0.92);
    this.controlsContainer.add(controlsBg);

    const anchorX = -sectionWidth / 2 + innerPadding;
    let currentY = -sectionHeight / 2 + innerPadding;
    this.gearIcon = this.add.text(anchorX + 72, currentY + 7, '⚙️', { fontSize: '36px' })
    .setOrigin(0, 0)
    .setFontSize(30)
    .setLineSpacing(50);
    this.gearIcon.setInteractive({ useHandCursor: true });
    this.controlsContainer.add(this.gearIcon);

    this.gearIcon.on('pointerover', () => {
      this.gearIcon.setScale(1.1);
    });

    this.gearIcon.on('pointerout', () => {
      this.gearIcon.setScale(1);
    });

    this.gearIcon.on('pointerdown', () => {
      this.openConfigModal();
    });

    this.modeText = this.add.text(-75, currentY + 20, `Mode: ${this.gameMode}`, {
      fontSize: '24px',
      color: '#9CF3FF',
      align: 'center',
      fontFamily: 'Arial, sans-serif'
    }).setOrigin(0, 0.5);
    this.controlsContainer.add(this.modeText);

    currentY += 70;

    this.timerText = this.add.text(-40, currentY, this.formatTime(this.countdownSeconds), {
      fontSize: '50px',
      color: '#10B981',
      align: 'center',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold'
    }).setOrigin(0, 0.5);
    this.controlsContainer.add(this.timerText);

    currentY += 70;

    const buttonWidth = (sectionWidth - innerPadding * 3) / 2;
    const buttonY = sectionHeight / 2 - innerPadding - 25;

    this.pauseButton = this.add.rectangle(anchorX + buttonWidth / 2, buttonY, buttonWidth, 45, 0xF59E0B);
    this.pauseButton.setStrokeStyle(2, 0xD97706);
    this.pauseButton.setInteractive({ useHandCursor: true });
    this.controlsContainer.add(this.pauseButton);

    this.pauseText = this.add.text(this.pauseButton.x, buttonY, 'Pause', {
      fontSize: '18px',
      color: '#FFFFFF',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    this.controlsContainer.add(this.pauseText);

    this.pauseButton.on('pointerdown', () => {
      this.isPaused = !this.isPaused;
      this.pauseText.setText(this.isPaused ? 'Resume' : 'Pause');
      this.pauseButton.setFillStyle(this.isPaused ? 0x059669 : 0xF59E0B);
      this.pauseButton.setStrokeStyle(2, this.isPaused ? 0x047857 : 0xD97706);
      if (this.votingCountdownTimer) {
        try { this.votingCountdownTimer.paused = this.isPaused; } catch (_) {}
      }
    });

    this.pauseButton.on('pointerover', () => {
      if (!this.isPaused) {
        this.pauseButton.setFillStyle(0xD97706);
      }
    });

    this.pauseButton.on('pointerout', () => {
      this.pauseButton.setFillStyle(this.isPaused ? 0x059669 : 0xF59E0B);
    });

    const startButtonX = anchorX + buttonWidth + innerPadding + buttonWidth / 2;
    this.startButton = this.add.rectangle(startButtonX, buttonY, buttonWidth, 45, 0x10B981);
    this.startButton.setStrokeStyle(2, 0x059669);
    this.startButton.setInteractive({ useHandCursor: true });
    this.controlsContainer.add(this.startButton);

    const startText = this.add.text(startButtonX, buttonY, 'Start Game', {
      fontSize: '18px',
      color: '#FFFFFF',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    this.controlsContainer.add(startText);

    this.startButton.on('pointerdown', () => {
      console.log('[JoinScene] Starting game with:', {
        gameMode: this.gameMode,
        teamCount: this.teamCount,
        maxHeroes: this.maxHeroes
      });
      this.startBattle();
    });

    this.startButton.on('pointerover', () => {
      this.startButton.setFillStyle(0x059669);
      this.startButton.setScale(1.05);
    });

    this.startButton.on('pointerout', () => {
      this.startButton.setFillStyle(0x10B981);
      this.startButton.setScale(1);
    });
  }

  setShopItems(items = []) {
    this.shopManager.setShopItems(items);
  }

  handleShopBuyCommand(config) {
    return this.shopManager.handleShopBuyCommand(config);
  }

  refreshPlayerInventoryDisplay(player, recordOverride) {
    const record =
      recordOverride ||
      this.playerDisplayObjects.find((entry) => entry && entry.player === player);
    if (!record || !record.container) {
      return;
    }

    const cleanupIcons = Array.isArray(record.inventoryIcons) ? record.inventoryIcons : [];
    cleanupIcons.forEach((obj) => {
      if (obj && typeof obj.destroy === 'function') {
        obj.destroy();
      }
    });
    record.inventoryIcons = [];

    let inventorySource =
      (player && player.hero && Array.isArray(player.hero.inventory) && player.hero.inventory) ||
      (player && Array.isArray(player.inventory) && player.inventory) ||
      [];

    if (!inventorySource.length) {
      return;
    }

    // Apply inventory limit (maxBattleStack items per container) for display
    if (this.itemsById && inventorySource.length > 0) {
      try {
        // Use nullish coalescing to allow 0 as a valid value
        const maxStack = this.maxBattleStack ?? 10;
        inventorySource = limitInventoryPerContainer(inventorySource, this.itemsById, maxStack);
      } catch (err) {
        console.warn('[JoinScene] Failed to limit inventory for display:', err);
      }
    }

    const slotBuckets = new Map();
    inventorySource.forEach((item) => {
      if (!item) return;
      const slot = item.equipmentSlot ?? item.equipment_slot ?? 'general';
      if (!slotBuckets.has(slot)) {
        slotBuckets.set(slot, []);
      }
      slotBuckets.get(slot).push(item);
    });

    const buildSlotEntries = () => {
      const results = [];
      slotBuckets.forEach((items, slot) => {
        const normalized = Array.isArray(items) ? [...items] : [];
        if (!normalized.length) return;
        const sorted = normalized.sort((a, b) => {
          // Slot numbers removed - no longer sorting by slot
          const aSlot = 0;
          const bSlot = 0;
          return aSlot - bSlot;
        });
        const representative =
          sorted.find((entry) => (entry.quantity ?? entry.qty ?? entry.amount ?? 0) > 0) ||
          sorted[0];
        const totalQty = normalized.reduce(
          (sum, entry) => sum + (Number(entry.quantity ?? entry.qty ?? entry.amount ?? 0) || 0),
          0
        );
        results.push({
          slot,
          items: normalized,
          representative,
          totalQty,
          iconKey: representative?.iconKey ?? representative?.icon_key ?? null,
        });
      });
      return results;
    };

    const slotEntries = buildSlotEntries();
    if (!slotEntries.length) {
      return;
    }

    const containerEntries = slotEntries
      .filter((entry) => entry.slot && entry.slot.toLowerCase().includes('container'))
      .sort((a, b) => a.slot.localeCompare(b.slot));
    const equipmentEntries = slotEntries
      .filter((entry) => !entry.slot || !entry.slot.toLowerCase().includes('container'))
      .sort((a, b) => a.slot.localeCompare(b.slot))
      .slice(0, 4);

    const { cellWidth = 220, cellHeight = 220 } = this.gridConfig || {};
    const containerX = -cellWidth / 2 + 26;
    const equipmentX = cellWidth / 2 - 26;
    const entrySpacing = 35;
    const containerStartY = -cellHeight / 2 + 35;
    const equipmentStartY = -cellHeight / 2 + 35;

    const createIconDisplay = (entry, x, y) => {
      const group = [];
      const iconKey = entry.iconKey;
      let iconObject = null;
      if (iconKey && this.textures && this.textures.exists(iconKey)) {
        iconObject = this.add.image(x, y, iconKey);
        iconObject.setOrigin(0.5);
        iconObject.setScale(0.30);
      } else {
        iconObject = this.add.circle(x, y, 16, 0x4b5563, 0.85);
      }
      record.container.add(iconObject);
      group.push(iconObject);

      const badgeText = entry.totalQty > 1 ? `${entry.totalQty}` : '';
      if (badgeText) {
        const countText = this.add.text(x + 20, y + 20, badgeText, {
          fontSize: '14px',
          color: '#F9FA00',
          fontFamily: 'Arial, sans-serif',
          //no background color
          //backgroundColor: '#000000',
          //padding: { x: 4, y: 2 },
          stroke: '#000000',
          strokeThickness: 2,
        }).setOrigin(1, 1);
        record.container.add(countText);
        group.push(countText);
      }

      group.forEach((obj) => record.inventoryIcons.push(obj));
    };

    // Check if player has arrows - if so, show arrows instead of poison potions
    const hasArrows = containerEntries.some(entry => entry.slot === 'arrow_container');
    const hasPoison = containerEntries.some(entry => entry.slot === 'poison_container');
    
    // Build container order: prioritize arrows over poison if arrows are present
    const containerOrder = ['battle_container', 'healing_container', 'magic_container'];
    if (hasArrows) {
      containerOrder.push('arrow_container');
    }
    if (hasPoison && !hasArrows) {
      // Only show poison if no arrows are present
      containerOrder.push('poison_container');
    }
    
    let containerIndex = 0;
    containerOrder.forEach((slotKey) => {
      const entry = containerEntries.find((candidate) => candidate.slot === slotKey);
      if (!entry) return;
      const y = containerStartY + containerIndex * entrySpacing;
      containerIndex += 1;
      createIconDisplay(entry, containerX, y);
    });

    equipmentEntries.forEach((entry, index) => {
      const y = equipmentStartY + index * entrySpacing;
      createIconDisplay(entry, equipmentX, y);
    });
  }

  /**
   * Create leaderboard content for the reusable right column container
   */
  createLeaderboardContent(contentWidth, topHeight) {
    // Clear any existing content
    if (this.leftContentContainer) {
      this.leftContentContainer.removeAll(true);
    }
    
    // Add 10px margin by reducing the available content area
    const margin = 10;
    const availableWidth = contentWidth - (margin * 2);
    const availableHeight = topHeight - (margin * 2);
    
    // Leaderboard title
    const leaderboardTitle = this.add.text(0, -availableHeight / 2 + 30, 'Leaderboard', {
      fontSize: '24px',
      color: '#F3F4F6',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    this.leftContentContainer.add(leaderboardTitle);
    
    // Leaderboard content
    const leaderboardContent = this.add.text(0, 0, 'Top Players\n\nRotating Stats\n& Rankings', {
      fontSize: '18px',
      color: '#9CA3AF',
      fontFamily: 'Arial, sans-serif',
      align: 'center',
      wordWrap: { width: availableWidth - 20 }
    }).setOrigin(0.5);
    this.leftContentContainer.add(leaderboardContent);
  }

  /**
   * Create instructions content for the reusable right column container
   */
  createInstructionsContent(contentWidth, topHeight) {
    // Clear any existing content
    if (this.leftContentContainer) {
      this.leftContentContainer.removeAll(true);
    }
    
    // Add 10px margin by reducing the available content area
    const margin = 10;
    const availableWidth = contentWidth - (margin * 2);
    const availableHeight = topHeight - (margin * 2);
    
    // Instructions title
    const instructionsTitle = this.add.text(0, -availableHeight / 2 + 30, 'Instructions', {
      fontSize: '24px',
      color: '#F3F4F6',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    this.leftContentContainer.add(instructionsTitle);
    
    // Instructions content
    const instructionsContent = this.add.text(0, 0, 
      'How to Play:\n\n' +
      '1. Type "Join" to enter battle\n' +
      '2. AI players will join when timer ends\n' +
      '3. Battle starts when AI players have joined\n\n' +
      'Player numbers shown on cards.', 
      { 
        fontSize: '18px', 
        color: '#D1D5DB', 
        fontFamily: 'Arial, sans-serif',
        align: 'center', 
        wordWrap: { width: availableWidth - 20 } 
      }
    ).setOrigin(0.5);
    /*
  const instructionsContent = this.add.text(0, 0, 
      'How to Play:\n\n' +
      '1. Type "Join" to enter battle\n' +
      '2. Type "bet to enable betting\n' +
      '3. Type "bet [#] [amount]" to predict the winner\n' +
      '   Example: "bet 3 5000"\n' +
      '4. Battle starts when timer ends\n\n' +
      'Player numbers shown on cards.', 
      { 
        fontSize: '18px', 
        color: '#D1D5DB', 
        fontFamily: 'Arial, sans-serif',
        align: 'center', 
        wordWrap: { width: availableWidth - 20 } 
      }
    ).setOrigin(0.5);
    */
    
    this.leftContentContainer.add(instructionsContent);
  }

  /**
   * Switch the right column content between different types
   * @param {string} contentType - 'leaderboard' or 'instructions'
   */
  switchRightContent(contentType) {
    const contentWidth = this.sideContentWidth || (this.sideWidth ? this.sideWidth - this.padding * 2 : this.cameras.main.width * 0.2);
    const topHeight = this.rotatingContentHeight || this.sideTopHeight || (this.cameras.main.height - this.padding * 2) * 0.45;
    
    switch (contentType) {
      case 'leaderboard':
        this.createLeaderboardContent(contentWidth, topHeight);
        break;
      case 'instructions':
        this.createInstructionsContent(contentWidth, topHeight);
        break;
      default:
        console.warn('Unknown content type:', contentType);
    }
  }

  /**
   * Start automatic content switching every 10 seconds
   */
  startContentSwitching() {
    // Initialize content types array
    this.contentTypes = ['leaderboard', 'instructions'];
    this.currentContentIndex = 1;
    
    // Start with leaderboard content
    this.switchRightContent(this.contentTypes[this.currentContentIndex]);
    
    // Set up the switching timer (10 seconds = 10000ms)
    this.contentSwitchTimer = this.time.addEvent({
      delay: 10000, // 10 seconds
      callback: this.switchToNextContent,
      callbackScope: this,
      loop: true
    });
    
    //console.log('[JoinScene] Content switching started - switching every 10 seconds');
  }

  /**
   * Switch to the next content type in the rotation
   */
  switchToNextContent() {
    if (!this.contentTypes || this.contentTypes.length === 0) {
      return;
    }
    
    // Move to next content type
    this.currentContentIndex = (this.currentContentIndex + 1) % this.contentTypes.length;
    const nextContentType = this.contentTypes[this.currentContentIndex];
    
    //console.log(`[JoinScene] Switching to ${nextContentType} content`);
    this.switchRightContent(nextContentType);
  }

  /**
   * Stop the content switching timer
   */
  stopContentSwitching() {
    if (this.contentSwitchTimer) {
      this.contentSwitchTimer.destroy();
      this.contentSwitchTimer = null;
      //console.log('[JoinScene] Content switching stopped');
    }
  }

  /**
   * Update vote percentages on player cards
   */
  async updateVotePercentages(standingsOverride = null) {
    // Only update if voting countdown is active
    if (!this.votingCountdownActive) {
      return;
    }
    
    try {
      const standings = standingsOverride || await VoteManager.getStandings();
      if (!standings || !standings.options) return;
      
      // Create lookup map by option key
      const voteMap = {};
      standings.options.forEach(opt => {
        voteMap[opt.optionKey] = opt.percentage;
      });
      
      // Update each player card
      this.playerDisplayObjects.forEach((obj, index) => {
        if (obj && obj.voteText && obj.player) {
          const optionKey = `player_${obj.player.playerNumber}`;
          let percentage = voteMap[optionKey] || 0;
          //Show no decimal places
          percentage = Math.floor(percentage);
          obj.voteText.setText(`${percentage}%`);
        }
      });
    } catch (error) {
      console.error('[JoinScene] Error updating vote percentages:', error);
    }
  }

  // Handle fullscreen resize
  resize() {
    const { width, height } = this.cameras.main;
    
    // Calculate scale factors
    const scaleX = width / this.originalWidth;
    const scaleY = height / this.originalHeight;
    const scale = Math.min(scaleX, scaleY); // Use minimum to maintain aspect ratio
    
    // Update background
    if (this.background) {
      this.background.setSize(width, height);
      this.background.setPosition(width / 2, height / 2);
    }
    
    // Update background image if it exists
    if (this.backgroundImage) {
      this.backgroundImage.setPosition(width / 2, height / 2);
      const imageScaleX = width / this.backgroundImage.width;
      const imageScaleY = height / this.backgroundImage.height;
      const imageScale = Math.max(imageScaleX, imageScaleY);
      this.backgroundImage.setScale(imageScale);
    }
    
    // Update overlay
    if (this.overlay) {
      this.overlay.setSize(width, height);
      this.overlay.setPosition(width / 2, height / 2);
    }
    
    const padding = this.padding || 20;
    const sideWidth = width * 0.18 + (this.sideWidthExtra || 0);
    const mainWidth = width - sideWidth;
    const mainContentWidth = mainWidth - padding * 2;
    const sideOffset = this.sideOffset || 0;
    const centerOffset = this.centerOffset || sideOffset;
    this.sideWidth = sideWidth;
    this.centerWidth = mainWidth;

    if (this.sideContainer) {
      this.sideContainer.setPosition(padding + sideWidth / 2 - sideOffset, height / 2);
    }

    if (this.centerContainer) {
      this.centerContainer.setPosition(sideWidth + padding + mainContentWidth / 2 - centerOffset, height / 2);
    }
    
    // Update modal if it exists
    if (this.modalContainer) {
      this.modalContainer.setPosition(width / 2, height / 2);
    }
    if (this.modalOverlay) {
      this.modalOverlay.setSize(width, height);
      this.modalOverlay.setPosition(width / 2, height / 2);
    }
    
    // Scale UI elements proportionally
    if (this.centerContainer) {
      this.centerContainer.setScale(scale);
    }
    if (this.sideContainer) {
      this.sideContainer.setScale(scale);
    }
    if (this.modalContainer) {
      this.modalContainer.setScale(scale);
    }
  }

  update(time, delta) {
    // Update countdown timer
    if (!this.isPaused && this.countdownSeconds > 0) {
      // Decrement countdown
      if (!this.lastUpdateTime) {
        this.lastUpdateTime = time;
      }
      
      const elapsed = time - this.lastUpdateTime;
      if (elapsed >= 1000) {
        this.countdownSeconds--;
        this.lastUpdateTime = time;
        
        // Update timer display
        if (this.timerText && this.timerText.active) {
          this.timerText.setText(this.formatTime(this.countdownSeconds));
          
          // Change color based on time remaining
          if (this.countdownSeconds <= 10) {
            this.timerText.setColor('#EF4444'); // Red
          } else if (this.countdownSeconds <= 30) {
            this.timerText.setColor('#F59E0B'); // Orange
          }
        }
        
        // Auto-start when countdown reaches 0
        if (this.countdownSeconds === 0) {
          console.log('[JoinScene] Starting game with:', { 
            gameMode: this.gameMode, 
            teamCount: this.teamCount,
            maxHeroes: this.maxHeroes 
          });
          this.startBattle();
        }
      }
    }
  }

  formatTime(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  addPlayer(playerData) {
    // Check if voting is active and joins are locked
    if (this.playersLockedForVoting) {
      console.log('Cannot join - voting is active');
      try {
        showMarqueeMessage('⚠️ Cannot join - voting in progress', 
          { color: '#EF4444' }, 'medium');
      } catch (error) {
        console.warn('Failed to show marquee message:', error);
      }
      return false;
    }

    //console.log('[JoinScene] Adding player:', playerData);
    // Create joinedPlayer object with enriched data
    const joinedPlayer = {
      id: `player_${Date.now()}_${Math.random()}`,
      name: playerData.username || playerData.name || 'Anonymous',
      platform: (playerData.platform || 'local')?.toLowerCase?.() || 'local',
      wallet: playerData.walletAddress || null,
      userId: playerData.userId || null,
      isAI: false,
      isJoined: true,
      isAutoJoin: !!playerData.isAutoJoin,
      teamId: null, // Will be assigned later
      hero: {
        id: playerData.heroId || null,
        name: playerData.heroName || 'Hero', // Always use heroName, never fall back to username/wallet
        image_url: playerData.sprite || null,
        level: playerData.heroLevel || 1,
        stats: {
          health: playerData.health || 100,
          attack: playerData.attack || 50,
          defense: playerData.defense || 30,
          speed: playerData.speed || 40,
          quickness: playerData.quickness || 10,
          coordination: playerData.coordination || 10,
          strength: playerData.strength || 10,
          endurance: playerData.endurance || 10,
          focus: playerData.focus || 10,
          self: playerData.self || 10,
          mana: playerData.mana || 100,
          stamina: playerData.stamina || 100
        },
        inventory: playerData.inventory || []
      },
      spawnPosition: null // Will be set during spawning
    };
    
    console.log('[JoinScene] Adding player:', joinedPlayer);
    
    // Check if player already joined (by wallet or userId or name)
    const isDuplicate = this.players.some(p => {
      if (joinedPlayer.userId && p.userId) {
        return p.userId === joinedPlayer.userId;
      }
      if (!joinedPlayer.userId && joinedPlayer.wallet && p.wallet) {
        return p.wallet === joinedPlayer.wallet;
      }
      if (!joinedPlayer.userId && !joinedPlayer.wallet) {
        return p.name === joinedPlayer.name;
      }
      return false;
    });
    
    if (isDuplicate) {
      console.log(`Player ${joinedPlayer.name} already joined`);
      return false;
    }
    
    const maxSlots = this.maxHeroes || 25;

    // Check if grid is full
    if (this.players.length >= maxSlots) {
      console.log(`[JoinScene] Player grid is full (${this.players.length}/${maxSlots})`);
      return false;
    }
    
    // Add player to the list (preserve order)
    this.players.push(joinedPlayer);
    
    // Also add to BattleStateManager
    BattleStateManager.addJoinedPlayer(joinedPlayer);
    
    this.updateCounters();
    
    // Hide join overlay when first player joins
    if (this.players.length === 1) {
      this.hideJoinOverlay();
    }
    
    // Ensure the grid can fit the new player; if resizing, queue visualization
    const desired = this.calculateGridSize(this.players.length);
    const needsResize = desired.rows !== this.gridConfig.rows || desired.cols !== this.gridConfig.cols;
    if (needsResize) {
      // Queue the visual creation until after resize completes
      this.pendingVisualJoins.push({ player: joinedPlayer, index: this.players.length - 1 });
      this.resizeGrid(desired);
      this.updateCounters();
    } else if (this.isResizingGrid) {
      // Already resizing; queue
      this.pendingVisualJoins.push({ player: joinedPlayer, index: this.players.length - 1 });
      this.updateCounters();
    } else {
      // Create visual representation in grid immediately
      this.createPlayerGridItem(joinedPlayer, this.players.length - 1);
      this.updateCounters();
    }

    if (joinedPlayer.userId != null) {
      const updates = {};
      if (joinedPlayer.platform) {
        updates.lastPlatform = joinedPlayer.platform;
      }
      if (joinedPlayer.wallet) {
        updates.lastWallet = joinedPlayer.wallet;
      }
      const inferredName = joinedPlayer.name || joinedPlayer.hero?.name || joinedPlayer.username;
      if (inferredName) {
        updates.lastUsername = inferredName;
      }
      if (Object.keys(updates).length > 0) {
        updates.lastJoinSource = updates.lastJoinSource || 'manual';
        updates.lastJoinAt = Date.now();
        this.updateAutoJoinRecord(joinedPlayer.userId, updates);
      }
    }

    return true;
  }

  async fetchHeroInventorySnapshot(heroId) {
    if (!heroId) return [];
    const headers = {};
    const apiKey = process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key';
    if (apiKey) {
      headers['X-API-Key'] = apiKey;
    }
    try {
      if (typeof localStorage !== 'undefined') {
        const token = localStorage.getItem('jwt_token');
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }
    } catch (_) {}

    try {
      const response = await fetch(`/api/inventory/hero/${heroId}`, { headers });
      if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error(text || `Failed to load inventory (${response.status})`);
      }
      const json = await response.json().catch(() => null);
      const inventory =
        (json && Array.isArray(json.inventory) && json.inventory) ||
        (json && json.data && Array.isArray(json.data.inventory) && json.data.inventory) ||
        [];
      return inventory;
    } catch (error) {
      console.warn('[JoinScene] Failed to fetch hero inventory snapshot', { heroId, error });
      return [];
    }
  }

  // Optional lookup helpers for command targeting
  findPlayerByHeroId(heroId) {
    if (!heroId) return null;
    return this.players.find(p => p && p.hero && p.hero.id === heroId) || null;
  }

  findPlayerByUserId(userId) {
    try { return this.players?.find(p => p && p.userId === userId) || null; } catch (_) { return null; }
  }

  findPlayerByWallet(wallet) {
    try { return this.players?.find(p => p && p.wallet === wallet) || null; } catch (_) { return null; }
  }

  // Auto-join system methods (now using user IDs instead of wallet addresses)

  getAutoJoinRecord(userId) {
    const existing = autoJoinRemaining.get(userId);
    if (existing && typeof existing === 'object') {
      const credits = Number.isFinite(existing.credits) ? Number(existing.credits) : Number(existing.credits || 0) || 0;
      const normalized = { ...existing, credits };
      autoJoinRemaining.set(userId, normalized);
      return normalized;
    }
    const credits = Number.isFinite(existing) ? Number(existing) : Number(existing || 0) || 0;
    const normalized = { credits };
    if (userId != null) {
      autoJoinRemaining.set(userId, normalized);
    }
    return normalized;
  }

  updateAutoJoinRecord(userId, updates = {}) {
    if (userId == null) {
      return null;
    }
    const base = this.getAutoJoinRecord(userId);
    const next = { ...base, ...updates };
    if (!Number.isFinite(next.credits)) {
      next.credits = Number(base.credits) || 0;
    }
    autoJoinRemaining.set(userId, next);
    return next;
  }

  markAutoJoin(userId, matches = 2) {
    try {
      const record = this.getAutoJoinRecord(userId);
      const incoming = Number(matches) || 0;
      const current = Number(record.credits) || 0;
      const next = incoming >= current ? incoming : current;
      this.updateAutoJoinRecord(userId, { credits: next, lastJoinSource: 'grant', lastJoinAt: Date.now() });
      console.log(`🎫 Auto-join credits granted: user ${userId} now has ${next} credits`);
    } catch (error) {
      console.error('Error marking auto-join:', error);
    }
  }

  hashString(s) {
    let h = 0;
    for (let i = 0; i < s.length; i++) {
      h = ((h << 5) - h) + s.charCodeAt(i);
      h |= 0;
    }
    return h;
  }

  truncateWallet(walletAddress) {
    if (!walletAddress || walletAddress.length < 8) return walletAddress || 'Unknown';
    return `${walletAddress.slice(0, 4)}...${walletAddress.slice(-4)}`;
  }

  truncateUsername(username) {
    if (!username) return 'Anonymous';
    return String(username).slice(0, 12);
  }

  /**
   * Select a hero based on battle frequency settings
   * @param {Array} heroes - Array of hero objects
   * @returns {Object} Selected hero
   */
  selectHeroByFrequency(heroes) {
    if (!heroes || heroes.length === 0) {
      throw new Error('No heroes available');
    }

    // If only one hero, return it
    if (heroes.length === 1) {
      return heroes[0];
    }

    // Create weighted selection based on frequency
    const weightedHeroes = [];

    for (const hero of heroes) {
      // Get frequency value, default to 6 (normal) if not set
      let frequency = hero.frequency !== null && hero.frequency !== undefined ? hero.frequency : 6;

      // Convert frequency to weight (higher frequency = higher weight)
      // Frequency scale: 0=never, 3=rare, 6=normal, 11=often
      let weight = 0;
      if (frequency === 0) {
        weight = 0; // Never select
      } else if (frequency === 3) {
        weight = 1; // Rare
      } else if (frequency === 6) {
        weight = 2; // Normal
      } else if (frequency === 11) {
        weight = 3; // Often
      } else {
        // Handle other values as normal
        weight = 2;
      }

      // Add hero to weighted pool based on weight
      for (let i = 0; i < weight; i++) {
        weightedHeroes.push(hero);
      }
    }

    // If no heroes have weight (all frequency 0), reject the join
    if (weightedHeroes.length === 0) {
      console.warn('[JoinScene] All heroes have frequency 0 (never), cannot join battle');
      throw new Error('No heroes available for battle - all heroes set to never join');
    }

    // Randomly select from weighted pool
    const randomIndex = Math.floor(Math.random() * weightedHeroes.length);
    const selectedHero = weightedHeroes[randomIndex];

    console.log(`[JoinScene] Selected hero "${selectedHero.name}" (frequency: ${selectedHero.frequency || 6}) from ${heroes.length} available heroes`);
    return selectedHero;
  }

  consumeAutoJoinCredits() {
    console.log('[JoinScene] Consuming auto-join credits...');
    try {
      autoJoinRemaining.forEach((entry, userId) => {
        const record = this.getAutoJoinRecord(userId);
        if (record.credits > 0) {
          const newRemaining = record.credits - 1;
          if (newRemaining <= 0) {
            autoJoinRemaining.delete(userId);
            console.log(`🎫 Auto-join credits exhausted for user ${userId}`);
          } else {
            this.updateAutoJoinRecord(userId, { credits: newRemaining });
            console.log(`🎫 Auto-join credits remaining for user ${userId}: ${newRemaining}`);
          }
        }
      });
    } catch (error) {
      console.error('Error consuming auto-join credits:', error);
    }
  }

  normalizeRosterPlayer(player, index) {
    const normalized = player;

    // Ensure a primary name is present
    if (!normalized.name && normalized.hero?.name) {
      normalized.name = normalized.hero.name;
    }

    if (!normalized.hero) {
      normalized.hero = {
        id: null,
        name: normalized.name || `Player ${index + 1}`,
        level: normalized.heroLevel || 1,
        image_url: null,
        stats: {}
      };
    } else if (!normalized.hero.name) {
      normalized.hero.name = normalized.name || `Player ${index + 1}`;
    }

    // Ensure inventory arrays exist
    if (!Array.isArray(normalized.hero.inventory) && Array.isArray(normalized.inventory)) {
      normalized.hero.inventory = normalized.inventory;
    }
    if (!Array.isArray(normalized.hero.inventory)) {
      normalized.hero.inventory = [];
    }
    normalized.inventory = normalized.hero.inventory;

    // Provide a default wallet for AI players (used for uniqueness in UI helpers)
    if (!normalized.wallet && normalized.isAI) {
      const identifier = normalized.hero?.id || normalized.id || index;
      normalized.wallet = `ai_wallet_${identifier}`;
    }

    // Provide a display username for consistency with joined players
    if (!normalized.username && normalized.name) {
      normalized.username = normalized.name;
    }

    // Default platform for display purposes
    if (!normalized.platform) {
      normalized.platform = 'local';
    }

    const heroStatHealth = Number(normalized.hero?.stats?.health) || 0;
    const providedHealth = Number(normalized.health);
    const providedMax = Number(normalized.maxHealth);
    const fallbackHealth = heroStatHealth > 0 ? heroStatHealth : (providedHealth > 0 ? providedHealth : 100);

    normalized.health = Number.isFinite(providedHealth) && providedHealth >= 0
      ? providedHealth
      : fallbackHealth;

    normalized.maxHealth = Number.isFinite(providedMax) && providedMax > 0
      ? providedMax
      : (heroStatHealth > 0 ? heroStatHealth : fallbackHealth);

    return normalized;
  }

  hideJoinOverlay(immediate = false) {
    const targets = [];
    if (this.joinOverlay && this.joinOverlay.visible) {
      targets.push(this.joinOverlay);
    }
    if (this.joinOverlayText && this.joinOverlayText.visible) {
      targets.push(this.joinOverlayText);
    }

    if (!targets.length) {
      if (this.playersPlaceholder && this.playersPlaceholder.visible) {
        this.playersPlaceholder.setVisible(false);
      }
      return;
    }

    if (this.joinOverlayTween) {
      if (!immediate) return;
      try { this.joinOverlayTween.stop(); } catch (_) {}
      this.joinOverlayTween = null;
    }

    const finalize = () => {
      targets.forEach(target => {
        try {
          target.setAlpha(0);
          target.setVisible(false);
        } catch (_) {}
      });
      if (this.playersPlaceholder && this.playersPlaceholder.visible) {
        this.playersPlaceholder.setVisible(false);
      }
    };

    if (immediate) {
      finalize();
      return;
    }

    this.joinOverlayTween = this.tweens.add({
      targets,
      alpha: 0,
      duration: 300,
      onComplete: () => {
        finalize();
        this.joinOverlayTween = null;
      }
    });
  }

  /**
   * Hide the battlefield row (used when countdown stops or is skipped)
   */
  hideBattlefieldRow() {
    if (this.battlefieldRow?.container) {
      this.battlefieldRow.container.setVisible(false);
    }
  }

  getPlayerMaxHealth(player) {
    if (!player) return 100;
    const candidates = [player.maxHealth, player.hero?.stats?.health, player.health, 100];
    for (const value of candidates) {
      const num = Number(value);
      if (Number.isFinite(num) && num > 0) {
        return num;
      }
    }
    return 100;
  }

  getPlayerCurrentHealth(player, fallbackMax = 100) {
    if (!player) return fallbackMax;
    const current = Number(player.health);
    if (Number.isFinite(current) && current >= 0) {
      return current;
    }
    return fallbackMax;
  }

  calculateHealthPercent(player) {
    const maxHealth = this.getPlayerMaxHealth(player);
    const currentHealth = this.getPlayerCurrentHealth(player, maxHealth);
    if (maxHealth <= 0) return 0;
    return Phaser.Math.Clamp(currentHealth / maxHealth, 0, 1);
  }

  getHealthBarColor(healthPercent) {
    if (healthPercent <= 0.33) {
      return 0xff3333;
    }
    if (healthPercent <= 0.66) {
      return 0xffff33;
    }
    return 0x33ff33;
  }

  computeHealthBarMetrics(cellWidth, cellHeight) {
    const nameY = cellHeight / 2 - 20;
    const width = Math.max(60, cellWidth - 40);
    const height = Math.max(6, Math.min(12, cellHeight * 0.08));
    let healthY = nameY - Math.max(12, height + 6);
    const minY = -cellHeight / 2 + 25;
    if (healthY < minY) {
      healthY = minY;
    }
    return {
      width,
      height,
      y: healthY,
      nameY
    };
  }

  updateGridHealthBar(record) {
    if (!record || !record.healthBar) return;
    const percent = this.calculateHealthPercent(record.player);
    record.healthBar.setScale(percent, 1);
    record.healthBar.setFillStyle(this.getHealthBarColor(percent), 1);
  }

  updateGridHealthBarLayout(record, cellWidth, cellHeight) {
    if (!record) return;
    const metrics = this.computeHealthBarMetrics(cellWidth, cellHeight);
    record.healthBarWidth = metrics.width;
    record.healthBarHeight = metrics.height;
    record.healthBarY = metrics.y;
    if (record.healthBarBg) {
      record.healthBarBg.setPosition(0, metrics.y);
      if (typeof record.healthBarBg.setSize === 'function') {
        record.healthBarBg.setSize(metrics.width, metrics.height);
      }
      if (typeof record.healthBarBg.setDisplaySize === 'function') {
        record.healthBarBg.setDisplaySize(metrics.width, metrics.height);
      } else {
        record.healthBarBg.width = metrics.width;
        record.healthBarBg.height = metrics.height;
      }
    }
    if (record.healthBar) {
      record.healthBar.setPosition(-metrics.width / 2, metrics.y);
      if (typeof record.healthBar.setSize === 'function') {
        record.healthBar.setSize(metrics.width, metrics.height);
      }
      if (typeof record.healthBar.setDisplaySize === 'function') {
        record.healthBar.setDisplaySize(metrics.width, metrics.height);
      }
    }
    if (record.nameText) {
      record.nameText.setPosition(0, metrics.nameY);
    }
  }

  async prepareBattleRoster() {
    if (this.battleRosterReady && this.battleRosterPlayers.length > 0) {
      return this.battleRosterPlayers;
    }

    if (this.rosterPreparing) {
      return this.rosterPreparing;
    }

    // Lock joins immediately to prevent state changes while building roster
    this.playersLockedForVoting = true;

    const config = BattleStateManager.getBattleConfig();
    const joinedPlayers = BattleStateManager.getJoinedPlayers();

    this.rosterPreparing = (async () => {
      try {
        const teamResult = await TeamBuilder.buildTeams(
          joinedPlayers,
          config.maxPlayers,
          config.gameMode,
          config.teamCount
        );

        this.teamBuildResult = teamResult;

        const normalizedPlayers = teamResult.players.map((player, index) =>
          this.normalizeRosterPlayer(player, index)
        );

        this.battleRosterPlayers = normalizedPlayers;
        this.players = [...normalizedPlayers];

        this.hideJoinOverlay();

        // Persist roster details for downstream scenes
        const aiPlayers = teamResult.players.filter(p => p.isAI);
        //console.log('[JoinScene] aiPlayers:', aiPlayers);
        BattleStateManager.setAIPlayers(aiPlayers);
        BattleStateManager.setFinalPlayerList(teamResult.players);

        const requiredTextures = normalizedPlayers
          .filter(p => p.hero && p.hero.image_url)
          .map(p => `player_${p.hero.id || p.wallet || p.id}`);
        if (requiredTextures.length > 0) {
          BattleStateManager.addRequiredTextures(requiredTextures);
        }

        // Refresh grid to show the full roster
        this.pendingVisualJoins.length = 0;
        this.isResizingGrid = false;
        this.rebuildGridFromPlayers();
        this.updateCounters();

        this.battleRosterReady = true;

        try {
          //showMarqueeMessage('👥 Roster locked. Voting open soon!', { color: '#10B981' }, 'medium');
        } catch (_) {}

        return this.battleRosterPlayers;
      } catch (error) {
        console.error('[JoinScene] Failed to prepare battle roster:', error);
        this.playersLockedForVoting = false;
        throw error;
      } finally {
        this.rosterPreparing = null;
      }
    })();

    return this.rosterPreparing;
  }

  createPlayerGridItem(player, index) {
    const { rows, cols, cellWidth, cellHeight, startX, startY } = this.gridConfig;
    
    // Calculate grid position (fill left to right, top to bottom)
    const row = Math.floor(index / cols);
    const col = index % cols;
    
    if (row >= rows) return; // Grid is full
    
    const x = startX + col * cellWidth + cellWidth / 2;
    const y = startY + row * cellHeight + cellHeight / 2;
    
    // Create container for this player
    const playerContainer = this.add.container(x, y);
    this.playersGridContainer.add(playerContainer);
    
    // Background cell
    //if the hero is AI, make the cell bgStrokeStyle slightly blue
    let cellBg = null;
    cellBg = this.add.rectangle(0, 0, cellWidth - 10, cellHeight - 10, 0x374151);
    cellBg.setStrokeStyle(1, player?.isAI ? 0x0000FF : 0x37FF51);
    cellBg.setAlpha(0.8);
    playerContainer.add(cellBg);

    // Player sprite (if available) - load dynamically if needed
    let playerSprite = null;
    const placeholder = this.add.circle(0, -10, Math.min(cellWidth, cellHeight) / 4, 0x6B7280);
    playerContainer.add(placeholder);

    // CHANGE 1: Platform icon moved to BOTTOM RIGHT
    let platformIcon = null;
    let platformFallback = null;
    const platformIconKey = this.getPlatformIconKey ? this.getPlatformIconKey(player.platform) : null;
    const iconX = -cellWidth / 2 + 25;
    const iconY = cellHeight / 2 - 20;

    if (player?.isAI) {
      platformFallback = this.add.text(iconX - 5, iconY + 5, 'AI', {
        fontSize: '18px',
        color: '#9CA3FF',
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold'
      }).setOrigin(0.5);
      playerContainer.add(platformFallback);
    } else if (platformIconKey && this.textures.exists(platformIconKey)) {
      platformIcon = this.add.image(iconX, iconY, platformIconKey);
      platformIcon.setScale(0.6);
      platformIcon.setOrigin(0.5);
      playerContainer.add(platformIcon);
    } else if (player.platform) {
      platformFallback = this.add.text(iconX, iconY, player.platform.slice(0, 3).toUpperCase(), {
        fontSize: '16px',
        color: '#9CA3AF',
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold'
      }).setOrigin(0.5);
      playerContainer.add(platformFallback);
    }
    
    // CHANGE 3: Vote percentage in LOWER RIGHT
    const voteText = this.add.text(cellWidth / 2 - 25, cellHeight / 2 - 18, '0%', {
      fontSize: '24px',
      color: '#10B981',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold',
      align: 'right',
      //backgroundColor: '#000000',
      //padding: { x: 6, y: 4 }
    }).setOrigin(1, 0.5);
    voteText.setVisible(false); // Initially hidden until voting starts
    playerContainer.add(voteText);
    
    // Prepare a record now so async loaders can attach sprite reference later
    const record = { 
      player: player, 
      container: playerContainer, 
      nameText: null, 
      creditsText: null, 
      sprite: null, 
      cellBg: null, 
      levelText: null,
      voteText, // NEW: store vote text for updates
      platformIcon,
      platformFallback,
      inventoryIcons: []
    };
    
    if (player.hero && player.hero.image_url) {
      // Generate a unique texture key from the URL
      const textureKey = `player_${player.hero.id || player.wallet || index}`;
      
      if (this.textures.exists(textureKey)) {
        // Texture already loaded
        playerSprite = this.add.image(0, 0, textureKey);
        const scale = Math.min((cellWidth - 30) / playerSprite.width, (cellHeight - 60) / playerSprite.height);
        playerSprite.setScale(scale);
        playerContainer.add(playerSprite);
        placeholder.setVisible(false);
        record.sprite = playerSprite;
      } else {
        // Load the texture dynamically
        //console.log(`[JoinScene] Loading player sprite: ${player.hero.image_url}`);
        this.load.image(textureKey, player.hero.image_url);
        // Use generic filecomplete and filter by key for robustness across Phaser versions
        const onFileComplete = (key /*, type, data */) => {
          if (key !== textureKey) return;
          try { this.load.off('filecomplete', onFileComplete); } catch (_) {}
          if (this.textures.exists(textureKey)) {
            playerSprite = this.add.image(0, 0, textureKey);
            // Re-read current grid config to use latest cell size
            const { cellWidth: cw, cellHeight: ch } = this.gridConfig || { cellWidth, cellHeight };
            let baseW = playerSprite.width;
            let baseH = playerSprite.height;
            try {
              const src = playerSprite.texture && playerSprite.texture.getSourceImage && playerSprite.texture.getSourceImage();
              if (src && src.width && src.height) { baseW = src.width; baseH = src.height; }
            } catch (_) {}
            const targetScale = Math.min((cw - 30) / baseW, (ch - 60) / baseH);
            playerSprite.setScale(Math.max(0.01, Math.min(1, targetScale))); // set immediately; sequential pass will enforce if needed
            playerContainer.add(playerSprite);
            placeholder.setVisible(false);
            record.sprite = playerSprite;
          }
        };
        this.load.on('filecomplete', onFileComplete);
        this.load.start();
      }
    }
    
    // Player name (or hero name if available)
    const displayName = player.hero.name || player.name;
    const nameText = this.add.text(0, cellHeight / 2 - 20, displayName, {
      fontSize: '17px',
      color: '#F3F4F6',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold',
      align: 'center',
      wordWrap: { width: cellWidth - 20 }
    }).setOrigin(0.5);
    playerContainer.add(nameText);
    
    // Level badge if hero exists
    let levelText = null;
    if (player.hero && player.hero.level) {
      levelText = this.add.text(0, -cellHeight / 2 + 18, `Lvl ${player.hero.level}`, {
        fontSize: '16px',
        color: '#F3F300',
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold',
        align: 'center',
        backgroundColor: '#000000',
        padding: { x: 4, y: 2 },
        //render it on top of the player sprite
        depth: 10,
      }).setOrigin(0.5);
      playerContainer.add(levelText);
    }
    
    // Complete record and store reference
    record.nameText = nameText;
    record.cellBg = cellBg;
    record.levelText = levelText;
    if (playerSprite) record.sprite = playerSprite;
    this.playerDisplayObjects.push(record);
    /*if (this.shopManager) {
      this.shopManager.registerPlayerRecord(record);
    }*/
    this.refreshPlayerInventoryDisplay(player, record);
    // If sprite was not yet loaded, update stored record later when it becomes available
    if (!record.sprite && player.hero && player.hero.image_url) {
      const textureKey = `player_${player.hero.id || player.wallet || index}`;
      const onFileCompleteAttach = (key) => {
        if (key !== textureKey) return;
        try { this.load.off('filecomplete', onFileCompleteAttach); } catch (_) {}
        // Find the same record by container reference and attach sprite instance
        const rec = this.playerDisplayObjects.find(r => r && r.container === playerContainer);
        if (rec && this.textures.exists(textureKey)) {
          // If not already added earlier in per-filecomplete above, add now
          if (!rec.sprite) {
            const sp = this.add.image(0, -10, textureKey);
            rec.container.add(sp);
            rec.sprite = sp;
          }
        }
      };
      this.load.on('filecomplete', onFileCompleteAttach);
    }
    
    // Fade in animation
    playerContainer.setAlpha(0);
    this.tweens.add({
      targets: playerContainer,
      alpha: 1,
      duration: 300,
      ease: 'Power2'
    });
  }

  // Determine ideal grid size based on player count
  calculateGridSize(playerCount) {
    const targetPlayers = Math.max(playerCount || 0, this.maxHeroes || 0, 1);
    let cols = Math.ceil(Math.sqrt(targetPlayers));
    cols = Math.max(cols, 1);
    const rows = Math.ceil(targetPlayers / cols);
    return { rows: Math.max(rows, 1), cols };
  }

  // Apply a grid configuration and compute derived dimensions
  applyGridConfig({ rows, cols }) {
    const gridWidth = this.centerContentWidth - (this.gridPadding || 20) * 2;
    const gridHeight = this.playersAreaHeight - 80;
    this.gridConfig = {
      rows,
      cols,
      cellWidth: gridWidth / cols,
      cellHeight: gridHeight / rows,
      startX: -gridWidth / 2,
      startY: 0
    };
    if (this.shopManager) {
      this.shopManager.setGridConfig(this.gridConfig);
    }
  }

  // Resize the grid with tweened animations and then process queued joins
  resizeGrid(newSize) {
    if (this.isResizingGrid) return; // already in progress
    this.isResizingGrid = true;
    this.updateCounters();
    const prevConfig = this.gridConfig;
    this.applyGridConfig(newSize);

    const { cols, rows, cellWidth, cellHeight, startX, startY } = this.gridConfig;

    let pendingTweens = 0;
    const originalsCount = this.playerDisplayObjects.length;
    const onTweenComplete = () => {
      console.log('onTweenComplete', pendingTweens);
      pendingTweens -= 1;
      if (pendingTweens === 1) {
        console.log('onTweenComplete last one');
        // Done resizing
        this.isResizingGrid = false;
        this.updateCounters();
        // Compute a single target scale for all sprites based on new grid cell size
        let baseW = 0, baseH = 0;
        const sample = this.playerDisplayObjects.find(o => o && o.sprite && o.sprite.texture);
        if (sample && sample.sprite && sample.sprite.texture) {
          try {
            const src = sample.sprite.texture.getSourceImage && sample.sprite.texture.getSourceImage();
            if (src && src.width && src.height) { baseW = src.width; baseH = src.height; }
          } catch (_) {}
        }
        if (!baseW || !baseH) { baseW = 512; baseH = 512; }
        const forcedScale = Math.min((cellWidth - 30) / baseW, (cellHeight - 60) / baseH);

        // Resize existing sprites sequentially (top-left → bottom-right) with forced scale
        this.sequentialResizeSprites(0, originalsCount, forcedScale);
        // Process queued joins in order
        const queued = [...this.pendingVisualJoins];
        this.pendingVisualJoins.length = 0;
        queued.forEach(({ player, index }) => this.createPlayerGridItem(player, index));
        this.updateCounters();
        // Follow-up pass: ensure all (including any newly added) are at exact target scale
        //this.sequentialResizeSprites(0, undefined, forcedScale);
      }
    };

    // Reflow existing players to new cells
    this.playerDisplayObjects.forEach((obj, index) => {
      const row = Math.floor(index / cols);
      const col = index % cols;
      const x = startX + col * cellWidth + cellWidth / 2;
      const y = startY + row * cellHeight + cellHeight / 2;

      // Update cell background size
      if (obj.cellBg) {
        const newW = Math.max(1, cellWidth - 10);
        const newH = Math.max(1, cellHeight - 10);
        if (typeof obj.cellBg.setSize === 'function') {
          obj.cellBg.setSize(newW, newH);
        } else {
          obj.cellBg.width = newW;
          obj.cellBg.height = newH;
        }
      }

      // Reposition name/credits/level texts relative to new cell size
      if (obj.nameText) obj.nameText.setPosition(0, cellHeight / 2 - 30);
      if (obj.creditsText) obj.creditsText.setPosition(0, cellHeight / 2 - 15);
      if (obj.levelText) obj.levelText.setPosition(0, -cellHeight / 2 + 18);
      
      // Update new UI elements
      if (obj.platformIcon) {
        obj.platformIcon.setPosition(-cellWidth / 2 + 25, cellHeight / 2 - 20);
      }
      if (obj.platformFallback) {
        obj.platformFallback.setPosition(-cellWidth / 2 + 25, cellHeight / 2 - 20);
      }
      if (obj.voteText) {
        obj.voteText.setPosition(cellWidth / 2 - 25, cellHeight / 2 - 18);
      }
      this.refreshPlayerInventoryDisplay(obj.player, obj);

      // Defer precise sprite scale setting to sequential pass

      // Tween container to new position and softly scale while moving
      pendingTweens += 1;
      this.tweens.add({
        targets: obj.container,
        x,
        y,
        duration: 420,
        ease: 'Power2',
        onComplete: onTweenComplete
      });
    });

    // Edge case: if there are no display objects yet, finish immediately
    if (this.playerDisplayObjects.length === 0) {
      this.isResizingGrid = false;
      this.updateCounters();
      const queued = [...this.pendingVisualJoins];
      this.pendingVisualJoins.length = 0;
      queued.forEach(({ player, index }) => this.createPlayerGridItem(player, index));
      this.updateCounters();
    }
  }

  // Public method to rebuild visuals from current players (preserving order)
  rebuildGridFromPlayers() {
    // Determine desired grid for current players
    const desired = this.calculateGridSize(this.players.length);
    // Clear current visuals
    try { this.playersGridContainer.removeAll(true); } catch (_) {}
    this.playerDisplayObjects = [];
    this.applyGridConfig(desired);
    // Create items in order
    this.players.forEach((p, idx) => this.createPlayerGridItem(p, idx));
    this.updateCounters();
  }

  updateCounters() {
    if (!this.countersText) return;
    const queued = this.pendingVisualJoins ? this.pendingVisualJoins.length : 0;
    const total = this.players ? this.players.length : 0;
    this.countersText.setText(`Players: ${total}/${this.maxHeroes}  |  Queued: ${queued} `);
  }

  updatePlayerCountDisplay() {
    // Update the main display to show current max players setting
    if (this.countersText) {
      const queued = this.pendingVisualJoins ? this.pendingVisualJoins.length : 0;
      const resizing = this.isResizingGrid ? 'Yes' : 'No';
      const total = this.players ? this.players.length : 0;
      this.countersText.setText(`Players: ${total}/${this.maxHeroes}  |  Queued: ${queued}  |  Resizing: ${resizing}`);
    }
  }

  queueGridRebuild() {
    if (!this.time) return;
    if (this.gridRebuildTimer) {
      try { this.gridRebuildTimer.remove(false); } catch (_) {}
    }
    this.gridRebuildTimer = this.time.delayedCall(150, () => {
      this.gridRebuildTimer = null;
      this.rebuildGridFromPlayers();
    });
  }

  // Sequentially set sprite scales (no tween) left-to-right, top-to-bottom
  sequentialResizeSprites(startIndex = 0, endCount, forcedScale) {
    const objs = this.playerDisplayObjects || [];
    const total = typeof endCount === 'number' ? Math.min(endCount, objs.length) : objs.length;
    const { cellWidth, cellHeight } = this.gridConfig || {};

    console.log('sequentialResizeSprites', startIndex, endCount, total, cellWidth, cellHeight);
    if (!cellWidth || !cellHeight) return;

    const setForIndex = (i) => {
      if (i >= total) return;
      const obj = objs[i];
      if (obj && obj.sprite) {
        if (typeof forcedScale === 'number' && forcedScale > 0) {
          //tween to the forced scale
          this.tweens.add({
            targets: obj.sprite,
            scaleX: forcedScale,
            scaleY: forcedScale,
            duration: 300,
            ease: 'Power2'
          });
          //obj.sprite.setScale(forcedScale);
        } else {
          let baseW = obj.sprite.width;
          let baseH = obj.sprite.height;
          try {
            const src = obj.sprite.texture && obj.sprite.texture.getSourceImage && obj.sprite.texture.getSourceImage();
            if (src && src.width && src.height) { baseW = src.width; baseH = src.height; }
          } catch (_) {}
          const targetScale = Math.min((cellWidth - 30) / baseW, (cellHeight - 60) / baseH);
          obj.sprite.setScale(targetScale);
        }
      }
      // tiny delay to keep UI responsive and guarantee proper flush order
      setTimeout(() => setForIndex(i + 1), 12);
    };

    // start shortly after layout settles
    setTimeout(() => setForIndex(startIndex), 30);
  }

  handleResize(gameSize) {
    const { width, height } = gameSize;
    
    // Update background
    if (this.background && this.background.active) {
      try {
        this.background.setPosition(width / 2, height / 2);
        this.background.setSize(width, height);
      } catch (e) {
        console.warn('Failed to resize background:', e);
      }
    }
    
    // Update background image
    if (this.backgroundImage && this.backgroundImage.active) {
      try {
        this.backgroundImage.setPosition(width / 2, height / 2);
        const scaleX = width / this.backgroundImage.width;
        const scaleY = height / this.backgroundImage.height;
        const scale = Math.max(scaleX, scaleY);
        this.backgroundImage.setScale(scale);
      } catch (e) {
        console.warn('Failed to resize background image:', e);
      }
    }
    
    // Update overlay
    if (this.overlay && this.overlay.active) {
      try {
        this.overlay.setPosition(width / 2, height / 2);
        this.overlay.setSize(width, height);
      } catch (e) {
        console.warn('Failed to resize overlay:', e);
      }
    }
    
    const padding = this.padding || 20;
    const sideWidth = width * 0.18;
    const mainWidth = width - sideWidth;
    const mainContentWidth = mainWidth - padding * 2;
    const sideOffset = this.sideOffset || 0;
    const centerOffset = this.centerOffset || sideOffset;

    if (this.sideContainer && this.sideContainer.active) {
      try {
        this.sideContainer.setPosition(padding + sideWidth / 2 - sideOffset, height / 2);
      } catch (e) {
        console.warn('Failed to reposition side container:', e);
      }
    }

    if (this.centerContainer && this.centerContainer.active) {
      try {
        this.centerContainer.setPosition(sideWidth + padding + mainContentWidth / 2 - centerOffset, height / 2);
      } catch (e) {
        console.warn('Failed to reposition center container:', e);
      }
    }
  }

  clonePrizePoolConfig(config) {
    try {
      return JSON.parse(JSON.stringify(config || { tokens: [] }));
    } catch (_) {
      return { tokens: [] };
    }
  }

  detectChainType(address = '') {
    const value = (address || '').trim();
    if (!value) return 'solana';
    const lower = value.toLowerCase();
    if (lower.startsWith('0x') && value.length === 66) {
      return 'sui';
    }
    if (!lower.startsWith('0x') && value.length >= 32 && value.length <= 44) {
      return 'solana';
    }
    return 'solana';
  }

  getAvailableTokensFromLedger() {
    const balances = this.prizePoolBalances;
    if (!balances || typeof balances !== 'object') return [];
    
    const tokens = [];
    const tokensRegistry = this.tokensRegistry || [];
    
    Object.entries(balances).forEach(([chain, tokenMap]) => {
      if (!tokenMap || typeof tokenMap !== 'object') return;
      Object.entries(tokenMap).forEach(([key, amount]) => {
        const parsed = Number(amount);
        if (Number.isFinite(parsed) && parsed > 0) {
          const normalizedChain = chain.toLowerCase();
          
          // First try to find by token_address (in case key is an address)
          let registryEntry = tokensRegistry.find(t => 
            t.chain === normalizedChain && 
            t.token_address && 
            t.token_address.toLowerCase() === key.toLowerCase()
          );
          
          // If not found by address, try by symbol (in case key is already a symbol)
          if (!registryEntry) {
            registryEntry = tokensRegistry.find(t => 
              t.chain === normalizedChain && 
              t.token_symbol && 
              t.token_symbol.toUpperCase() === key.toUpperCase()
            );
          }
          
          // Use the proper token symbol from registry, or fall back to key
          const displaySymbol = registryEntry?.token_symbol || (key.length > 20 ? `${key.slice(0, 8)}...${key.slice(-4)}` : key);
          const canonicalSymbol = registryEntry?.token_symbol?.toUpperCase() || key.toUpperCase();
          const tokenAddress = registryEntry?.token_address || (key.length > 20 ? key : null);
          
          tokens.push({
            chain: normalizedChain,
            symbol: canonicalSymbol,
            amount: parsed,
            tokenAddress: tokenAddress,
            display: `$${displaySymbol} (${parsed.toLocaleString(undefined, { maximumFractionDigits: 4 })}) ${chain.toUpperCase()}`
          });
        }
      });
    });
    return tokens.sort((a, b) => {
      if (a.chain !== b.chain) return a.chain.localeCompare(b.chain);
      return a.symbol.localeCompare(b.symbol);
    });
  }

  async loadTokensRegistry() {
    if (this.tokensRegistry) return;
    try {
      const apiKey = process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key';
      const response = await fetch('/api/ledger/tokens', {
        headers: { 'X-API-Key': apiKey }
      });
      if (response.ok) {
        const json = await response.json().catch(() => ({ data: [] }));
        this.tokensRegistry = json?.data || [];
      } else {
        this.tokensRegistry = [];
      }
    } catch (error) {
      console.warn('[JoinScene] Failed to load tokens registry', error);
      this.tokensRegistry = [];
    }
  }

  sanitizePrizePoolConfig(config) {
    const tokens = Array.isArray(config?.tokens) ? config.tokens : [];
    const sanitizedTokens = tokens
      .map((entry) => {
        const chain = typeof entry?.chain === 'string' ? entry.chain.trim().toLowerCase() : 'solana';
        const tokenSymbol = typeof entry?.tokenSymbol === 'string' ? entry.tokenSymbol.trim().toUpperCase() : '';
        if (!tokenSymbol) return null;

        const detectedChain = this.detectChainType(entry?.tokenAddress || entry?.tokenSymbol || '');

        const safeNumber = (value, allowZero = true) => {
          if (value === null || value === undefined || value === '') return allowZero ? 0 : undefined;
          const num = Number(value);
          if (!Number.isFinite(num) || Number.isNaN(num)) return allowZero ? 0 : undefined;
          return Math.max(0, num);
        };

        const sanitized = {
          chain: chain === 'sui' ? 'sui' : (detectedChain === 'sui' ? 'sui' : 'solana'),
          tokenSymbol,
        };

        if (entry?.tokenAddress) sanitized.tokenAddress = String(entry.tokenAddress);

        const groundAmount = safeNumber(entry?.groundAmount);
        if (groundAmount) sanitized.groundAmount = groundAmount;

        const chestAmount = safeNumber(entry?.chestAmount);
        if (chestAmount) sanitized.chestAmount = chestAmount;

        const chestCount = safeNumber(entry?.chestCount, false);
        if (typeof chestCount === 'number' && chestCount >= 0) sanitized.chestCount = chestCount;

        if (Array.isArray(entry?.denominations)) {
          const cleaned = entry.denominations
            .map((d) => Number(d))
            .filter((d) => Number.isFinite(d) && d > 0);
          if (cleaned.length) sanitized.denominations = cleaned;
        }

        if (entry?.metadata && typeof entry.metadata === 'object') {
          sanitized.metadata = entry.metadata;
        }

        return sanitized;
      })
      .filter(Boolean);

    const notes = typeof config?.notes === 'string' && config.notes.trim().length ? config.notes.trim() : undefined;

    const payload = { tokens: sanitizedTokens };
    if (notes) payload.notes = notes;
    return payload;
  }

  formatPrizePoolLedgerSummary() {
    const balances = this.prizePoolBalances;
    if (!balances || typeof balances !== 'object') {
      return 'Wallet balances unavailable. Ensure you are logged in.';
    }

    const tokensRegistry = this.tokensRegistry || [];
    const lines = [];
    
    Object.entries(balances).forEach(([chain, tokenMap]) => {
      if (!tokenMap || typeof tokenMap !== 'object') return;
      const entries = Object.entries(tokenMap)
        .map(([key, amount]) => {
          const parsed = Number(amount);
          const display = Number.isFinite(parsed) ? parsed.toLocaleString(undefined, { maximumFractionDigits: 4 }) : amount;
          
          // Try to find the token in the registry by address or symbol
          let displaySymbol = key;
          const normalizedChain = chain.toLowerCase();
          
          // First try to find by token_address (in case key is an address)
          const tokenByAddress = tokensRegistry.find(t => 
            t.chain === normalizedChain && 
            t.token_address && 
            t.token_address.toLowerCase() === key.toLowerCase()
          );
          
          if (tokenByAddress && tokenByAddress.token_symbol) {
            displaySymbol = tokenByAddress.token_symbol;
          } else {
            // If not found by address, try by symbol (in case key is already a symbol)
            const tokenBySymbol = tokensRegistry.find(t => 
              t.chain === normalizedChain && 
              t.token_symbol && 
              t.token_symbol.toUpperCase() === key.toUpperCase()
            );
            
            if (tokenBySymbol && tokenBySymbol.token_symbol) {
              displaySymbol = tokenBySymbol.token_symbol;
            } else if (key.length > 20) {
              // If key looks like an address but not found in registry, truncate it
              displaySymbol = `${key.slice(0, 8)}...${key.slice(-4)}`;
            }
            // Otherwise, use the key as-is (might be a symbol not in registry)
          }
          
          return `$${displaySymbol}: ${display}`;
        });
      if (entries.length) {
        lines.push(`${chain.toUpperCase()}: ${entries.join(', ')}`);
      }
    });

    if (!lines.length) {
      return 'No ledger balances detected for Solana/Sui tokens yet.';
    }

    return `Ledger balances → ${lines.join(' | ')}`;
  }

  refreshPrizePoolUI() {
    if (!this.prizePoolUI) return;

    if (this.prizePoolUI.ledgerText) {
      this.prizePoolUI.ledgerText.setText(this.formatPrizePoolLedgerSummary());
    }

    if (this.prizePoolUI.matchOnlyToggle) {
      this.prizePoolUI.matchOnlyToggle.setText(`This match only: ${this.prizePoolThisMatchOnly ? 'ON' : 'OFF'}`);
    }

    if (this.prizePoolUI.tokensList) {
      this.prizePoolUI.tokensList.removeAll(true);
      const tokens = Array.isArray(this.prizePoolConfig?.tokens) ? this.prizePoolConfig.tokens : [];
      const sectionWidth = this.prizePoolUI.sectionWidth || 360;

      if (!tokens.length) {
        const emptyText = this.add.text(0, 0, 'No tokens configured yet. Use “Add Token” to seed rewards.', {
          fontSize: '14px',
          color: '#9CA3AF',
          fontFamily: 'Arial, sans-serif',
          wordWrap: { width: sectionWidth },
        }).setOrigin(0, 0);
        this.prizePoolUI.tokensList.add(emptyText);
      } else {
        const rowHeight = 70;
        tokens.forEach((token, idx) => {
          const row = this.add.container(0, idx * rowHeight);
          this.prizePoolUI.tokensList.add(row);

          const chainLabel = (token.chain || this.detectChainType(token.tokenAddress)).toUpperCase();
          const summary = `${token.tokenSymbol || 'TOKEN'} (${chainLabel})`;
          const ground = typeof token.groundAmount === 'number' ? token.groundAmount : 0;
          const chest = typeof token.chestAmount === 'number' ? token.chestAmount : 0;
          const chestCount = typeof token.chestCount === 'number' ? token.chestCount : 0;
          const denomText = Array.isArray(token.denominations) && token.denominations.length
            ? `Denoms: ${token.denominations.join(', ')}`
            : 'Default denominations';

          const label = this.add.text(0, 0, summary, {
            fontSize: '16px',
            color: '#E5E7EB',
            fontFamily: 'Arial, sans-serif',
            fontStyle: 'bold',
          }).setOrigin(0, 0);
          row.add(label);

          const detail = this.add.text(0, 24, `Ground: ${ground} | Chests: ${chest} (${chestCount})`, {
            fontSize: '14px',
            color: '#CBD5F5',
            fontFamily: 'Arial, sans-serif',
          }).setOrigin(0, 0);
          row.add(detail);

          const caDisplay = token.tokenAddress ? `${token.tokenAddress.slice(0, 6)}...${token.tokenAddress.slice(-4)}` : 'No contract address';
          const addressText = this.add.text(0, 44, `${denomText} • ${caDisplay}`, {
            fontSize: '13px',
            color: '#9CA3AF',
            fontFamily: 'Arial, sans-serif',
          }).setOrigin(0, 0);
          row.add(addressText);

          const editText = this.add.text(sectionWidth - 140, 24, 'Edit', {
            fontSize: '14px',
            color: '#60A5FA',
            fontFamily: 'Arial, sans-serif',
          }).setOrigin(0, 0.5);
          editText.setInteractive({ useHandCursor: true });
          editText.on('pointerdown', () => this.openPrizePoolTokenEditor(idx));
          editText.on('pointerover', () => editText.setColor('#93C5FD'));
          editText.on('pointerout', () => editText.setColor('#60A5FA'));
          row.add(editText);

          const removeText = this.add.text(sectionWidth - 70, 24, 'Remove', {
            fontSize: '14px',
            color: '#F87171',
            fontFamily: 'Arial, sans-serif',
          }).setOrigin(0, 0.5);
          removeText.setInteractive({ useHandCursor: true });
          removeText.on('pointerdown', () => this.removePrizePoolToken(idx));
          removeText.on('pointerover', () => removeText.setColor('#FCA5A5'));
          removeText.on('pointerout', () => removeText.setColor('#F87171'));
          row.add(removeText);
        });
      }
    }

    if (this.prizePoolUI.warningsText) {
      const visible = Array.isArray(this.prizePoolWarnings) && this.prizePoolWarnings.length > 0;
      this.prizePoolUI.warningsText.setText(visible ? `⚠️ ${this.prizePoolWarnings.join('\n⚠️ ')}` : '');
      this.prizePoolUI.warningsText.setVisible(visible);
    }
  }

  createModalButton(container, options = {}) {
    const {
      x = 0,
      y = 0,
      width = 150,
      height = 42,
      label = 'Button',
      fill = 0x2563EB,
      stroke = 0x1D4ED8,
      onClick = null,
      textStyle = {},
    } = options;

    const button = this.add.rectangle(x, y, width, height, fill, 1);
    button.setStrokeStyle(2, stroke);
    button.setInteractive({ useHandCursor: true });
    container.add(button);

    const text = this.add.text(x, y, label, {
      fontSize: '16px',
      color: '#FFFFFF',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold',
      ...textStyle,
    }).setOrigin(0.5);
    container.add(text);

    button.on('pointerdown', () => {
      if (typeof onClick === 'function') onClick();
    });

    const hoverScale = 1.04;
    button.on('pointerover', () => {
      button.setScale(hoverScale);
      text.setScale(hoverScale);
    });
    button.on('pointerout', () => {
      button.setScale(1);
      text.setScale(1);
    });

    return { button, text };
  }

  buildPrizePoolSection(container, columnWidth, columnHeight) {
    const columnMarginY = 24;
    const innerPadding = 28;
    const sectionWidth = columnWidth - innerPadding * 2;
    const sectionHeight = columnHeight - innerPadding * 2;
    this.prizePoolUI.sectionWidth = sectionWidth - 20;
    this.prizePoolUI.sectionHeight = sectionHeight;

    // Align with left column starting position (40px from top of column)
    let startY = -columnHeight / 2 + 20;

    const title = this.add.text(-sectionWidth / 2, startY, 'Prize Pool', {
      fontSize: '20px',
      color: '#F3F4F6',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold',
    }).setOrigin(0, 0);
    container.add(title);

    const subtitle = this.add.text(-sectionWidth / 2, startY + 30, 'Seed on-ground coins and chest rewards before the match starts.', {
      fontSize: '14px',
      color: '#9CA3AF',
      fontFamily: 'Arial, sans-serif',
      wordWrap: { width: sectionWidth },
    }).setOrigin(0, 0);
    container.add(subtitle);

    const refreshButton = this.createModalButton(container, {
      x: sectionWidth / 2 - 70,
      y: startY + 60,
      width: 120,
      height: 38,
      label: 'Refresh',
      onClick: () => this.refreshLedgerBalances(),
      textStyle: { fontSize: '15px' }
    });

    const ledgerText = this.add.text(-sectionWidth / 2, startY + 60, '', {
      fontSize: '14px',
      color: '#FBBF24',
      fontFamily: 'Arial, sans-serif',
      wordWrap: { width: sectionWidth - 140 },
    }).setOrigin(0, 0);
    container.add(ledgerText);

    startY += 60;

    const tokensList = this.add.container(-sectionWidth / 2, startY + 80);
    container.add(tokensList);

    const warningsText = this.add.text(-sectionWidth / 2, columnHeight / 2 - 40, '', {
      fontSize: '13px',
      color: '#F87171',
      fontFamily: 'Arial, sans-serif',
      wordWrap: { width: sectionWidth },
    }).setOrigin(0, 1);
    warningsText.setVisible(false);
    container.add(warningsText);

    const addTokenButton = this.createModalButton(container, {
      x: -sectionWidth / 2 + 90,
      y: columnHeight / 2 - 30,
      width: 180,
      height: 46,
      label: '+ Add Token',
      onClick: () => this.openPrizePoolTokenEditor(),
    });

    const matchToggle = this.add.text(sectionWidth / 2, columnHeight / 2 - 20, '', {
      fontSize: '15px',
      color: '#FCD34D',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold',
    }).setOrigin(1, 0.5);
    matchToggle.setInteractive({ useHandCursor: true });
    matchToggle.on('pointerdown', () => this.togglePrizePoolMatchScope());
    container.add(matchToggle);

    this.prizePoolUI.container = container;
    this.prizePoolUI.tokensList = tokensList;
    this.prizePoolUI.ledgerText = ledgerText;
    this.prizePoolUI.matchOnlyToggle = matchToggle;
    this.prizePoolUI.warningsText = warningsText;
    this.prizePoolUI.refreshButton = refreshButton;
    this.prizePoolUI.sectionWidth = sectionWidth - 20;

    this.refreshPrizePoolUI();
  }

  async openPrizePoolTokenEditor(index = null) {
    this.closePrizePoolTokenEditor();
    await this.loadTokensRegistry();

    const { width, height } = this.cameras.main;
    const existing = index !== null && Array.isArray(this.prizePoolConfig?.tokens) && this.prizePoolConfig.tokens[index]
      ? { ...this.prizePoolConfig.tokens[index] }
      : { tokenSymbol: '', tokenAddress: '', groundAmount: '', chestAmount: '', chestCount: '4', denominations: null };

    const overlay = this.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0.72);
    overlay.setDepth(400);
    overlay.setInteractive({ useHandCursor: false });

    const container = this.add.container(width / 2, height / 2).setDepth(401);
    const formWidth = Math.min(520, width * 0.75);
    const formHeight = Math.min(620, height * 0.85);

    const bg = this.add.rectangle(0, 0, formWidth, formHeight, 0x1F2937, 0.96);
    bg.setStrokeStyle(3, 0x4B5563);
    container.add(bg);

    const title = this.add.text(0, -formHeight / 2 + 36, index !== null ? 'Edit Prize Pool Token' : 'Add Prize Pool Token', {
      fontSize: '22px',
      color: '#F3F4F6',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    container.add(title);

    const inputs = [];
    let fieldY = -formHeight / 2 + 105;
    const fieldWidth = formWidth - 96;

    const availableTokens = this.getAvailableTokensFromLedger();
    const selectedToken = existing.tokenSymbol && existing.chain
      ? availableTokens.find(t => t.symbol === existing.tokenSymbol.toUpperCase() && t.chain === existing.chain.toLowerCase())
      : null;
    const selectedIndex = selectedToken ? availableTokens.indexOf(selectedToken) : -1;

    const tokenDropdown = this.createPrizePoolTokenDropdown(container, {
      label: 'Select Token',
      y: fieldY - 8,
      width: fieldWidth,
      options: availableTokens.length > 0 ? availableTokens : [{ chain: 'solana', symbol: 'No tokens available', display: 'No tokens available', amount: 0 }],
      selectedIndex: selectedIndex >= 0 ? selectedIndex : 0,
      disabled: availableTokens.length === 0
    });

    /*const chainText = this.add.text(-fieldWidth / 2, fieldY + 50, '', {
      fontSize: '13px',
      color: '#A5B4FC',
      fontFamily: 'Arial, sans-serif'
    }).setOrigin(0, 0);
    container.add(chainText);

    if (selectedToken) {
      chainText.setText(`Detected Chain: ${selectedToken.chain.toUpperCase()}`);
    } else if (existing.chain) {
      chainText.setText(`Chain: ${existing.chain.toUpperCase()}`);
    }*/

    const hiddenTokenSymbol = { key: 'tokenSymbol', value: selectedToken ? selectedToken.symbol : (existing.tokenSymbol || ''), type: 'hidden' };
    const hiddenTokenAddress = { key: 'tokenAddress', value: existing.tokenAddress || '', type: 'hidden' };
    const hiddenTokenChain = { key: 'tokenChain', value: selectedToken ? selectedToken.chain : (existing.chain || 'solana'), type: 'hidden' };
    inputs.push(hiddenTokenSymbol, hiddenTokenAddress, hiddenTokenChain);

    fieldY += 100;
    inputs.push(this.createPrizePoolInputField(container, {
      key: 'groundAmount',
      label: 'Total Ground Amount',
      y: fieldY,
      width: fieldWidth,
      value: existing.groundAmount != null ? String(existing.groundAmount) : '',
      placeholder: '0',
      type: 'number'
    }));

    fieldY += 85;
    inputs.push(this.createPrizePoolInputField(container, {
      key: 'chestAmount',
      label: 'Total Chest Amount',
      y: fieldY,
      width: fieldWidth,
      value: existing.chestAmount != null ? String(existing.chestAmount) : '',
      placeholder: '0',
      type: 'number'
    }));

    fieldY += 85;
    inputs.push(this.createPrizePoolInputField(container, {
      key: 'chestCount',
      label: 'Chest Count',
      y: fieldY,
      width: fieldWidth,
      value: existing.chestCount != null ? String(existing.chestCount) : '4',
      placeholder: 'Number of chests funded',
      type: 'integer'
    }));

    fieldY += 85;
    inputs.push(this.createPrizePoolInputField(container, {
      key: 'denominations',
      label: 'Ground Denominations (optional)',
      y: fieldY,
      width: fieldWidth,
      value: Array.isArray(existing.denominations) ? existing.denominations.join(',') : '',
      placeholder: 'e.g. 50,10,5,1',
      type: 'denominations'
    }));

    const errorText = this.add.text(0, formHeight / 2 - 110, '', {
      fontSize: '14px',
      color: '#FCA5A5',
      fontFamily: 'Arial, sans-serif'
    }).setOrigin(0.5);
    container.add(errorText);

    const cancelButton = this.createModalButton(container, {
      x: -formWidth / 2 + 120,
      y: formHeight / 2 - 50,
      width: 160,
      height: 44,
      label: 'Cancel',
      onClick: () => this.closePrizePoolTokenEditor()
    });

    const saveButton = this.createModalButton(container, {
      x: formWidth / 2 - 120,
      y: formHeight / 2 - 50,
      width: 200,
      height: 46,
      label: 'Save Token',
      onClick: () => this.savePrizePoolTokenFromEditor()
    });

    this.prizePoolTokenEditor = {
      overlay,
      container,
      inputs,
      //chainText,
      errorText,
      index,
      cancelButton,
      saveButton,
      tokenDropdown
    };

    if (overlay && tokenDropdown) {
      overlay.on('pointerdown', () => {
        if (tokenDropdown.isOpen && tokenDropdown.isOpen()) {
          tokenDropdown.close();
        }
      });
    }

    if (this.prizePoolKeyListener) {
      try {
        this.input.keyboard.off('keydown', this.handlePrizePoolInputKey, this);
      } catch (_) {}
    }
    this.prizePoolKeyListener = this.input.keyboard.on('keydown', this.handlePrizePoolInputKey, this);

    if (inputs.length) {
      const firstVisibleInput = inputs.find(f => f.type !== 'hidden');
      if (firstVisibleInput) {
        this.focusPrizePoolInput(firstVisibleInput);
      }
    }
    this.updatePrizePoolEditorDerivedValues();
  }

  closePrizePoolTokenEditor() {
    if (this.prizePoolTokenEditor?.tokenDropdown?.isOpen && this.prizePoolTokenEditor.tokenDropdown.isOpen()) {
      this.prizePoolTokenEditor.tokenDropdown.close();
    }
    if (this.prizePoolKeyListener) {
      try {
        this.input.keyboard.off('keydown', this.handlePrizePoolInputKey, this);
      } catch (_) {}
      this.prizePoolKeyListener = null;
    }
    if (this.prizePoolTokenEditor) {
      try { this.prizePoolTokenEditor.overlay?.destroy(); } catch (_) {}
      try { this.prizePoolTokenEditor.container?.destroy(); } catch (_) {}
    }
    this.prizePoolTokenEditor = null;
    this.activePrizePoolInput = null;
  }

  focusPrizePoolInput(field) {
    if (!field || field.type === 'hidden') return;
    if (this.activePrizePoolInput && this.activePrizePoolInput !== field && this.activePrizePoolInput.background) {
      this.activePrizePoolInput.background.setStrokeStyle(2, 0x4B5563);
    }
    this.activePrizePoolInput = field;
    if (field.background) {
      field.background.setStrokeStyle(3, 0x60A5FA);
    }
    this.updatePrizePoolInputDisplay(field);
    this.setPrizePoolEditorError('');
  }

  updatePrizePoolInputDisplay(field) {
    if (!field || field.type === 'hidden' || !field.valueText) return;
    const displayValue = field.value && field.value.length ? field.value : field.placeholder || '';
    field.valueText.setText(displayValue);
    field.valueText.setColor(field.value && field.value.length ? '#F9FAFB' : '#6B7280');
  }

  handlePrizePoolInputKey(event) {
    if (!this.prizePoolTokenEditor) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      if (this.prizePoolTokenEditor?.tokenDropdown?.isOpen && this.prizePoolTokenEditor.tokenDropdown.isOpen()) {
        this.prizePoolTokenEditor.tokenDropdown.close();
      } else {
        this.closePrizePoolTokenEditor();
      }
      return;
    }

    if (event.key === 'Tab') {
      event.preventDefault();
      this.cyclePrizePoolInput(event.shiftKey ? -1 : 1);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      this.savePrizePoolTokenFromEditor();
      return;
    }

    const field = this.activePrizePoolInput;
    if (!field || field.type === 'hidden') return;

    if (event.key === 'Backspace') {
      event.preventDefault();
      field.value = field.value ? field.value.slice(0, -1) : '';
      this.updatePrizePoolInputDisplay(field);
      this.updatePrizePoolEditorDerivedValues();
      return;
    }

    if (event.key === 'Delete') {
      event.preventDefault();
      field.value = '';
      this.updatePrizePoolInputDisplay(field);
      this.updatePrizePoolEditorDerivedValues();
      return;
    }

    if (event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
      let char = event.key;
      if (field.uppercase) char = char.toUpperCase();

      const allow = this.validatePrizePoolChar(field, char);
      if (!allow) {
        event.preventDefault();
        return;
      }

      if (field.maxLength && field.value.length >= field.maxLength) {
        event.preventDefault();
        return;
      }

      field.value += char;
      this.updatePrizePoolInputDisplay(field);
      this.updatePrizePoolEditorDerivedValues();
      event.preventDefault();
    }
  }

  validatePrizePoolChar(field, char) {
    if (!field) return false;
    const type = field.type || 'text';
    if (type === 'number') {
      if (!/[0-9.]/.test(char)) return false;
      if (char === '.' && field.value.includes('.')) return false;
      return true;
    }
    if (type === 'integer') {
      return /[0-9]/.test(char);
    }
    if (type === 'denominations') {
      return /[0-9, ]/.test(char);
    }
    return char.length === 1;
  }

  cyclePrizePoolInput(direction = 1) {
    if (!this.prizePoolTokenEditor) return;
    const inputs = this.prizePoolTokenEditor.inputs || [];
    const visibleInputs = inputs.filter(f => f.type !== 'hidden');
    if (!visibleInputs.length) return;
    const currentIndex = visibleInputs.indexOf(this.activePrizePoolInput);
    const nextIndex = currentIndex === -1
      ? 0
      : (currentIndex + direction + visibleInputs.length) % visibleInputs.length;
    this.focusPrizePoolInput(visibleInputs[nextIndex]);
  }

  updatePrizePoolEditorDerivedValues() {
    if (!this.prizePoolTokenEditor) return;
    const chainField = this.prizePoolTokenEditor.inputs.find((f) => f.key === 'tokenChain');
    const chain = chainField?.value || 'solana';
    if (this.prizePoolTokenEditor.chainText) {
      this.prizePoolTokenEditor.chainText.setText(`Chain: ${chain.toUpperCase()}`);
    }
  }

  createPrizePoolTokenDropdown(container, config) {
    const {
      label,
      y,
      width,
      options = [],
      selectedIndex = 0,
      disabled = false
    } = config;

    const labelText = this.add.text(-width / 2, y - 35, label, {
      fontSize: '14px',
      color: '#CBD5F5',
      fontFamily: 'Arial, sans-serif'
    }).setOrigin(0, 0.5);
    container.add(labelText);

    const bg = this.add.rectangle(0, y, width, 52, 0x111827, 0.95);
    bg.setStrokeStyle(2, disabled ? 0x374151 : 0x4B5563);
    bg.setInteractive({ useHandCursor: !disabled });
    container.add(bg);

    const selectedOption = options[selectedIndex] || options[0] || { display: 'No tokens available' };
    const displayText = this.add.text(-width / 2 + 12, y, disabled ? 'No tokens available' : selectedOption.display, {
      fontSize: '16px',
      color: disabled ? '#6B7280' : '#F9FAFB',
      fontFamily: 'Arial, sans-serif'
    }).setOrigin(0, 0.5);
    container.add(displayText);

    const arrowText = this.add.text(width / 2 - 12, y, '▼', {
      fontSize: '14px',
      color: disabled ? '#6B7280' : '#D1D5DB',
      fontFamily: 'Arial, sans-serif'
    }).setOrigin(1, 0.5);
    container.add(arrowText);

    let dropdownOpen = false;
    let dropdownList = null;

    const openDropdown = () => {
      if (disabled || dropdownOpen || !options.length) return;
      dropdownOpen = true;
      arrowText.setText('▲');

      const listY = y + 32;
      const maxHeight = 240;
      const itemHeight = 42;
      const visibleCount = Math.min(options.length, Math.floor(maxHeight / itemHeight));
      const listHeight = visibleCount * itemHeight;
      
      const listBg = this.add.rectangle(0, listY + listHeight / 2, width, listHeight, 0x1F2937, 0.98);
      listBg.setStrokeStyle(2, 0x4B5563);
      listBg.setDepth(402);
      container.add(listBg);

      const items = [];
      options.slice(0, visibleCount).forEach((option, idx) => {
        const itemY = listY + idx * itemHeight + itemHeight / 2;
        
        const itemBg = this.add.rectangle(0, itemY, width - 4, itemHeight - 2, idx === selectedIndex ? 0x2563EB : 0x111827);
        itemBg.setDepth(403);
        itemBg.setInteractive({ useHandCursor: true });
        container.add(itemBg);

        const itemText = this.add.text(-width / 2 + 12, itemY, option.display, {
          fontSize: '15px',
          color: '#F9FAFB',
          fontFamily: 'Arial, sans-serif'
        }).setOrigin(0, 0.5);
        itemText.setDepth(404);
        container.add(itemText);

        itemBg.on('pointerdown', () => {
          const hiddenSymbol = this.prizePoolTokenEditor?.inputs?.find(f => f.key === 'tokenSymbol');
          const hiddenAddress = this.prizePoolTokenEditor?.inputs?.find(f => f.key === 'tokenAddress');
          const hiddenChain = this.prizePoolTokenEditor?.inputs?.find(f => f.key === 'tokenChain');
          
          if (hiddenSymbol) hiddenSymbol.value = option.symbol;
          if (hiddenAddress) hiddenAddress.value = option.tokenAddress || '';
          if (hiddenChain) hiddenChain.value = option.chain;

          displayText.setText(option.display);
          if (this.prizePoolTokenEditor?.chainText) {
            this.prizePoolTokenEditor.chainText.setText(`Chain: ${option.chain.toUpperCase()}`);
          }
          closeDropdown();
        });

        itemBg.on('pointerover', () => {
          if (idx !== selectedIndex) {
            itemBg.setFillStyle(0x374151);
          }
        });
        itemBg.on('pointerout', () => {
          if (idx !== selectedIndex) {
            itemBg.setFillStyle(0x111827);
          }
        });

        items.push({ bg: itemBg, text: itemText });
      });

      dropdownList = { bg: listBg, items };
    };

    const closeDropdown = () => {
      if (!dropdownOpen) return;
      dropdownOpen = false;
      arrowText.setText('▼');
      if (dropdownList) {
        try {
          dropdownList.bg.destroy();
          dropdownList.items.forEach(item => {
            try { item.bg.destroy(); } catch (_) {}
            try { item.text.destroy(); } catch (_) {}
          });
        } catch (_) {}
        dropdownList = null;
      }
    };

    bg.on('pointerdown', () => {
      if (disabled) return;
      if (dropdownOpen) {
        closeDropdown();
      } else {
        openDropdown();
      }
    });

    // Close dropdown when clicking outside (handled by editor overlay)

    return {
      container: bg,
      displayText,
      arrowText,
      selectedIndex,
      open: openDropdown,
      close: closeDropdown,
      isOpen: () => dropdownOpen
    };
  }

  createPrizePoolInputField(container, config) {
    const {
      key,
      label,
      y,
      width,
      value = '',
      placeholder = '',
      type = 'text',
      uppercase = false,
      maxLength = 100
    } = config;

    const labelText = this.add.text(-width / 2, y - 30, label, {
      fontSize: '14px',
      color: '#CBD5F5',
      fontFamily: 'Arial, sans-serif'
    }).setOrigin(0, 0.5);
    container.add(labelText);

    const bg = this.add.rectangle(0, y, width, 46, 0x111827, 0.95);
    bg.setStrokeStyle(2, 0x4B5563);
    bg.setInteractive({ useHandCursor: true });
    container.add(bg);

    const valueText = this.add.text(-width / 2 + 12, y, '', {
      fontSize: '16px',
      color: '#F9FAFB',
      fontFamily: 'Arial, sans-serif'
    }).setOrigin(0, 0.5);
    container.add(valueText);

    const field = {
      key,
      labelText,
      background: bg,
      valueText,
      value: value || '',
      placeholder,
      type,
      uppercase,
      maxLength
    };

    bg.on('pointerdown', () => this.focusPrizePoolInput(field));
    valueText.setInteractive({ useHandCursor: true });
    valueText.on('pointerdown', () => this.focusPrizePoolInput(field));

    this.updatePrizePoolInputDisplay(field);
    return field;
  }

  savePrizePoolTokenFromEditor() {
    if (!this.prizePoolTokenEditor) return;
    const inputs = this.prizePoolTokenEditor.inputs || [];
    const getValue = (key) => {
      const field = inputs.find((f) => f.key === key);
      return field ? field.value.trim() : '';
    };

    const tokenSymbol = getValue('tokenSymbol').toUpperCase();
    const tokenAddress = getValue('tokenAddress');
    const tokenChain = getValue('tokenChain') || 'solana';
    const groundAmount = getValue('groundAmount');
    const chestAmount = getValue('chestAmount');
    const chestCount = getValue('chestCount');
    const denominationsRaw = getValue('denominations');

    if (!tokenSymbol || tokenSymbol === 'NO TOKENS AVAILABLE') {
      this.setPrizePoolEditorError('Please select a token from your ledger balance.');
      return;
    }

    const parsedGround = parseFloat(groundAmount || '0');
    const parsedChest = parseFloat(chestAmount || '0');
    const parsedChestCount = Math.max(0, parseInt(chestCount || '0', 10) || 0);

    if (Number.isNaN(parsedGround) || parsedGround < 0) {
      this.setPrizePoolEditorError('Ground amount must be a valid non-negative number.');
      return;
    }

    if (Number.isNaN(parsedChest) || parsedChest < 0) {
      this.setPrizePoolEditorError('Chest amount must be a valid non-negative number.');
      return;
    }

    let denominations = null;
    if (denominationsRaw) {
      denominations = denominationsRaw
        .split(',')
        .map((d) => Number(d.trim()))
        .filter((d) => Number.isFinite(d) && d > 0);
      if (!denominations.length) {
        denominations = null;
      }
    }

    if (!Array.isArray(this.prizePoolConfig.tokens)) {
      this.prizePoolConfig.tokens = [];
    }

    const entry = {
      tokenSymbol,
      tokenAddress: tokenAddress || null,
      chain: tokenChain === 'sui' ? 'sui' : 'solana',
      groundAmount: Number(parsedGround.toFixed(4)),
      chestAmount: Number(parsedChest.toFixed(4)),
      chestCount: parsedChestCount,
      denominations,
    };

    if (this.prizePoolTokenEditor.index !== null && this.prizePoolTokenEditor.index !== undefined) {
      this.prizePoolConfig.tokens[this.prizePoolTokenEditor.index] = entry;
    } else {
      this.prizePoolConfig.tokens.push(entry);
    }

    this.prizePoolWarnings = [];
    this.closePrizePoolTokenEditor();
    this.refreshPrizePoolUI();
  }

  setPrizePoolEditorError(message) {
    if (this.prizePoolTokenEditor?.errorText) {
      this.prizePoolTokenEditor.errorText.setText(message || '');
    }
  }

  removePrizePoolToken(index) {
    if (!Array.isArray(this.prizePoolConfig?.tokens) || !this.prizePoolConfig.tokens[index]) return;
    this.prizePoolConfig.tokens.splice(index, 1);
    this.prizePoolWarnings = [];
    this.refreshPrizePoolUI();
  }

  togglePrizePoolMatchScope() {
    this.prizePoolThisMatchOnly = !this.prizePoolThisMatchOnly;
    this.refreshPrizePoolUI();
  }

  async refreshLedgerBalances() {
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('jwt_token') : null;
    if (!token) {
      this.prizePoolWarnings = ['Login required to refresh ledger balances.'];
      this.prizePoolBalances = null;
      this.refreshPrizePoolUI();
      return;
    }

    if (this.prizePoolUI?.ledgerText) {
      this.prizePoolUI.ledgerText.setText('Refreshing balances...');
    }

    const apiKey = process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key';

    try {
      // Ensure tokens registry is loaded for proper symbol display
      await this.loadTokensRegistry();
      
      const response = await fetch('/api/ledger/balance', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-API-Key': apiKey,
        },
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }

      const json = await response.json().catch(() => ({ data: null }));
      this.prizePoolBalances = json?.data || null;
      this.refreshPrizePoolUI();
    } catch (error) {
      console.warn('[JoinScene] Failed to refresh ledger balances', error);
      this.prizePoolWarnings = ['Failed to refresh ledger balances'];
      this.refreshPrizePoolUI();
    }
  }

  async ensurePrizePoolSettingsLoaded(force = false) {
    if (this.prizePoolLoading) return;
    if (this.prizePoolDataLoaded && !force) return;

    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('jwt_token') : null;
    if (!token) {
      this.prizePoolConfig = { tokens: [] };
      this.prizePoolDataLoaded = true;
      return;
    }

    this.prizePoolLoading = true;
    const apiKey = process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key';

    try {
      await this.loadTokensRegistry();
      
      const [configRes, balanceRes] = await Promise.allSettled([
        fetch('/api/prize-pools', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }),
        fetch('/api/ledger/balance', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-API-Key': apiKey,
          },
        }),
      ]);

      if (configRes.status === 'fulfilled' && configRes.value.ok) {
        const json = await configRes.value.json().catch(() => ({ data: { tokens: [] } }));
        this.prizePoolSavedConfig = json?.data || { tokens: [] };
      } else {
        this.prizePoolSavedConfig = { tokens: [] };
      }

      if (balanceRes.status === 'fulfilled' && balanceRes.value.ok) {
        const json = await balanceRes.value.json().catch(() => ({ data: null }));
        this.prizePoolBalances = json?.data || null;
      } else {
        this.prizePoolBalances = null;
      }

      const cached = BattleStateManager.getPrizePoolConfig && BattleStateManager.getPrizePoolConfig();
      if (cached && typeof cached === 'object') {
        this.prizePoolConfig = this.clonePrizePoolConfig(cached);
        this.prizePoolThisMatchOnly = !!cached.runtimeOnly;
      } else if (this.prizePoolSavedConfig) {
        this.prizePoolConfig = this.clonePrizePoolConfig(this.prizePoolSavedConfig);
        this.prizePoolThisMatchOnly = false;
      } else {
        this.prizePoolConfig = { tokens: [] };
        this.prizePoolThisMatchOnly = false;
      }

      this.prizePoolWarnings = [];
      this.prizePoolDataLoaded = true;
    } catch (error) {
      console.warn('[JoinScene] Failed to load prize pool settings', error);
      this.prizePoolConfig = { tokens: [] };
      this.prizePoolBalances = null;
      this.prizePoolWarnings = ['Failed to load existing prize pool settings'];
    } finally {
      this.prizePoolLoading = false;
      if (this.prizePoolUI?.tokensList) {
        this.refreshPrizePoolUI();
      }
    }
  }

  async loadMatchSettings() {
    if (this.matchSettingsLoading) return;
    if (this.matchSettingsLoaded) return;

    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('jwt_token') : null;
    if (!token) {
      // No token, use global config or default
      this.loadGlobalMaxBattleStack();
      return;
    }

    this.matchSettingsLoading = true;

    try {
      // First try to get user's match settings
      const matchSettingsRes = await fetch('/api/match-settings', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (matchSettingsRes.ok) {
        const json = await matchSettingsRes.json().catch(() => ({ data: null }));
        // Explicitly check for number type to allow 0 as a valid value
        if (json?.success && json?.data && typeof json.data.max_battle_stack === 'number') {
          this.matchSettings = json.data;
          this.maxBattleStack = Math.max(0, Math.min(99, Math.floor(json.data.max_battle_stack)));
          // Load casual mode setting (default to true if not set)
          this.casualMode = json.data.casual_mode !== undefined ? Boolean(json.data.casual_mode) : true;
          // Load stat cap setting (default to 50 if not set, clamp to 20-200)
          this.statCap = json.data.stat_cap !== undefined ? Math.max(20, Math.min(200, Math.floor(json.data.stat_cap))) : 50;
          this.matchSettingsLoaded = true;
          this.matchSettingsLoading = false;
          // Update BattleStateManager with loaded value
          BattleStateManager.setBattleConfig({
            maxPlayers: this.maxHeroes,
            gameMode: this.gameMode,
            teamCount: this.teamCount,
            maxBattleStack: this.maxBattleStack,
            statNormalization: this.getStatNormalizationConfig()
          });
          // Refresh player cards with new stack size
          this.rebuildGridFromPlayers();
          return;
        }
      }

      // Fallback to global config
      this.loadGlobalMaxBattleStack();
    } catch (error) {
      console.warn('[JoinScene] Failed to load match settings', error);
      // Fallback to global config
      this.loadGlobalMaxBattleStack();
    } finally {
      this.matchSettingsLoading = false;
    }
  }

  async loadBattleConfigs() {
    
  }

  async loadGlobalMaxBattleStack() {
    try {
      const configRes = await fetch('/api/config/max_battle_stack');
      if (configRes.ok) {
        const json = await configRes.json().catch(() => ({ value: 10 }));
        if (json?.success && json?.value !== undefined) {
          this.maxBattleStack = Math.max(0, Math.min(99, Math.floor(json.value)));
        } else {
          this.maxBattleStack = 10; // Default fallback
        }
      } else {
        this.maxBattleStack = 10; // Default fallback
      }
    } catch (error) {
      console.warn('[JoinScene] Failed to load global max_battle_stack config', error);
      this.maxBattleStack = 10; // Default fallback
    }
    this.matchSettingsLoaded = true;
          // Update BattleStateManager with loaded value
          BattleStateManager.setBattleConfig({
            maxPlayers: this.maxHeroes,
            gameMode: this.gameMode,
            teamCount: this.teamCount,
            maxBattleStack: this.maxBattleStack,
            statNormalization: this.getStatNormalizationConfig()
          });
    // Refresh player cards with new stack size
    this.rebuildGridFromPlayers();
  }

  async persistPrizePoolSettings() {
    const sanitized = this.sanitizePrizePoolConfig(this.prizePoolConfig);
    const runtimeConfig = {
      ...sanitized,
      runtimeOnly: !!this.prizePoolThisMatchOnly,
    };

    if (BattleStateManager.setPrizePoolConfig) {
      BattleStateManager.setPrizePoolConfig(runtimeConfig);
    }

    this.prizePoolWarnings = [];

    if (this.prizePoolThisMatchOnly) {
      return { success: true };
    }

    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('jwt_token') : null;
    if (!token) {
      this.prizePoolWarnings = ['Login required to save prize pool settings.'];
      return { success: false };
    }

    if (!Array.isArray(sanitized.tokens) || !sanitized.tokens.length) {
      try {
        await fetch('/api/prize-pools', {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        this.prizePoolSavedConfig = { tokens: [] };
        return { success: true };
      } catch (error) {
        console.warn('[JoinScene] Failed clearing prize pool settings', error);
        this.prizePoolWarnings = ['Failed to clear saved prize pool settings'];
        return { success: false };
      }
    }

    try {
      const response = await fetch('/api/prize-pools', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(sanitized),
      });

      if (!response.ok) {
        const errorJson = await response.json().catch(() => null);
        this.prizePoolWarnings = [errorJson?.message || errorJson?.error || 'Failed to save prize pool settings'];
        return { success: false };
      }

      const json = await response.json().catch(() => ({ data: sanitized }));
      this.prizePoolSavedConfig = json?.data || sanitized;
      if (Array.isArray(json?.warnings) && json.warnings.length) {
        this.prizePoolWarnings = json.warnings;
      }
      return { success: true };
    } catch (error) {
      console.error('[JoinScene] Error saving prize pool settings:', error);
      this.prizePoolWarnings = ['Unexpected error saving prize pool settings'];
      return { success: false };
    }
  }

  async persistMatchSettings() {
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('jwt_token') : null;
    if (!token) {
      console.warn('[JoinScene] Login required to save match settings');
      return { success: false };
    }

    try {
      const response = await fetch('/api/match-settings', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          max_battle_stack: this.maxBattleStack,
          casual_mode: this.casualMode,
          stat_cap: this.statCap
        }),
      });

      if (!response.ok) {
        const errorJson = await response.json().catch(() => null);
        console.warn('[JoinScene] Failed to save match settings', errorJson);
        return { success: false };
      }

      const json = await response.json().catch(() => ({ data: { max_battle_stack: this.maxBattleStack } }));
      this.matchSettings = json?.data || { max_battle_stack: this.maxBattleStack };
      return { success: true };
    } catch (error) {
      console.error('[JoinScene] Error saving match settings:', error);
      return { success: false };
    }
  }

  async handleConfigSave() {
    try {
      const { min: minPlayers, max: maxPlayers } = this.getTotalPlayersSliderBounds();
      this.maxHeroes = Phaser.Math.Clamp(this.maxHeroes, minPlayers, maxPlayers);
      this.updateCounters();
      this.rebuildGridFromPlayers();

      const [prizePoolResult, matchSettingsResult] = await Promise.all([
        this.persistPrizePoolSettings(),
        this.persistMatchSettings()
      ]);

      const latestPrizePoolConfig = BattleStateManager.getPrizePoolConfig ? BattleStateManager.getPrizePoolConfig() : undefined;

      // Ensure selected battlefield is set in BattleStateManager and display is updated
      // Get from window state (which is updated when text is clicked)
      const selectedBattlefieldId = window.__SW_SELECTED_BATTLEFIELD_ID__ || '';
      
      if (selectedBattlefieldId) {
        // Update BattleStateManager and refresh display
        await this.updateBattlefieldSelection(selectedBattlefieldId);
      }

      BattleStateManager.setBattleConfig({
        maxPlayers: this.maxHeroes,
        gameMode: this.gameMode,
        teamCount: this.teamCount,
        maxBattleStack: this.maxBattleStack,
        prizePoolConfig: latestPrizePoolConfig,
        statNormalization: this.getStatNormalizationConfig()
      });
      this.refreshPrizePoolUI();

      if (!prizePoolResult.success) {
        return;
      }

      this.closeConfigModal();
    } catch (error) {
      console.error('[JoinScene] Failed to save configuration', error);
      this.prizePoolWarnings = ['Failed to save configuration'];
      this.refreshPrizePoolUI();
    }
  }

  async openConfigModal() {
    await this.ensurePrizePoolSettingsLoaded();
    // Ensure match settings are loaded before opening modal
    if (!this.matchSettingsLoaded) {
      await this.loadMatchSettings();
    }
    // Load operator's battlefields for selection
    await this.loadOperatorBattlefields();

    const { width, height } = this.cameras.main;
    
    // Create modal overlay
    this.modalOverlay = this.add.rectangle(width / 2, height / 2, width, height, 0x000000);
    this.modalOverlay.setAlpha(0.7);
    this.modalOverlay.setDepth(100);
    this.modalOverlay.setInteractive();
    
    // Modal container sized to 80% of viewport
    const modalWidth = width * 0.8;
    const modalHeight = height * 0.9;
    
    this.modalContainer = this.add.container(width / 2, height / 2).setDepth(101);
    
    // Modal background
    const modalBg = this.add.rectangle(0, 0, modalWidth, modalHeight, 0x1F2937);
    modalBg.setStrokeStyle(3, 0x4B5563);
    this.modalContainer.add(modalBg);
    
    // Modal title
    const modalTitle = this.add.text(0, -modalHeight / 2 + 40, 'Game Configuration', {
      fontSize: '28px',
      color: '#F3F300',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    this.modalContainer.add(modalTitle);
    
    // Close button
    const closeButton = this.add.text(modalWidth / 2 - 30, -modalHeight / 2 + 40, '✕', {
      fontSize: '32px',
      color: '#EF4444',
      fontFamily: 'Arial, sans-serif'
    }).setOrigin(0.5);
    closeButton.setInteractive({ useHandCursor: true });
    closeButton.on('pointerdown', () => this.closeConfigModal());
    closeButton.on('pointerover', () => closeButton.setScale(1.2));
    closeButton.on('pointerout', () => closeButton.setScale(1));
    this.modalContainer.add(closeButton);
    
    const margin = 32;
    const titleY = -modalHeight / 2 + 40; // Y position of title center
    const titleHeight = 28; // Approximate height of title text
    const titleBottomOffset = 20; // Space between title bottom and container top
    const saveAreaHeight = 110;
    const columnWidth = (modalWidth - margin * 3) / 2;
    
    // Calculate container top position (just under title)
    const containerTop = titleY + titleHeight / 2 + titleBottomOffset;
    // Calculate available height for containers
    const containerBottom = modalHeight / 2 - margin - saveAreaHeight;
    // Column height to fit all content within modal bounds
    const columnHeight = containerBottom - containerTop;
    // Position containers centered vertically in available space
    const columnCenterY = containerTop + columnHeight / 2;

    this.modalLeftContainer = this.add.container(-modalWidth / 2 + margin + columnWidth / 2, columnCenterY);
    this.modalRightContainer = this.add.container(modalWidth / 2 - margin - columnWidth / 2, columnCenterY);
    this.modalContainer.add(this.modalLeftContainer);
    this.modalContainer.add(this.modalRightContainer);

    const leftBg = this.add.rectangle(0, 0, columnWidth, columnHeight + 110, 0x1F2937, 0.95);
    leftBg.setStrokeStyle(2, 0x374151);
    this.modalLeftContainer.add(leftBg);

    const rightBg = this.add.rectangle(0, 0, columnWidth, columnHeight + 110, 0x111827, 0.95);
    rightBg.setStrokeStyle(2, 0x374151);
    this.modalRightContainer.add(rightBg);

    let leftY = -columnHeight / 2 + 40;
    const contentWidth = columnWidth - 80;

    const modeLabel = this.add.text(0, leftY, 'Game Mode', {
      fontSize: '16px',
      color: '#F3F4F6',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    this.modalLeftContainer.add(modeLabel);

    leftY += 28;

    const modeDropdownBg = this.add.rectangle(0, leftY, contentWidth, 38, 0x374151);
    modeDropdownBg.setStrokeStyle(2, 0x4B5563);
    modeDropdownBg.setInteractive({ useHandCursor: true });
    this.modalLeftContainer.add(modeDropdownBg);

    this.modalModeText = this.add.text(0, leftY, `${this.gameMode} ▼`, {
      fontSize: '15px',
      color: '#D1D5DB',
      fontFamily: 'Arial, sans-serif'
    }).setOrigin(0.5);
    this.modalLeftContainer.add(this.modalModeText);

    modeDropdownBg.on('pointerdown', () => {
      const modes = ['free-for-all', 'team-battle', 'last-man-standing'];
      const currentIndex = modes.indexOf(this.gameMode);
      this.gameMode = modes[(currentIndex + 1) % modes.length];
      this.modalModeText.setText(`${this.gameMode} ▼`);
      this.modeText.setText(`Mode: ${this.gameMode}`);
    });

    leftY += 48;

    // Battlefield Selection
    const battlefieldLabel = this.add.text(0, leftY, 'Battlefield ID', {
      fontSize: '16px',
      color: '#F3F4F6',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    this.modalLeftContainer.add(battlefieldLabel);

    leftY += 26;

    const idInputBg = this.add.rectangle(0, leftY, contentWidth, 34, 0x374151);
    idInputBg.setStrokeStyle(2, 0x4B5563);
    idInputBg.setInteractive({ useHandCursor: true });
    this.modalLeftContainer.add(idInputBg);

    const selectedBattlefieldId = window.__SW_SELECTED_BATTLEFIELD_ID__ || '';
    const displayText = selectedBattlefieldId || 'Click to paste ID...';
    
    this.modalBattlefieldIdText = this.add.text(0, leftY, displayText, {
      fontSize: '13px',
      color: selectedBattlefieldId ? '#D1D5DB' : '#6B7280',
      fontFamily: 'Arial, sans-serif',
      fontStyle: selectedBattlefieldId ? 'normal' : 'italic'
    }).setOrigin(0.5);
    this.modalLeftContainer.add(this.modalBattlefieldIdText);

    // Make the background and text clickable to open prompt
    const handleIdInput = () => {
      const currentId = window.__SW_SELECTED_BATTLEFIELD_ID__ || '';
      const input = prompt('Enter Battlefield ID:', currentId);
      if (input !== null) {
        const value = input.trim();
        if (value) {
          window.__SW_SELECTED_BATTLEFIELD_ID__ = value;
          try {
            localStorage.setItem('sw_selected_battlefield_id', value);
          } catch (_) {}
          this.modalBattlefieldIdText.setText(value);
          this.modalBattlefieldIdText.setStyle({ color: '#D1D5DB', fontStyle: 'normal' });
          this.updateBattlefieldSelection(value);
        } else if (value === '') {
          // Clear selection
          window.__SW_SELECTED_BATTLEFIELD_ID__ = '';
          try {
            localStorage.removeItem('sw_selected_battlefield_id');
          } catch (_) {}
          this.modalBattlefieldIdText.setText('Click to paste ID...');
          this.modalBattlefieldIdText.setStyle({ color: '#6B7280', fontStyle: 'italic' });
        }
      }
    };

    idInputBg.on('pointerdown', handleIdInput);
    this.modalBattlefieldIdText.setInteractive({ useHandCursor: true });
    this.modalBattlefieldIdText.on('pointerdown', handleIdInput);

    // Initialize battlefield selection if ID is already set
    if (selectedBattlefieldId) {
      this.updateBattlefieldSelection(selectedBattlefieldId);
    }

    leftY += 44;

    // Operator's Battlefields Dropdown (if any)
    const operatorBattlefields = window.__SW_OPERATOR_BATTLEFIELDS__ || [];
    if (operatorBattlefields.length > 0) {
      const operatorLabel = this.add.text(0, leftY, 'Your Battlefields:', {
        fontSize: '13px',
        color: '#D1D5DB',
        fontFamily: 'Arial, sans-serif'
      }).setOrigin(0.5);
      this.modalLeftContainer.add(operatorLabel);

      leftY += 30;

      const operatorDropdownBg = this.add.rectangle(0, leftY, contentWidth, 38, 0x374151);
      operatorDropdownBg.setStrokeStyle(2, 0x4B5563);
      operatorDropdownBg.setInteractive({ useHandCursor: true });
      this.modalLeftContainer.add(operatorDropdownBg);

      // Find selected operator battlefield
      const selectedOperatorBf = operatorBattlefields.find(bf => bf.id === selectedBattlefieldId);
      let operatorDisplayName = 'Select your battlefield...';
      if (selectedOperatorBf) {
        const customName = selectedOperatorBf.customName;
        const themeName = selectedOperatorBf.themeBattlefield?.name || 'Unknown';
        operatorDisplayName = customName ? customName : themeName;
      }

      this.modalOperatorBattlefieldText = this.add.text(0, leftY, `${operatorDisplayName} ▼`, {
        fontSize: '15px',
        color: '#D1D5DB',
        fontFamily: 'Arial, sans-serif'
      }).setOrigin(0.5);
      this.modalLeftContainer.add(this.modalOperatorBattlefieldText);

      operatorDropdownBg.on('pointerdown', () => {
        this.cycleOperatorBattlefield();
      });

      leftY += 48;
    } else {
      leftY += 8;
    }

    const playersLabel = this.add.text(0, leftY, 'Players per Team', {
      fontSize: '15px',
      color: '#D1D5DB',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    this.modalLeftContainer.add(playersLabel);

    leftY += 26;
    this.createPlayersSlider(this.modalLeftContainer, leftY, contentWidth);

    leftY += 70;

    const totalPlayersLabel = this.add.text(0, leftY, 'Total Players', {
      fontSize: '15px',
      color: '#D1D5DB',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    this.modalLeftContainer.add(totalPlayersLabel);

    leftY += 26;
    this.createTotalPlayersSlider(this.modalLeftContainer, leftY, contentWidth);

    leftY += 70;

    const consumablesLabel = this.add.text(0, leftY, 'Incoming Consumables Stack Size', {
      fontSize: '15px',
      color: '#D1D5DB',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    this.modalLeftContainer.add(consumablesLabel);

    leftY += 26;
    this.createConsumablesStackSlider(this.modalLeftContainer, leftY, contentWidth);

    leftY += 70;

    // Casual Mode checkbox
    this.createCasualModeCheckbox(this.modalLeftContainer, leftY, contentWidth);

    leftY += 60;

    // Stat Cap slider (only show when casual mode is enabled)
    this.createStatCapSlider(this.modalLeftContainer, leftY, contentWidth);

    this.buildPrizePoolSection(this.modalRightContainer, columnWidth, columnHeight);

    const saveButtonY = columnCenterY + columnHeight / 2 + saveAreaHeight / 2;
    this.createModalButton(this.modalContainer, {
      x: 0, // Move 200 pixels to the right to center under right panel
      y: saveButtonY + 40,
      width: 200,
      height: 50,
      label: 'Save Settings',
      onClick: () => this.handleConfigSave(),
    });
    
    // Fade in animation
    this.modalContainer.setAlpha(0);
    this.tweens.add({
      targets: this.modalContainer,
      alpha: 1,
      scaleX: { from: 0.9, to: 1 },
      scaleY: { from: 0.9, to: 1 },
      duration: 300,
      ease: 'Back.easeOut'
    });
  }
  
  createPlayersSlider(container, yPos, sliderWidth) {
    if (!this.playersPerTeam) {
      this.playersPerTeam = 1; // Default 1 players per team
    }
    
    const sliderMinX = -sliderWidth / 2;
    const sliderMaxX = sliderWidth / 2;
    
    // Slider track
    const track = this.add.rectangle(0, yPos, sliderWidth, 6, 0x4B5563);
    track.setStrokeStyle(1, 0x6B7280);
    container.add(track);
    
    // Slider fill
    const fillWidth = ((this.playersPerTeam - 2) / 998) * sliderWidth; // 2-1000 range
    this.playersSliderFill = this.add.rectangle(sliderMinX, yPos, fillWidth, 6, 0x10B981);
    this.playersSliderFill.setOrigin(0, 0.5);
    container.add(this.playersSliderFill);
    
    // Slider thumb
    const thumbX = sliderMinX + ((this.playersPerTeam - 2) / 998) * sliderWidth;
    this.playersSliderThumb = this.add.circle(thumbX, yPos, 12, 0x10B981);
    this.playersSliderThumb.setStrokeStyle(3, 0xFFFFFF);
    this.playersSliderThumb.setInteractive({ useHandCursor: true, draggable: true });
    container.add(this.playersSliderThumb);
    
    // Value display
    this.playersSliderValue = this.add.text(0, yPos + 20, `${this.playersPerTeam} Players`, {
      fontSize: '14px',
      color: '#10B981',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    container.add(this.playersSliderValue);
    
    // Store for drag calculations
    this.playersSliderMinX = sliderMinX;
    this.playersSliderMaxX = sliderMaxX;
    this.playersSliderWidth = sliderWidth;
    
    // Drag logic
    this.playersSliderThumb.on('drag', (pointer) => {
      const containerCenterX = this.modalContainer.x + container.x;
      const relativeX = pointer.x - containerCenterX;
      const clampedX = Phaser.Math.Clamp(relativeX, sliderMinX, sliderMaxX);
      
      this.playersSliderThumb.x = clampedX;
      
      const percent = (clampedX - sliderMinX) / (sliderMaxX - sliderMinX);
      this.playersPerTeam = Math.round(2 + percent * 998);
      
      const newFillWidth = ((this.playersPerTeam - 2) / 998) * sliderWidth;
      this.playersSliderFill.width = newFillWidth;
      
      this.playersSliderValue.setText(`${this.playersPerTeam} Players`);
    });
    
    this.playersSliderThumb.on('pointerover', () => {
      this.playersSliderThumb.setScale(1.2);
    });
    
    this.playersSliderThumb.on('pointerout', () => {
      this.playersSliderThumb.setScale(1);
    });
  }
  
  getTotalPlayersSliderBounds() {
    if (this.gameMode === 'free-for-all') {
      return { min: 4, max: 100 };
    }

    return { min: 2, max: 1000 };
  }

  createTotalPlayersSlider(container, yPos, sliderWidth) {
    const { min: minPlayers, max: maxPlayers } = this.getTotalPlayersSliderBounds();

    if (!this.maxHeroes) {
      this.maxHeroes = Math.max(minPlayers, 16);
    }

    this.maxHeroes = Phaser.Math.Clamp(this.maxHeroes, minPlayers, maxPlayers);

    const sliderMinX = -sliderWidth / 2;
    const sliderMaxX = sliderWidth / 2;
    const sliderRange = Math.max(1, maxPlayers - minPlayers);
    const currentPercent = (this.maxHeroes - minPlayers) / sliderRange;

    // Slider track
    const track = this.add.rectangle(0, yPos, sliderWidth, 6, 0x4B5563);
    track.setStrokeStyle(1, 0x6B7280);
    container.add(track);

    // Slider fill
    const fillWidth = currentPercent * sliderWidth;
    this.totalPlayersSliderFill = this.add.rectangle(sliderMinX, yPos, fillWidth, 6, 0x3B82F6);
    this.totalPlayersSliderFill.setOrigin(0, 0.5);
    container.add(this.totalPlayersSliderFill);

    // Slider thumb
    const thumbX = sliderMinX + currentPercent * sliderWidth;
    this.totalPlayersSliderThumb = this.add.circle(thumbX, yPos, 12, 0x3B82F6);
    this.totalPlayersSliderThumb.setStrokeStyle(3, 0xFFFFFF);
    this.totalPlayersSliderThumb.setInteractive({ useHandCursor: true, draggable: true });
    container.add(this.totalPlayersSliderThumb);

    // Value display
    this.totalPlayersSliderValue = this.add.text(0, yPos + 20, `${this.maxHeroes} Players`, {
      fontSize: '14px',
      color: '#3B82F6',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    container.add(this.totalPlayersSliderValue);

    // Store for drag calculations
    this.totalPlayersSliderMinX = sliderMinX;
    this.totalPlayersSliderMaxX = sliderMaxX;
    this.totalPlayersSliderWidth = sliderWidth;
    this.totalPlayersSliderBounds = { min: minPlayers, max: maxPlayers, range: sliderRange };

    // Drag logic
    this.totalPlayersSliderThumb.on('drag', (pointer) => {
      const containerCenterX = this.modalContainer.x + container.x;
      const relativeX = pointer.x - containerCenterX;
      const clampedX = Phaser.Math.Clamp(relativeX, sliderMinX, sliderMaxX);

      const rawPercent = sliderWidth === 0 ? 0 : (clampedX - sliderMinX) / sliderWidth;
      const rawValue = minPlayers + rawPercent * sliderRange;
      this.maxHeroes = Math.round(rawValue);
      this.maxHeroes = Phaser.Math.Clamp(this.maxHeroes, minPlayers, maxPlayers);

      const normalizedPercent = sliderRange === 0 ? 0 : (this.maxHeroes - minPlayers) / sliderRange;
      this.totalPlayersSliderThumb.x = sliderMinX + normalizedPercent * sliderWidth;
      this.totalPlayersSliderFill.width = normalizedPercent * sliderWidth;

      this.totalPlayersSliderValue.setText(`${this.maxHeroes} Players`);

      // Update the main display
      this.updatePlayerCountDisplay();
      this.queueGridRebuild();
    });

    this.totalPlayersSliderThumb.on('pointerover', () => {
      this.totalPlayersSliderThumb.setScale(1.2);
    });

    this.totalPlayersSliderThumb.on('pointerout', () => {
      this.totalPlayersSliderThumb.setScale(1);
    });
  }

  createConsumablesStackSlider(container, yPos, sliderWidth) {
    const minStack = 0;
    const maxStack = 99;

    if (this.maxBattleStack === undefined || this.maxBattleStack === null) {
      this.maxBattleStack = 10;
    }

    this.maxBattleStack = Phaser.Math.Clamp(this.maxBattleStack, minStack, maxStack);

    const sliderMinX = -sliderWidth / 2;
    const sliderMaxX = sliderWidth / 2;
    const sliderRange = Math.max(1, maxStack - minStack);
    const currentPercent = (this.maxBattleStack - minStack) / sliderRange;

    // Slider track
    const track = this.add.rectangle(0, yPos, sliderWidth, 6, 0x4B5563);
    track.setStrokeStyle(1, 0x6B7280);
    container.add(track);

    // Slider fill
    const fillWidth = currentPercent * sliderWidth;
    this.consumablesStackSliderFill = this.add.rectangle(sliderMinX, yPos, fillWidth, 6, 0x10B981);
    this.consumablesStackSliderFill.setOrigin(0, 0.5);
    container.add(this.consumablesStackSliderFill);

    // Slider thumb
    const thumbX = sliderMinX + currentPercent * sliderWidth;
    this.consumablesStackSliderThumb = this.add.circle(thumbX, yPos, 12, 0x10B981);
    this.consumablesStackSliderThumb.setStrokeStyle(3, 0xFFFFFF);
    this.consumablesStackSliderThumb.setInteractive({ useHandCursor: true, draggable: true });
    container.add(this.consumablesStackSliderThumb);

    // Value display
    this.consumablesStackSliderValue = this.add.text(0, yPos + 20, `${this.maxBattleStack} Items`, {
      fontSize: '14px',
      color: '#10B981',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    container.add(this.consumablesStackSliderValue);

    // Store for drag calculations
    this.consumablesStackSliderMinX = sliderMinX;
    this.consumablesStackSliderMaxX = sliderMaxX;
    this.consumablesStackSliderWidth = sliderWidth;
    this.consumablesStackSliderBounds = { min: minStack, max: maxStack, range: sliderRange };

    // Drag logic
    this.consumablesStackSliderThumb.on('drag', (pointer) => {
      const containerCenterX = this.modalContainer.x + container.x;
      const relativeX = pointer.x - containerCenterX;
      const clampedX = Phaser.Math.Clamp(relativeX, sliderMinX, sliderMaxX);

      const rawPercent = sliderWidth === 0 ? 0 : (clampedX - sliderMinX) / sliderWidth;
      const rawValue = minStack + rawPercent * sliderRange;
      this.maxBattleStack = Math.round(rawValue);
      this.maxBattleStack = Phaser.Math.Clamp(this.maxBattleStack, minStack, maxStack);

      const normalizedPercent = sliderRange === 0 ? 0 : (this.maxBattleStack - minStack) / sliderRange;
      this.consumablesStackSliderThumb.x = sliderMinX + normalizedPercent * sliderWidth;
      this.consumablesStackSliderFill.width = normalizedPercent * sliderWidth;

      this.consumablesStackSliderValue.setText(`${this.maxBattleStack} Items`);

      // Refresh player cards with new stack size
      this.rebuildGridFromPlayers();
    });

    this.consumablesStackSliderThumb.on('pointerover', () => {
      this.consumablesStackSliderThumb.setScale(1.2);
    });

    this.consumablesStackSliderThumb.on('pointerout', () => {
      this.consumablesStackSliderThumb.setScale(1);
    });
  }

  createCasualModeCheckbox(container, yPos, contentWidth) {
    const checkboxSize = 24;
    const checkboxX = -contentWidth / 2 + checkboxSize / 2 + 10;
    const checkboxY = yPos;

    // Checkbox background
    const checkboxBg = this.add.rectangle(checkboxX, checkboxY, checkboxSize, checkboxSize, 0x374151);
    checkboxBg.setStrokeStyle(2, this.casualMode ? 0x10B981 : 0x6B7280);
    checkboxBg.setInteractive({ useHandCursor: true });
    container.add(checkboxBg);

    // Checkmark (only visible when checked)
    this.casualModeCheckmark = this.add.text(checkboxX, checkboxY, '✓', {
      fontSize: '18px',
      color: '#10B981',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    this.casualModeCheckmark.setVisible(this.casualMode);
    container.add(this.casualModeCheckmark);

    // Label text
    const labelText = this.add.text(checkboxX + checkboxSize / 2 + 12, checkboxY, 'Casual Mode (Stat Normalization)', {
      fontSize: '14px',
      color: '#D1D5DB',
      fontFamily: 'Arial, sans-serif'
    }).setOrigin(0, 0.5);
    container.add(labelText);

    // Description text
    this.casualModeDescText = this.add.text(checkboxX + checkboxSize / 2 + 12, checkboxY + 18, `Caps stats at ${this.statCap} for balanced gameplay`, {
      fontSize: '11px',
      color: '#9CA3AF',
      fontFamily: 'Arial, sans-serif'
    }).setOrigin(0, 0.5);
    container.add(this.casualModeDescText);

    // Toggle on click
    const toggleCheckbox = () => {
      this.casualMode = !this.casualMode;
      this.casualModeCheckmark.setVisible(this.casualMode);
      checkboxBg.setStrokeStyle(2, this.casualMode ? 0x10B981 : 0x6B7280);
      // Show/hide stat cap slider based on casual mode
      if (this.statCapSliderThumb) {
        const visible = this.casualMode;
        this.statCapSliderThumb.setVisible(visible);
        this.statCapSliderFill.setVisible(visible);
        this.statCapSliderValue.setVisible(visible);
        if (this.statCapSliderLabel) {
          this.statCapSliderLabel.setVisible(visible);
        }
        if (this.statCapSliderTrack) {
          this.statCapSliderTrack.setVisible(visible);
        }
      }
    };

    checkboxBg.on('pointerdown', toggleCheckbox);
    labelText.setInteractive({ useHandCursor: true });
    labelText.on('pointerdown', toggleCheckbox);
  }

  getStatNormalizationConfig() {
    // Check if ranked mode is enabled via vote (overrides personal setting)
    if (this.rankedVoteStatus && this.rankedVoteStatus.rankedModeEnabled) {
      // Ranked mode: no normalization (all players voted ranked)
      return {
        enabled: false,
        strategy: 'hardCap',
        cap: this.statCap,
        debug: false
      };
    }
    
    // Use personal casual mode setting
    if (this.casualMode) {
      return {
        enabled: true,
        strategy: 'hardCap',
        cap: this.statCap, // Use configurable stat cap
        debug: false
      };
    } else {
      // Ranked mode: no normalization
      return {
        enabled: false
      };
    }
  }

  createStatCapSlider(container, yPos, sliderWidth) {
    const minCap = 20;
    const maxCap = 200;

    if (this.statCap === undefined || this.statCap === null) {
      this.statCap = 50;
    }

    this.statCap = Phaser.Math.Clamp(this.statCap, minCap, maxCap);

    const sliderMinX = -sliderWidth / 2;
    const sliderMaxX = sliderWidth / 2;
    const sliderRange = Math.max(1, maxCap - minCap);
    const currentPercent = (this.statCap - minCap) / sliderRange;

    // Label (store reference for visibility toggle)
    this.statCapSliderLabel = this.add.text(0, yPos - 24, 'Stat Cap Target', {
      fontSize: '14px',
      color: '#D1D5DB',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    this.statCapSliderLabel.setVisible(this.casualMode); // Only visible when casual mode is enabled
    container.add(this.statCapSliderLabel);

    // Slider track (store reference for visibility toggle)
    this.statCapSliderTrack = this.add.rectangle(0, yPos, sliderWidth, 6, 0x4B5563);
    this.statCapSliderTrack.setStrokeStyle(1, 0x6B7280);
    this.statCapSliderTrack.setVisible(this.casualMode); // Only visible when casual mode is enabled
    container.add(this.statCapSliderTrack);

    // Slider fill
    const fillWidth = currentPercent * sliderWidth;
    this.statCapSliderFill = this.add.rectangle(sliderMinX, yPos, fillWidth, 6, 0xF59E0B);
    this.statCapSliderFill.setOrigin(0, 0.5);
    this.statCapSliderFill.setVisible(this.casualMode); // Only visible when casual mode is enabled
    container.add(this.statCapSliderFill);

    // Slider thumb
    const thumbX = sliderMinX + currentPercent * sliderWidth;
    this.statCapSliderThumb = this.add.circle(thumbX, yPos, 12, 0xF59E0B);
    this.statCapSliderThumb.setStrokeStyle(3, 0xFFFFFF);
    this.statCapSliderThumb.setInteractive({ useHandCursor: true, draggable: true });
    this.statCapSliderThumb.setVisible(this.casualMode); // Only visible when casual mode is enabled
    container.add(this.statCapSliderThumb);

    // Value display
    this.statCapSliderValue = this.add.text(0, yPos + 20, `${this.statCap}`, {
      fontSize: '14px',
      color: '#F59E0B',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    this.statCapSliderValue.setVisible(this.casualMode); // Only visible when casual mode is enabled
    container.add(this.statCapSliderValue);

    // Store for drag calculations
    this.statCapSliderMinX = sliderMinX;
    this.statCapSliderMaxX = sliderMaxX;
    this.statCapSliderWidth = sliderWidth;
    this.statCapSliderY = yPos;

    // Drag handler
    this.input.on('drag', (pointer, gameObject, dragX) => {
      if (gameObject === this.statCapSliderThumb) {
        const clampedX = Phaser.Math.Clamp(dragX, sliderMinX, sliderMaxX);
        const percent = (clampedX - sliderMinX) / sliderWidth;
        this.statCap = Math.round(minCap + percent * sliderRange);
        this.statCap = Phaser.Math.Clamp(this.statCap, minCap, maxCap);

        // Update thumb position
        this.statCapSliderThumb.x = clampedX;

        // Update fill width
        const newFillWidth = percent * sliderWidth;
        this.statCapSliderFill.width = newFillWidth;

        // Update value display
        this.statCapSliderValue.setText(`${this.statCap}`);

        // Update checkbox description if it exists
        if (this.casualModeDescText) {
          this.casualModeDescText.setText(`Caps stats at ${this.statCap} for balanced gameplay`);
        }
      }
    });

    // Click on track to jump to position
    this.statCapSliderTrack.setInteractive({ useHandCursor: true });
    this.statCapSliderTrack.on('pointerdown', (pointer) => {
      const localX = pointer.x - (container.x + sliderMinX);
      const percent = Phaser.Math.Clamp(localX / sliderWidth, 0, 1);
      this.statCap = Math.round(minCap + percent * sliderRange);
      this.statCap = Phaser.Math.Clamp(this.statCap, minCap, maxCap);

      const thumbX = sliderMinX + percent * sliderWidth;
      this.statCapSliderThumb.x = thumbX;
      const fillWidth = percent * sliderWidth;
      this.statCapSliderFill.width = fillWidth;
      this.statCapSliderValue.setText(`${this.statCap}`);

      // Update checkbox description if it exists
      if (this.casualModeDescText) {
        this.casualModeDescText.setText(`Caps stats at ${this.statCap} for balanced gameplay`);
      }
    });

    this.statCapSliderThumb.on('pointerover', () => {
      this.statCapSliderThumb.setScale(1.2);
    });
    this.statCapSliderThumb.on('pointerout', () => {
      this.statCapSliderThumb.setScale(1.0);
    });
  }

  /**
   * Update ranked vote status and refresh UI
   */
  updateRankedVoteStatus(voteStatus) {
    this.rankedVoteStatus = voteStatus;
    
    // Update battle config if ranked mode is now enabled
    if (voteStatus.rankedModeEnabled) {
      BattleStateManager.setBattleConfig({
        maxPlayers: this.maxHeroes,
        gameMode: this.gameMode,
        teamCount: this.teamCount,
        maxBattleStack: this.maxBattleStack,
        statNormalization: this.getStatNormalizationConfig()
      });
    }
    
    // Create or update vote status UI
    this.createOrUpdateRankedVoteUI();
  }

  /**
   * Create or update the ranked vote status UI
   */
  createOrUpdateRankedVoteUI() {
    const { width, height } = this.cameras.main;
    
    // Position in top-right area, below any existing UI
    const uiX = width - 20;
    const uiY = 120;
    const uiWidth = 280;
    
    // Destroy existing UI if it exists
    if (this.rankedVoteUI) {
      this.rankedVoteUI.destroy(true);
      this.rankedVoteUI = null;
    }
    
    // Only show UI if there are players
    if (this.rankedVoteStatus.totalPlayers === 0) {
      return;
    }
    
    // Create container
    this.rankedVoteUI = this.add.container(uiX, uiY);
    
    // Background
    const bg = this.add.rectangle(0, 0, uiWidth, 120, 0x1F2937, 0.9);
    bg.setStrokeStyle(2, this.rankedVoteStatus.rankedModeEnabled ? 0xF59E0B : 0x4B5563);
    this.rankedVoteUI.add(bg);
    
    // Title
    const title = this.add.text(0, -45, 'Ranked Mode Vote', {
      fontSize: '16px',
      color: '#F59E0B',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    this.rankedVoteUI.add(title);
    
    // Status text
    let statusText = '';
    if (this.rankedVoteStatus.rankedModeEnabled) {
      statusText = `✅ Ranked Mode Active!\nAll ${this.rankedVoteStatus.totalPlayers} players voted ranked`;
    } else {
      statusText = `${this.rankedVoteStatus.votesRanked} / ${this.rankedVoteStatus.totalPlayers} players voted ranked\nType "ranked" to vote!`;
    }
    
    const status = this.add.text(0, -10, statusText, {
      fontSize: '14px',
      color: '#D1D5DB',
      fontFamily: 'Arial, sans-serif',
      align: 'center'
    }).setOrigin(0.5);
    this.rankedVoteUI.add(status);
    
    // Progress bar background
    const progressBg = this.add.rectangle(0, 25, uiWidth - 40, 8, 0x374151);
    this.rankedVoteUI.add(progressBg);
    
    // Progress bar fill
    const progress = this.rankedVoteStatus.totalPlayers > 0 
      ? this.rankedVoteStatus.votesRanked / this.rankedVoteStatus.totalPlayers 
      : 0;
    const progressFill = this.add.rectangle(
      -(uiWidth - 40) / 2 + (uiWidth - 40) * progress / 2,
      25,
      (uiWidth - 40) * progress,
      8,
      this.rankedVoteStatus.rankedModeEnabled ? 0xF59E0B : 0x10B981
    );
    this.rankedVoteUI.add(progressFill);
    
    // Set container origin to top-right
    this.rankedVoteUI.setX(uiX - uiWidth / 2);
    
    // Make it visible above other UI
    this.rankedVoteUI.setDepth(1000);
  }
  
  closeConfigModal() {
    this.closePrizePoolTokenEditor();
    if (this.modalOverlay) {
      this.modalOverlay.destroy();
      this.modalOverlay = null;
    }
    if (this.modalContainer) {
      this.tweens.add({
        targets: this.modalContainer,
        alpha: 0,
        scaleX: 0.9,
        scaleY: 0.9,
        duration: 200,
        ease: 'Power2',
        onComplete: () => {
          this.modalContainer.destroy();
          this.modalContainer = null;
        }
      });
    }

    this.modalLeftContainer = null;
    this.modalRightContainer = null;

    this.prizePoolUI = {
      container: null,
      tokensList: null,
      matchOnlyToggle: null,
      warningsText: null,
      ledgerText: null,
      sectionWidth: 360,
      sectionHeight: 320,
    };
  }

  /**
   * Start voting countdown when betting interest has been expressed
   */
  async startVotingCountdown() {
    this.hideJoinOverlay();
    await this.prepareBattleRoster();

    // Lock player joins
    this.playersLockedForVoting = true;
    
    // Generate battleId now
    const battleId = `battle_${Date.now()}`;
    BattleStateManager.setBattleId(battleId);
    
    // Initialize vote session with current players
    console.log('🎯 [JoinScene] Current players for voting:', this.players.length);
    const votePlayers = this.players.map((p, index) => ({
      heroId: p.hero?.id || `player_${index}`,
      name: p.hero?.name || p.name,
      level: p.hero?.level || 1,
      isAI: p.isAI || false,
      isSpecial: p.isSpecial || false,
      playerNumber: index + 1
    }));
    
    console.log('🎯 [JoinScene] Vote players prepared:', votePlayers);
    
    const roomId = (typeof window !== 'undefined' && window.currentRoomId) ? window.currentRoomId : null;
    const sessionId = await VoteManager.initSession(battleId, votePlayers, roomId);
    BattleStateManager.setVoteSessionId(sessionId);
    
    // Activate voting countdown flag
    this.votingCountdownActive = true;
    
    // Show all vote percentage displays
    this.playerDisplayObjects.forEach(obj => {
      if (obj.voteText) {
        obj.voteText.setVisible(true);
      }
    });
    
    // Display countdown message
    try {
      
      showMarqueeMessage('🎯 Voting is now open! 60 seconds to place bets!', 
        { color: '#10B981' }, 'high');
    } catch (error) {
      console.warn('Failed to show marquee message:', error);
    }
    
    // Start 60-second countdown
    let votingSecondsRemaining = 60;
    this.votingCountdownTimer = this.time.addEvent({
      delay: 1000,
      callback: () => {
        votingSecondsRemaining--;
        
        // Update timer display
        if (this.timerText) {
          this.timerText.setText(`Voting: ${votingSecondsRemaining}s`);
          this.timerText.setColor('#F59E0B'); // Orange for voting countdown
          //center the timer text
          this.timerText.setOrigin(0.5);
          this.timerText.setPosition(0, 0);
          this.timerText.setAlign('center');
        }
        
        if (votingSecondsRemaining <= 0) {
          this.votingCountdownTimer.destroy();
          this.proceedToBattle();
        }
      },
      callbackScope: this,
      loop: true
    });
  }

  /**
   * Proceed to battle after voting countdown or when no voting interest
   */
  async proceedToBattle() {
    try {
      console.log('🏁 Starting battle process...');

      await this.prepareBattleRoster();

      const finalPlayers = BattleStateManager.getPlayerList() || this.battleRosterPlayers;
      const teamResult = this.teamBuildResult;

      console.log('🎯 Team roster ready:', {
        players: finalPlayers?.length || 0,
        hasTeamResult: !!teamResult
      });
      
      // Decrement auto-join credits for all players starting the battle
      this.consumeAutoJoinCredits();
      
      // Determine required textures for asset loading
      const requiredTextures = (finalPlayers || [])
        .filter(p => p.hero && p.hero.image_url)
        .map(p => `player_${p.hero.id || p.wallet || p.id}`);
      
      if (requiredTextures.length > 0) {
        BattleStateManager.addRequiredTextures(requiredTextures);
      }

      // Battlefield should already be selected from join phase
      const battlefield = BattleStateManager.getBattlefield();
      const battlefieldInstanceId = BattleStateManager.getBattlefieldInstanceId();
      
      if (!battlefield) {
        console.warn('⚠️ No battlefield selected, using fallback');
        const fallback = {
          id: 'fallback',
          name: 'Default Arena',
          description: 'A standard battlefield for epic battles.',
          image: 'battlefields/default-arena.jpg',
          owner: 'System',
          loot_collected: 0
        };
        BattleStateManager.setBattlefield(fallback, null);
      }

      console.log('🌍 Battlefield selected:', battlefield?.name, 'Instance ID:', battlefieldInstanceId);
      console.log('📦 Required textures:', requiredTextures.length);

      const playerCount = finalPlayers?.length || 0;
      const derivedTeamCount = teamResult?.teams ? Object.keys(teamResult.teams).length : this.teamCount;

      // Update state to 'pre-battle' before transitioning
      sessionStateManager.updateState('pre-battle');

      // Transition to PreBattleScene
      this.scene.start('PreBattleScene', {
        battlefield,
        playerCount,
        teamCount: derivedTeamCount,
        musicState: this.getMusicState()
      });
      
    } catch (error) {
      console.error('❌ Error starting battle:', error);
      
      // Show error message to user
      if (this.centerContainer) {
        const errorText = this.add.text(0, 0, 'Failed to start battle. Please try again.', {
          fontSize: '18px',
          color: '#EF4444',
          fontFamily: 'Arial, sans-serif',
          fontStyle: 'bold'
        }).setOrigin(0.5);
        this.centerContainer.add(errorText);
        
        // Remove error message after 3 seconds
        this.time.delayedCall(3000, () => {
          if (errorText && errorText.active) {
            errorText.destroy();
          }
        });
      }
    }
  }

  /**
   * Start the battle by building teams and transitioning to PreBattleScene
   */
  async startBattle() {
    try {
      this.hideJoinOverlay();
      this.hideBattlefieldRow();
      console.log('🏁 Starting battle process...');
      console.log('🔍 Voting state check:', {
        votingInterestExpressed: this.votingInterestExpressed,
        votingCountdownActive: this.votingCountdownActive
      });

      // Stop the join countdown immediately
      this.countdownSeconds = 0;
      this.isPaused = true;
      if (this.timerText && this.timerText.active) {
        this.timerText.setText('...');
        this.timerText.setColor('#10B981');
      }

      await this.prepareBattleRoster();
      
      // Check if voting was activated
      if (this.votingInterestExpressed && !this.votingCountdownActive) {
        console.log('🎯 Starting voting countdown...');
        // Start voting countdown
        await this.startVotingCountdown();
        return;
      } else if (this.votingCountdownActive) {
        // If countdown is already active and operator hits Start, skip the wait and proceed
        console.log('⏭️ Skipping active voting countdown and proceeding to battle');
        try {
          if (this.votingCountdownTimer) {
            this.votingCountdownTimer.destroy();
            this.votingCountdownTimer = null;
          }
        } catch (_) {}
        this.votingCountdownActive = false;
        // Clear/neutralize timer UI
        if (this.timerText && this.timerText.active) {
          this.timerText.setText('...');
          this.timerText.setColor('#10B981');
        }
        // Prevent join countdown from continuing to tick visually
        this.countdownSeconds = 0;
        await this.proceedToBattle();
      } else {
        // No voting interest, proceed directly to battle
        console.log('🚀 No voting interest, proceeding directly to battle');
        const timer = this.time.addEvent({
          delay: 7000,
          callback: () => {
            this.proceedToBattle();
          },
          callbackScope: this,
          loop: false
        });
        this.waitingToProceedTimer = timer;
      }
    } catch (error) {
      console.error('❌ Error starting battle:', error);
      
      // Show error message to user
      if (this.centerContainer) {
        const errorText = this.add.text(0, 0, 'Failed to start battle. Please try again.', {
          fontSize: '18px',
          color: '#EF4444',
          fontFamily: 'Arial, sans-serif',
          fontStyle: 'bold'
        }).setOrigin(0.5);
        this.centerContainer.add(errorText);
        
        // Remove error message after 3 seconds
        this.time.delayedCall(3000, () => {
          if (errorText && errorText.active) {
            errorText.destroy();
          }
        });
      }
    }
  }

  /**
   * Select battlefield early in join phase and fetch instance ID
   * This allows revenue sharing for shop purchases during join phase
   * NOTE: This is only called if no battlefield has been manually selected
   */
  async selectBattlefield() {
    // Check if a battlefield was already manually selected
    const manuallySelectedId = BattleStateManager.getBattlefieldInstanceId();
    if (manuallySelectedId) {
      console.log('[JoinScene] Battlefield already manually selected, skipping random selection:', manuallySelectedId);
      return;
    }
    try {
      // Select a random battlefield template using Utils function
      const { getFreshRandomBattlefield } = await import('../Utils.js');
      let battlefield = await getFreshRandomBattlefield(this);
      
      if (!battlefield) {
        console.warn('⚠️ No battlefield available, using fallback');
        battlefield = {
          id: 'fallback',
          name: 'Default Arena',
          description: 'A standard battlefield for epic battles.',
          image: 'battlefields/default-arena.jpg',
          owner: 'System',
          loot_collected: 0
        };
        BattleStateManager.setBattlefield(battlefield, null);
        return;
      }

      // Fetch/create battlefield instance to get the instance ID
      const theme = getCurrentTheme();
      const battlefieldId = battlefield.id || battlefield.battlefield_id || battlefield.battlefieldId;
      const themeId = theme?.id || 'soulwars-default';

      if (battlefieldId && themeId) {
        try {
          const response = await fetch(`/api/battlefields/available/${themeId}/${battlefieldId}`, {
            headers: {
              'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key'
            }
          });

          if (response.ok) {
            const data = await response.json();
            if (data.success && data.data) {
              const instanceId = data.data.id;
              const customName = data.data.customName || null;
              BattleStateManager.setBattlefield(battlefield, instanceId, customName);
              console.log('🌍 Battlefield selected and instance fetched:', {
                template: battlefield.name,
                customName: customName,
                instanceId: instanceId,
                ownerUserId: data.data.ownerUserId
              });
              return;
            }
          }
        } catch (error) {
          console.error('❌ Error fetching battlefield instance:', error);
        }
      }

      // Fallback: set battlefield without instance ID
      BattleStateManager.setBattlefield(battlefield, null);
      console.log('🌍 Battlefield selected (no instance ID):', battlefield.name);
    } catch (error) {
      console.error('❌ Error selecting battlefield:', error);
      // Set fallback battlefield
      const fallback = {
        id: 'fallback',
        name: 'Default Arena',
        description: 'A standard battlefield for epic battles.',
        image: 'battlefields/default-arena.jpg',
        owner: 'System',
        loot_collected: 0
      };
      BattleStateManager.setBattlefield(fallback, null);
    }
  }

  /**
   * Handle shop purchase event - flash revenue share on battlefield row
   */
  onShopPurchase({ tokenAmount, revenueShare, tokenSymbol }) {
    if (!this.battlefieldRow || !this.battlefieldData) return;
    
    // Flash the revenue share amount on the battlefield row
    this.flashBattlefieldRevenue(revenueShare, tokenSymbol);
  }

  /**
   * Flash revenue share amount on battlefield row
   */
  flashBattlefieldRevenue(amount, tokenSymbol) {
    if (!this.battlefieldRow || !this.battlefieldRow.revenueText) return;
    
    const revenueText = this.battlefieldRow.revenueText;
    const formattedAmount = `+${amount.toLocaleString()} ${tokenSymbol}`;
    
    // Update text
    revenueText.setText(formattedAmount);
    revenueText.setVisible(true);
    revenueText.setAlpha(1);
    revenueText.setColor('#10B981'); // Green color
    
    // Flash animation
    this.tweens.add({
      targets: revenueText,
      alpha: { from: 1, to: 0 },
      scale: { from: 1, to: 1.2 },
      duration: 2000,
      ease: 'Power2',
      onComplete: () => {
        revenueText.setVisible(false);
      }
    });
  }

  /**
   * Create battlefield display row at bottom of join grid
   */
  async createBattlefieldRow() {
    const contentWidth = this.centerContentWidth || 800;
    const playersAreaHeight = this.playersAreaHeight || 600;
    const rowHeight = 160; // Doubled from 80
    const rowY = playersAreaHeight / 2 - rowHeight / 2 - 10; // 10px from bottom
    
    // Container for the battlefield row
    const rowContainer = this.add.container(0, rowY);
    this.centerContainer.add(rowContainer);
    
    // Background
    const bg = this.add.rectangle(0, 0, contentWidth - 40, rowHeight, 0x1F2937);
    bg.setStrokeStyle(2, 0x4B5563);
    bg.setAlpha(0.9);
    rowContainer.add(bg);
    
    // Fetch battlefield data
    await this.loadBattlefieldData();
    
    // Battlefield image (moved more to the right, enlarged)
    const imageSize = 140; // Enlarged from 80 to 140
    const imageX = -contentWidth / 2 + 100; // Moved further right from 30
    const imageBg = this.add.rectangle(imageX, 0, imageSize, imageSize, 0x111827);
    imageBg.setStrokeStyle(1, 0x374151);
    rowContainer.add(imageBg);
    
    // Get battlefield image path - check both instance data and template
    const battlefield = BattleStateManager.getBattlefield();
    const battlefieldImagePath = this.battlefieldData?.themeBattlefield?.image || battlefield?.image;
    const themeId = this.battlefieldData?.themeBattlefield?.theme || getCurrentTheme()?.id || 'soulwars-default';
    
    let battlefieldImage = null;
    if (battlefieldImagePath) {
      // Use a safe texture key (slashes can break Phaser's filecomplete events)
      const safeTextureKey = `battlefield-row-${battlefieldImagePath.replace(/[^a-zA-Z0-9_-]/g, '_')}`;
      const originalTextureKey = battlefieldImagePath; // In case another scene already loaded with the raw name
      const imagePath = this.formatBattlefieldImage(battlefieldImagePath, themeId);
      console.log('[JoinScene] Loading battlefield image:', { battlefieldImagePath, imagePath, safeTextureKey, originalTextureKey, themeId });

      // If already loaded, use it
      if (this.textures.exists(safeTextureKey)) {
        console.log('[JoinScene] Battlefield texture already exists (safe key):', safeTextureKey);
        battlefieldImage = this.add.image(imageX, 0, safeTextureKey);
        battlefieldImage.setDisplaySize(imageSize - 4, imageSize - 4);
        rowContainer.add(battlefieldImage);
      } else if (this.textures.exists(originalTextureKey)) {
        console.log('[JoinScene] Battlefield texture already exists (original key):', originalTextureKey);
        battlefieldImage = this.add.image(imageX, 0, originalTextureKey);
        battlefieldImage.setDisplaySize(imageSize - 4, imageSize - 4);
        rowContainer.add(battlefieldImage);
      } else if (imagePath) {
        try {
          // Match PreBattleScene: use HTMLImageElement for remote URLs
          this.load.imageLoadType = 'HTMLImageElement';
          this.load.image(safeTextureKey, imagePath);
          this.load.once(`filecomplete-image-${safeTextureKey}`, () => {
            console.log('[JoinScene] Battlefield image loaded successfully:', safeTextureKey);
            if (battlefieldImage) battlefieldImage.destroy();
            battlefieldImage = this.add.image(imageX, 0, safeTextureKey);
            battlefieldImage.setDisplaySize(imageSize - 4, imageSize - 4);
            rowContainer.add(battlefieldImage);
          });
          this.load.once(`loaderror-image-${safeTextureKey}`, (file) => {
            console.error('[JoinScene] Failed to load battlefield image:', imagePath, file);
            // Show placeholder if load fails
            const placeholder = this.add.rectangle(imageX, 0, imageSize - 8, imageSize - 8, 0x4B5563, 0.6);
            rowContainer.add(placeholder);
          });
          this.load.start();
        } catch (e) {
          console.error('[JoinScene] Error loading battlefield image:', e);
          const placeholder = this.add.rectangle(imageX, 0, imageSize - 8, imageSize - 8, 0x4B5563, 0.6);
          rowContainer.add(placeholder);
        }
      } else {
        console.warn('[JoinScene] No valid image path for battlefield:', battlefieldImagePath);
        const placeholder = this.add.rectangle(imageX, 0, imageSize - 8, imageSize - 8, 0x4B5563, 0.6);
        rowContainer.add(placeholder);
      }
    } else {
      console.warn('[JoinScene] No battlefield image path available. Battlefield data:', this.battlefieldData, 'Battlefield:', battlefield);
      const placeholder = this.add.rectangle(imageX, 0, imageSize - 8, imageSize - 8, 0x4B5563, 0.6);
      rowContainer.add(placeholder);
    }
    
    // Battlefield name (left of center)
    const nameX = imageX + imageSize / 2 + 20;
    const nameText = this.add.text(nameX, -15, this.battlefieldData?.customName || this.battlefieldData?.themeBattlefield?.name || 'Unknown Battlefield', {
      fontSize: '20px',
      color: '#F3F4F6',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold'
    }).setOrigin(0, 0.5);
    rowContainer.add(nameText);
    
    // Owner info (below name)
    const ownerText = this.add.text(nameX, 15, this.battlefieldData?.ownerUserId 
      ? `Owner: ${this.battlefieldData.ownerName || this.battlefieldData.ownerUsername || 'Player'}` 
      : 'Unowned', {
      fontSize: '14px',
      color: '#9CA3AF',
      fontFamily: 'Arial, sans-serif'
    }).setOrigin(0, 0.5);
    rowContainer.add(ownerText);
    
    // Loot pool info (right side)
    const lootPoolX = contentWidth / 2 - 30;
    const lootPoolText = this.add.text(lootPoolX, 0, this.formatLootPoolText(), {
      fontSize: '14px',
      color: '#FBBF24',
      fontFamily: 'Arial, sans-serif',
      align: 'right'
    }).setOrigin(1, 0.5);
    rowContainer.add(lootPoolText);
    
    // Revenue share text (hidden by default, shown when purchase occurs)
    const revenueText = this.add.text(0, -25, '', {
      fontSize: '18px',
      color: '#10B981',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold',
      align: 'center'
    }).setOrigin(0.5, 0.5).setVisible(false);
    rowContainer.add(revenueText);
    
    // Store references
    this.battlefieldRow = {
      container: rowContainer,
      nameText,
      ownerText,
      lootPoolText,
      revenueText,
      image: battlefieldImage
    };
  }

  /**
   * Format battlefield image URL
   */
  formatBattlefieldImage(imagePath, themeId = null) {
    if (!imagePath) return null;
    
    // If already a full URL, return as-is
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    
    const theme = getCurrentTheme?.();
    const DEFAULT_THEME_ID = process.env.REACT_APP_DEFAULT_THEME_ID || 'soulwars-default';
    const awsRegion = process.env.REACT_APP_AWS_REGION || 'us-east-1';
    const resolvedTheme = themeId || theme?.id || DEFAULT_THEME_ID;
    const imagePathFormatted = imagePath.startsWith('battlefields/')
      ? imagePath
      : `battlefields/${imagePath}`;

    // Always prefer S3 (fallback to default bucket if s3BasePath not provided)
    const s3Base = theme?.s3BasePath || `https://soulwars.s3.${awsRegion}.amazonaws.com/themes/${resolvedTheme}`;
    return `${s3Base}/${imagePathFormatted}`;
  }

  /**
   * Format loot pool text for display
   */
  formatLootPoolText() {
    if (!this.battlefieldLootPool || this.battlefieldLootPool.length === 0) {
      return 'No custom loot pool';
    }
    
    const items = this.battlefieldLootPool.slice(0, 3); // Show first 3 items
    const parts = items.map(item => {
      if (item.lootType === 'token') {
        return `${item.amount} ${item.tokenSymbol}`;
      } else {
        return `${item.amount}x ${item.themeItemId || 'Item'}`;
      }
    });
    
    const moreCount = this.battlefieldLootPool.length - items.length;
    if (moreCount > 0) {
      parts.push(`+${moreCount} more`);
    }
    
    return `Prize Pool: ${parts.join(', ')}`;
  }

  /**
   * Load battlefield data and loot pool
   */
  async loadBattlefieldData() {
    try {
      const battlefield = BattleStateManager.getBattlefield();
      const battlefieldInstanceId = BattleStateManager.getBattlefieldInstanceId();
      
      if (!battlefieldInstanceId) {
        // No instance ID, use template data
        this.battlefieldData = {
          themeBattlefield: battlefield,
          customName: null,
          ownerUserId: null,
          ownerUsername: null
        };
        this.battlefieldLootPool = [];
        return;
      }
      
      // Fetch battlefield instance data (directly by instanceId to include themeBattlefield with image)
      // Only attempt if we have an auth token to avoid 401 spam
      const authToken = typeof window !== 'undefined' ? localStorage.getItem('jwt_token') : null;
      if (authToken) {
        try {
          const instanceRes = await fetch(`/api/battlefields/${battlefieldInstanceId}`, {
            headers: {
              'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key',
              'Authorization': `Bearer ${authToken}`
            }
          });

          if (instanceRes.ok) {
            const data = await instanceRes.json();
            if (data.success && data.data) {
              this.battlefieldData = data.data;
            }
          } else if (instanceRes.status === 401) {
            console.log('[JoinScene] Battlefield instance fetch unauthorized, falling back to template fetch');
          }
        } catch (err) {
          console.warn('Failed to fetch battlefield instance by ID:', err);
        }
      } else {
        console.log('[JoinScene] No auth token; skipping instance fetch, using template fallback');
      }

      // Fallback: use template fetch if instance call did not populate themeBattlefield
      if (!this.battlefieldData || !this.battlefieldData.themeBattlefield) {
        const theme = getCurrentTheme();
        const themeId = theme?.id || 'soulwars-default';
        const response = await fetch(`/api/battlefields/available/${themeId}/${battlefield?.id || battlefield?.battlefield_id}`, {
          headers: {
            'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            this.battlefieldData = data.data;
          }
        }
      }

      // Fetch loot pool if battlefield is owned
      if (this.battlefieldData?.ownerUserId) {
        try {
          const lootPoolResponse = await fetch(`/api/battlefields/${battlefieldInstanceId}/loot-pool`, {
            headers: {
              'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key'
            }
          });
          
          if (lootPoolResponse.ok) {
            const lootPoolData = await lootPoolResponse.json();
            if (lootPoolData.success && lootPoolData.data) {
              this.battlefieldLootPool = lootPoolData.data;
            }
          }
        } catch (e) {
          console.warn('Failed to load battlefield loot pool:', e);
        }
      }
      
      // Update UI if already created
      if (this.battlefieldRow && this.battlefieldData) {
        this.battlefieldRow.nameText.setText(this.battlefieldData.customName || this.battlefieldData.themeBattlefield?.name || 'Unknown Battlefield');
        this.battlefieldRow.ownerText.setText(this.battlefieldData.ownerUserId 
          ? `Owner: ${this.battlefieldData.ownerName || this.battlefieldData.ownerUsername || 'Player'}` 
          : 'Unowned');
        this.battlefieldRow.lootPoolText.setText(this.formatLootPoolText());
      }
    } catch (error) {
      console.error('Error loading battlefield data:', error);
      this.battlefieldData = null;
      this.battlefieldLootPool = [];
    }
  }

  shutdown() {
    // Clean up socket listeners
    if (socketService && socketService.socket) {
      if (this.inventoryUpdateHandler) {
        socketService.socket.off('hero-inventory-updated', this.inventoryUpdateHandler);
        this.inventoryUpdateHandler = null;
      }
      if (this.heroSelectionHandler) {
        socketService.socket.off('hero-selected-for-join', this.heroSelectionHandler);
        this.heroSelectionHandler = null;
      }
      if (this.balanceUpdateHandler) {
        socketService.socket.off('balance-updated', this.balanceUpdateHandler);
        this.balanceUpdateHandler = null;
      }
      if (this.shopItemsUpdateHandler) {
        socketService.socket.off('shop-items-updated', this.shopItemsUpdateHandler);
        this.shopItemsUpdateHandler = null;
      }
      if (this.rankedVoteUpdateHandler) {
        socketService.socket.off('ranked-vote-updated', this.rankedVoteUpdateHandler);
        this.rankedVoteUpdateHandler = null;
      }
    }
    
    // Clear server-selected heroes cache
    this.serverSelectedHeroes.clear();

    // Stop lobby music when leaving JoinScene
    if (this.currentLobbyMusic && this.currentLobbyMusicKey) {
      console.log('🎵 Stopping lobby music from JoinScene:', this.currentLobbyMusicKey);
      this.sound.stopByKey(this.currentLobbyMusicKey);
      this.currentLobbyMusic = null;
      this.currentLobbyMusicKey = null;
    }
    
    // Stop content switching timer
    this.stopContentSwitching();
    
    // Stop vote standings timer
    if (this.voteStandingsTimer) {
      this.voteStandingsTimer.destroy();
      this.voteStandingsTimer = null;
    }
    
    // Stop voting countdown timer
    if (this.votingCountdownTimer) {
      this.votingCountdownTimer.destroy();
      this.votingCountdownTimer = null;
    }
    
    // Remove voting interest event listener
    if (this._onVotingInterest) {
      window.removeEventListener('voting-interest-expressed', this._onVotingInterest);
    }
    
    // Clean up resize listener
    this.scale.off('resize', this.handleResize, this);
    
    // Clean up focus event listeners
    if (this.focusHandler) {
      window.removeEventListener('focus', this.focusHandler);
    }
    if (this.blurHandler) {
      window.removeEventListener('blur', this.blurHandler);
    }
    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
    }
  }

  getPlatformIconKey(platform) {
    const normalized = (platform || '').toString().trim().toLowerCase();
    switch (normalized) {
      case 'pumpfun':
      case 'pump':
        return 'platform-pumpfun';
      case 'tiktok':
      case 'tt':
        return 'platform-tiktok';
      case 'x':
      case 'twitter':
        return 'platform-x';
      case 'youtube':
        return 'platform-youtube';
      case 'twitch':
        return 'platform-twitch';
      case 'facebook':
      case 'meta':
        return 'platform-facebook';
      default:
        return null;
    }
  }

  async loadOperatorBattlefields() {
    try {
      const authToken = typeof window !== 'undefined' ? localStorage.getItem('jwt_token') : null;
      if (!authToken) {
        console.log('[JoinScene] No auth token; skipping operator battlefield fetch');
        window.__SW_OPERATOR_BATTLEFIELDS__ = [];
        return;
      }

      // Get user ID from JWT token (decode or fetch from API)
      // For now, try to get it from a user info endpoint or decode token
      // Let's use a simpler approach - fetch user's battlefields directly
      const response = await fetch('/api/battlefields/user/me', {
        credentials: 'include',
        headers: {
          'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key',
          'Authorization': `Bearer ${authToken}`
        }
      });
      
      if (response.ok) {
        const json = await response.json();
        if (json.success && json.data) {
          window.__SW_OPERATOR_BATTLEFIELDS__ = json.data;
          console.log('[JoinScene] Loaded operator battlefields:', json.data.length);
        } else {
          window.__SW_OPERATOR_BATTLEFIELDS__ = [];
        }
      } else if (response.status === 401 || response.status === 403) {
        // Not authenticated or no access - that's okay
        console.log('[JoinScene] Not authenticated or no access to user battlefields');
        window.__SW_OPERATOR_BATTLEFIELDS__ = [];
      } else {
        console.warn('[JoinScene] Failed to fetch operator battlefields:', response.status);
        window.__SW_OPERATOR_BATTLEFIELDS__ = [];
      }
    } catch (error) {
      console.error('[JoinScene] Error loading operator battlefields:', error);
      window.__SW_OPERATOR_BATTLEFIELDS__ = [];
    }
  }

  async updateBattlefieldSelection(battlefieldId) {
    if (!battlefieldId) return;
    
    console.log('[JoinScene] updateBattlefieldSelection called with instance ID:', battlefieldId);
    
    // Update BattleStateManager with the instance ID first
    BattleStateManager.setBattlefieldInstanceId(battlefieldId);
    console.log('[JoinScene] Set battlefield instance ID in BattleStateManager:', battlefieldId);
    
    // Fetch the full battlefield data from the API (includes instance info)
    await this.fetchBattlefieldDetails(battlefieldId);
    
    // Verify the instance ID is still set after fetching
    const verifyInstanceId = BattleStateManager.getBattlefieldInstanceId();
    console.log('[JoinScene] Verified battlefield instance ID after fetch:', verifyInstanceId);
    
    // Also fetch the full theme battlefield data with all fields (size, margin, object_scale, etc.)
    // This ensures we have complete battlefield data, not just the basic themeBattlefield object
    try {
      const operatorBattlefields = window.__SW_OPERATOR_BATTLEFIELDS__ || [];
      const foundBf = operatorBattlefields.find(bf => bf.id === battlefieldId);
      
      if (foundBf && foundBf.themeBattlefield) {
        const theme = getCurrentTheme();
        const themeId = theme?.id || 'soulwars-default';
        const battlefieldTemplateId = foundBf.themeBattlefield.battlefieldId || foundBf.themeBattlefield.id;
        
        if (battlefieldTemplateId && themeId) {
          // Fetch full battlefield template data from theme's normalized endpoint
          // This includes all fields: size, margin, object_scale, etc.
          const response = await fetch(`/api/themes/normalized/${themeId}/battlefields`, {
            headers: {
              'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key'
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data && Array.isArray(data)) {
              // Find the battlefield by battlefieldId or id
              const fullBattlefield = data.find(bf => 
                bf.id === battlefieldTemplateId || 
                bf.battlefieldId === battlefieldTemplateId ||
                (typeof battlefieldTemplateId === 'number' && bf.id === battlefieldTemplateId)
              );
              
              if (fullBattlefield) {
                // Use the full battlefield data with all fields (size, margin, object_scale, etc.)
                // Preserve the instance ID that was already set
                const currentInstanceId = BattleStateManager.getBattlefieldInstanceId();
                const currentCustomName = BattleStateManager.getBattlefieldCustomName?.() || null;
                BattleStateManager.setBattlefield(fullBattlefield, currentInstanceId, currentCustomName);
                console.log('[JoinScene] Loaded full battlefield data with all fields:', {
                  battlefield: fullBattlefield,
                  instanceId: currentInstanceId,
                  customName: currentCustomName
                });
              } else {
                console.warn('[JoinScene] Battlefield template not found in theme data:', battlefieldTemplateId);
              }
            }
          }
        }
      }
    } catch (error) {
      console.warn('[JoinScene] Error fetching full battlefield template data:', error);
      // Continue with whatever battlefield data we have
    }
  }

  async fetchBattlefieldDetails(battlefieldId) {
    try {
      const authToken = typeof window !== 'undefined' ? localStorage.getItem('jwt_token') : null;
      const response = await fetch(`/api/battlefields/${battlefieldId}`, {
        credentials: 'include',
        headers: {
          'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key',
          ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
        }
      });
      
      if (response.ok) {
        const json = await response.json();
        if (json.success && json.data) {
          // Preserve the instance ID and custom name when setting battlefield
          const instanceId = battlefieldId; // The ID passed in IS the instance ID
          const customName = json.data.customName || null;
          
          if (json.data.themeBattlefield) {
            // Set battlefield with instance ID and custom name preserved
            BattleStateManager.setBattlefield(json.data.themeBattlefield, instanceId, customName);
          }
          // Update the battlefield data and display
          await this.loadBattlefieldDataForInstance(battlefieldId, json.data);
        }
      }
    } catch (error) {
      console.error('[JoinScene] Error fetching battlefield details:', error);
    }
  }

  /**
   * Load battlefield data for a specific instance and update the display
   */
  async loadBattlefieldDataForInstance(battlefieldInstanceId, battlefieldData) {
    try {
      // Store the battlefield data
      this.battlefieldData = {
        id: battlefieldInstanceId,
        customName: battlefieldData.customName || null,
        ownerUserId: battlefieldData.ownerUserId || battlefieldData.owner_user_id || null,
        ownerName: battlefieldData.ownerName || null,
        ownerUsername: battlefieldData.ownerName || battlefieldData.ownerUsername || null,
        themeBattlefield: battlefieldData.themeBattlefield || battlefieldData.theme_battlefield || null
      };

      // Update the display if battlefield row exists
      if (this.battlefieldRow) {
        this.battlefieldRow.nameText.setText(
          this.battlefieldData.customName || 
          this.battlefieldData.themeBattlefield?.name || 
          'Unknown Battlefield'
        );
        this.battlefieldRow.ownerText.setText(
          this.battlefieldData.ownerUserId 
            ? `Owner: ${this.battlefieldData.ownerName || this.battlefieldData.ownerUsername || 'Player'}` 
            : 'Unowned'
        );
      }

      // Load loot pool if battlefield is owned
      if (this.battlefieldData.ownerUserId) {
        try {
          const authToken = typeof window !== 'undefined' ? localStorage.getItem('jwt_token') : null;
          const lootResponse = await fetch(`/api/battlefields/${battlefieldInstanceId}/loot-pool`, {
            credentials: 'include',
            headers: {
              'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key',
              ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
            }
          });

          if (lootResponse.ok) {
            const lootJson = await lootResponse.json();
            if (lootJson.success && lootJson.data && Array.isArray(lootJson.data)) {
              this.battlefieldLootPool = lootJson.data;
              if (this.battlefieldRow) {
                this.battlefieldRow.lootPoolText.setText(this.formatLootPoolText());
              }
            }
          }
        } catch (lootError) {
          console.warn('Failed to load battlefield loot pool:', lootError);
          this.battlefieldLootPool = [];
        }
      } else {
        this.battlefieldLootPool = [];
        if (this.battlefieldRow) {
          this.battlefieldRow.lootPoolText.setText(this.formatLootPoolText());
        }
      }

      console.log('[JoinScene] Battlefield data loaded and display updated:', {
        instanceId: battlefieldInstanceId,
        customName: this.battlefieldData.customName,
        owner: this.battlefieldData.ownerUsername
      });
    } catch (error) {
      console.error('[JoinScene] Error loading battlefield data for instance:', error);
    }
  }

  cycleOperatorBattlefield() {
    const operatorBattlefields = window.__SW_OPERATOR_BATTLEFIELDS__ || [];
    if (!operatorBattlefields.length) {
      console.warn('[JoinScene] No operator battlefields available to cycle');
      return;
    }
    
    const currentId = window.__SW_SELECTED_BATTLEFIELD_ID__ || '';
    const idx = currentId ? Math.max(0, operatorBattlefields.findIndex(bf => bf.id === currentId)) : -1;
    const next = (idx + 1) % operatorBattlefields.length;
    const selectedBf = operatorBattlefields[next];
    
    window.__SW_SELECTED_BATTLEFIELD_ID__ = selectedBf.id;
    
    try {
      localStorage.setItem('sw_selected_battlefield_id', selectedBf.id);
    } catch (_) {}
    
    // Update text display
    if (this.modalBattlefieldIdText) {
      this.modalBattlefieldIdText.setText(selectedBf.id);
      this.modalBattlefieldIdText.setStyle({ color: '#D1D5DB', fontStyle: 'normal' });
    }
    
    // Update dropdown display
    const customName = selectedBf.customName;
    const themeName = selectedBf.themeBattlefield?.name || 'Unknown';
    const displayName = customName ? customName : themeName;
    
    if (this.modalOperatorBattlefieldText) {
      this.modalOperatorBattlefieldText.setText(`${displayName} ▼`);
    }
    
    // Update BattleStateManager
    this.updateBattlefieldSelection(selectedBf.id);
  }
}

